/**
 * Kalender-Seite (W3, 2026-07-08) — Microsoft-365-Kalender über den
 * email-service-Proxy. Reine Alltags-Ansicht + „Termin finden"-Wizard.
 *
 * NICHT verwechseln mit `pages/Calendar.tsx` (Task-Fälligkeits-Kalender unter
 * /tasks?view=kalender). Diese Seite lebt unter /calendar.
 *
 * Kontrakt: docs/atlas-account-calendar-endpoints.md §III.
 *   GET  /calendar/view?start&end   — eigener Kalender (UPN = Token-User)
 *   GET  /calendar/colleagues       — Teilnehmer-Picker (aktive @bitop.de-User)
 *   POST /calendar/availability      — gemeinsame freie Slots
 *   POST /calendar/events            — Termin anlegen (201, web_link)
 * Organizer/UPN kommen serverseitig aus dem Token — das FE schickt sie nie.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  CalendarClock,
  RefreshCw,
  ServerCrash,
  MapPin,
  User as UserIcon,
  Users,
  Search,
  X,
  Check,
  Clock,
  CalendarPlus,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  ExternalLink,
  AlertTriangle,
  Send,
  Pencil,
  Trash2,
  HelpCircle,
  Video,
  Bell,
  Building2,
  Plus,
  ListChecks,
  CalendarRange,
  ChevronDown,
  MoreHorizontal,
} from "lucide-react";
import clsx from "clsx";
import { api, ApiError } from "../api";
import { rankColleagues, type Colleague } from "../ui/recipientRank";
import {
  CONTACT_CHECK_TIMEOUT_MS,
  CONTACT_DEBOUNCE_MS,
  MAX_HITS,
  MIN_CONTACT_QUERY,
  externalParticipants,
  internalUpns,
  isExternalParticipant,
  isFunctionMailbox,
  isSameAddress,
  looksLikeEmailAddress,
  mergeHits,
  toParticipant,
  withoutSelf,
  type ContactSuggestion,
} from "../ui/participants";
import { useAuth } from "../auth";
import { useT, type Katalog } from "../i18n";
import CalendarTimeGrid from "../components/CalendarTimeGrid";
import {
  useBusinessHours,
  hourLabel,
  type BusinessHours,
} from "../ui/calendarBusinessHours";
import { compareEvents, dateAtMinute, sortEvents } from "../components/calendarLayout";
// Bauform der Kopf-Menüs aus Etappe 1 (Posteingang) — E2 benutzt dieselbe.
import { KopfMenu, KopfMenuItem } from "../components/KopfMenu";
import { pollSlotsPayload } from "./calendarSlotTime";
import {
  DURATION_OPTIONS,
  TIME_OPTIONS,
  durationLabel,
  fmtDeDate,
  fmtHm,
  minutesFromHm,
  parseDeDate,
  withCurrent,
} from "../ui/germanDate";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  StatusBadge,
  Textarea,
  useModal,
  useToast,
} from "../ui";

// `Colleague` lebt als EINE Quelle in ../ui/recipientRank (P0.4) — hier re-
// exportiert, damit „Termin aus dieser Mail" (P2.16) den Wizard + Picker + den
// Typ aus DIESEM Modul zieht (Driver-Entscheidung #3), ohne den Typ zu doppeln.
export type { Colleague };

// ---- Typen (Spiegel des API-Kontrakts) ------------------------------------

interface CalEvent {
  // graph_id ist die Voraussetzung für Bearbeiten/Löschen/Zu-Absagen. Das
  // email-service ``GET /view`` liefert es (sb_calendar :120); der rohe
  // Dict-Passthrough des Routers verwirft es nicht. Bei Alt-/Leerform null.
  graph_id: string | null;
  ical_uid?: string | null;
  subject: string;
  start: string;
  end: string;
  // Live-Form (email-service): organizer_name/organizer_address. ``organizer``
  // (Single-String) ist die Alt-/Fallback-Form (nur der frühere Fake lieferte sie).
  organizer?: string | null;
  organizer_name?: string | null;
  organizer_address?: string | null;
  location?: string | null;
  timezone?: string | null;
  is_all_day: boolean;
  show_as?: string | null;
  is_cancelled?: boolean;
  // X2 Slice C — Teams-Join-Link aus der /calendar/view-Projektion (nur bei
  // Online-Terminen gesetzt). Das Detail-Fenster bietet damit „Beitreten" an.
  join_url?: string | null;
  // P2.17 — Reminder-Ist-Zustand (Lese-Felder aus /calendar/view; email-service
  // sb_calendar-Projektion + $select). ``undefined`` = alter Backend-Stand (Feld
  // fehlt) → Platzhalter „–"; ``false`` = „Keine"; ``true`` + Minutenwert = aktiv.
  is_reminder_on?: boolean | null;
  reminder_minutes_before_start?: number | null;
  // F-32 — wer ausser dem Organisator eingeladen ist (email-service
  // sb_calendar.py::_teilnehmer). `undefined` = alter Backend-Stand → das
  // Detail-Fenster zeigt gar keine Teilnehmerzeile; `[]` = „niemand sonst"
  // und ist eine Aussage. Die beiden werden BEWUSST unterschieden: eine Zeile
  // „keine weiteren Teilnehmer" gegen einen alten email-service wäre gelogen.
  attendees?: CalAttendee[];
}
/** Ein Eingeladener. `response` ist der Graph-Antwortstatus im Rohwert
 *  (none | organizer | tentativelyAccepted | accepted | declined |
 *  notResponded); die Übersetzung macht {@link antwortText}. */
interface CalAttendee {
  name?: string | null;
  address: string;
  type?: string | null;
  response?: string | null;
}
interface CalViewResponse {
  // Envelope der Live-Form (email-service ``GET /view`` → {mailbox, events}).
  mailbox?: string;
  events: CalEvent[];
}

type ViewMode = "tag" | "woche" | "monat" | "agenda";
type RespondChoice = "accept" | "decline" | "tentativelyAccept";
interface Slot {
  start: string;
  end: string;
  timezone?: string | null;
  // OPTIONALE Teilnehmer, die in DIESEM Slot belegt sind. Optionale blockieren
  // keinen Slot — sie erscheinen nur als Markierung. Leeres Array = alle können.
  //
  // `undefined` ist NICHT dasselbe wie `[]`: fehlt das Feld (Server-Stand ohne
  // diesen Vertrag), ist die Aussage UNBEKANNT. Dann darf die Liste kein „alle
  // können" behaupten — das wäre eine Zusage auf Nicht-Daten. Deshalb steht das
  // Feld optional im Typ und wird an der Verwendungsstelle mit `Array.isArray`
  // unterschieden, nicht mit `?? []`.
  busy_optional?: string[];
}
/** Befund zu EINER selbst eingetragenen Zeit (`POST /calendar/slot-status`).
 *
 *  Drei Listen, drei verschiedene Wahrheiten — dieselbe Trennung wie bei den
 *  Vorschlägen: `busy_required` blockiert (der Termin geht so nicht),
 *  `busy_optional` informiert (er geht, aber jemand fehlt), `unknown` ist gar
 *  keine Antwort (für diese Adresse lag kein Kalender vor). Alle drei sind im
 *  Vertrag IMMER vorhanden; fehlen sie trotzdem (älterer Server), wird das an
 *  der Karte als „unbekannt" gezeigt und NICHT als „frei" — `?? []` an dieser
 *  Stelle wäre eine Zusage auf Nicht-Daten. */
interface SlotStatus {
  start: string;
  end: string;
  busy_required?: string[];
  busy_optional?: string[];
  unknown?: string[];
}
interface SlotStatusResponse {
  slots: SlotStatus[];
  attendees_checked?: number;
}
interface AvailabilityResponse {
  slots: Slot[];
  attendees_checked: string[];
  // OPTIONALE interne Adressen, für die Graph gar kein verwertbares Free/Busy
  // liefert (Fehler-Zeile, leeres availabilityView). Für sie wissen wir weder
  // „kann" noch „kann nicht" — sie stehen deshalb in KEINEM `busy_optional`.
  //
  // Auf RESPONSE-Ebene, nicht am Slot: „kein Kalender abrufbar" ist eine
  // Eigenschaft der PERSON. Wer keine Daten hat, hat in jedem Slot keine.
  //
  // Fehlt der Schlüssel (Server-Stand ohne diesen Vertrag), ist auch das keine
  // Zusage — dann darf kein Slot „alle können" behaupten (siehe `Slot`).
  optional_unknown?: string[];
}
interface EventCreateResponse {
  graph_id: string;
  ical_uid: string;
  web_link: string;
  // Slice B (P2.16): bei online_meeting=true liefert der email-service den
  // Teams-Join-Link durch; sonst null/absent.
  join_url?: string | null;
}
/** Antwort von `POST /calendar/polls` (app/schemas/calendar_poll.py::
 *  PollCreateResponse). Hier steht NUR, was der Wizard wirklich anzeigt.
 *
 *  Die `tokens` der Antwort fehlen mit Absicht: das sind die Klartext-Links der
 *  Teilnehmer, die es genau einmal gibt (danach nur noch Hashes). Sie gehören
 *  in den Versand, nicht in eine Oberfläche — und was der Wizard nicht kennt,
 *  kann er auch nicht versehentlich anzeigen oder loggen. */
interface PollSlotOut {
  id: string;
  starts_at: string;
  ends_at: string;
}
/** Ein Empfänger, wie ihn die Antwort meldet — OHNE das Feld `token`.
 *
 *  Der Klartext-Token steht in derselben Liste, ist aber hier bewusst nicht
 *  modelliert: was der Wizard nicht kennt, kann er nicht versehentlich
 *  anzeigen. Interessant ist nur, ob die Einladung rausging.
 *
 *  `invitation_sent` ist OPTIONAL, und das ist kein Schönheitsfehler: fehlt
 *  das Feld (Server-Stand ohne Versand), ist die Frage UNBEANTWORTET. Dann
 *  sagt der Abschluss über den Versand gar nichts — statt ihn zu behaupten
 *  oder zu bestreiten. Dieselbe Unterscheidung wie bei `busy_optional`. */
interface PollInvitationOut {
  email: string;
  name?: string | null;
  invitation_sent?: boolean;
  invitation_error?: string | null;
}
interface PollCreateResponse {
  poll: {
    id: string;
    // Frist der Abstimmung. Der Server setzt sie ohne eigene Angabe auf den
    // frühesten Vorschlag — danach wäre die Abstimmung gegenstandslos.
    expires_at: string;
    slots: PollSlotOut[];
  };
  tokens?: PollInvitationOut[];
}

// ---- Datums-Helfer ---------------------------------------------------------

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
function fmtWeekdayDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
}

// ---- Woche/Monat-Raster (Dry-Run code-verifiziert, Mo-Beginn) --------------

/** Montag der Woche, die ``d`` enthält (deutsche Konvention, Mo=Start). */
function startOfWeekMon(d: Date): Date {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Mo=0 … So=6
  return addDays(x, -dow);
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/** Fenster [start, end) für einen View-Modus um einen Anker.
 *  Monat holt bewusst NUR den Monat selbst (≤31 T, email-service MAX_WINDOW_DAYS
 *  = 31) — das 6-Wochen-Grid (bis 42 T) würde 422en; Rand-Tage bleiben ohne
 *  Termine (gedimmt). */
function windowFor(mode: ViewMode, anchor: Date): { start: Date; end: Date } {
  const a = startOfDay(anchor);
  if (mode === "tag") return { start: a, end: addDays(a, 1) };
  if (mode === "woche") {
    const m = startOfWeekMon(a);
    return { start: m, end: addDays(m, 7) };
  }
  if (mode === "agenda") return { start: a, end: addDays(a, 14) };
  const first = startOfMonth(a);
  return { start: first, end: startOfMonth(addMonths(first, 1)) };
}

/** Anker um die View-Länge verschieben (auch negativ → Vergangenheit). */
function shiftAnchor(mode: ViewMode, anchor: Date, dir: 1 | -1): Date {
  if (mode === "tag") return addDays(anchor, dir);
  if (mode === "woche") return addDays(anchor, 7 * dir);
  if (mode === "agenda") return addDays(anchor, 14 * dir);
  return addMonths(anchor, dir);
}

function rangeLabel(mode: ViewMode, anchor: Date): string {
  const a = startOfDay(anchor);
  if (mode === "tag")
    return a.toLocaleDateString("de-DE", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  if (mode === "monat")
    return a.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const { start, end } = windowFor(mode, a);
  const last = addDays(end, -1); // inklusives Enddatum fürs Label
  const sameYear = start.getFullYear() === last.getFullYear();
  const fmtShort = (d: Date) =>
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
  const fmtFull = (d: Date) =>
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  return `${sameYear ? fmtShort(start) : fmtFull(start)} – ${fmtFull(last)}`;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Events nach Kalendertag gruppieren (Key = lokaler Tag), je Tag sortiert.
 *
 *  Sortiert mit DEMSELBEN `compareEvents` wie `sortEvents` — inklusive der
 *  „ganztägig zuerst"-Regel. Vorher stand hier ein eigenes `localeCompare` OHNE
 *  diese Regel: das war keine bewusste Abweichung, sondern tote Arbeit, denn
 *  beide Aufrufer (MonthView :671, AgendaView :778) schicken das Ergebnis
 *  ohnehin noch durch `sortEvents` und überschrieben die Reihenfolge damit
 *  sofort wieder. Zwei Ordnungsregeln für dieselbe Liste können nur
 *  auseinanderlaufen — es gibt jetzt genau eine. */
function groupByDay(events: CalEvent[]): Map<string, CalEvent[]> {
  const map = new Map<string, CalEvent[]>();
  for (const ev of events) {
    const d = new Date(ev.start);
    if (Number.isNaN(d.getTime())) continue;
    const k = dayKey(d);
    const arr = map.get(k);
    if (arr) arr.push(ev);
    else map.set(k, [ev]);
  }
  for (const [, arr] of map) arr.sort(compareEvents);
  return map;
}

function organizerLabel(ev: CalEvent): string | null {
  return ev.organizer_name ?? ev.organizer ?? null;
}

// ---- Teilnehmer eines Termins (F-32) ---------------------------------------
// Wieviele Eingeladene ohne Aufklappen dastehen. Vier Zeilen sind der Punkt, an
// dem das Detail-Fenster noch ohne Scrollen lesbar bleibt; ein Verteiler mit 30
// Adressen darf es nicht auf Bildschirmhöhe treiben.
const TEILNEHMER_KURZ = 4;

/** Graph-Antwortstatus → deutsches Wort. Unbekannte/fehlende Werte werden
 *  „offen" — das ist die ehrliche Auskunft: Graph unterscheidet `none` und
 *  `notResponded` für denselben Zustand, und ein erfundener Status wäre
 *  schlimmer als keiner. */
function antwortText(t: Katalog, response: string | null | undefined): string {
  switch (response) {
    case "accepted":
      return t.calendar.termin.antwortZugesagt;
    case "declined":
      return t.calendar.termin.antwortAbgesagt;
    case "tentativelyAccepted":
      return t.calendar.termin.antwortMitVorbehalt;
    case "organizer":
      return t.calendar.termin.antwortOrganisator;
    default:
      return t.calendar.termin.antwortOffen;
  }
}

// Deutsches Datum + Uhrzeit + Dauer liegen in `ui/germanDate.ts` — EINE
// Implementierung für Kalender UND Mail-Suche (kein zweiter Parser). Warum
// überhaupt eigene Felder statt `<input type="date">`, und warum ein Playwright-
// `locale` das nicht nachweist: siehe Kopfkommentar dort.

/** Kopfzeile des Anlegen-Dialogs: deutsches Datum + Zeitspanne, mehrtägig mit
 *  beiden Tagen. Läuft live mit den Eingaben mit. */
function rangeSummary(t: Katalog, start: Date | null, end: Date | null): string {
  if (!start) return t.calendar.neu.datumUnvollstaendig;
  if (!end) return t.calendar.neu.zeitspanneUngueltig;
  const wd = (d: Date) => d.toLocaleDateString("de-DE", { weekday: "short" });
  return start.toDateString() === end.toDateString()
    ? `${wd(start)}, ${fmtDeDate(start)} · ${fmtHm(start)} – ${fmtHm(end)}`
    : `${wd(start)}, ${fmtDeDate(start)} ${fmtHm(start)} – ${wd(end)}, ${fmtDeDate(
        end
      )} ${fmtHm(end)}`;
}

function respondLabel(t: Katalog, choice: RespondChoice): string {
  if (choice === "accept") return t.calendar.termin.respondZugesagt;
  if (choice === "decline") return t.calendar.termin.respondAbgesagt;
  return t.calendar.termin.respondMitVorbehalt;
}

// ---- Reminder-Dropdown (P2.17) ---------------------------------------------
// Erinnerungszeit am Termin setzen. Reused den P1.9-Event-PATCH (KEIN eigener
// Endpoint): „Keine" → {is_reminder_on:false}; N → {is_reminder_on:true,
// reminder_minutes_before_start:N}. Der Ist-Wert kommt aus /calendar/view.
const REMINDER_VALUES = ["none", "5", "15", "30", "60", "1440"];
const REMINDER_STD = new Set([5, 15, 30, 60, 1440]);

function reminderLabel(t: Katalog, min: number): string {
  return min === 1440
    ? t.calendar.termin.erinnerungEinTag
    : t.calendar.termin.erinnerungMinuten(min);
}

/** Die Auswahl-Liste. Als FUNKTION und nicht als Modul-Konstante: eine
 *  Konstante fror die Sprache auf den Zeitpunkt des Imports ein. */
function reminderOptions(t: Katalog): { value: string; label: string }[] {
  return REMINDER_VALUES.map((value) => ({
    value,
    label:
      value === "none"
        ? t.calendar.termin.erinnerungKeine
        : reminderLabel(t, Number(value)),
  }));
}

// <select>-Wert aus dem Ist-Zustand des Events ableiten (D5-Ehrlichkeit):
//   Feld fehlt (undefined/null) → "" (Platzhalter „–", disabled — nie falscher Wert)
//   is_reminder_on === false    → "none" („Keine")
//   is_reminder_on === true     → String(minutes) bzw. "none" ohne Minutenwert
function reminderValue(ev: CalEvent): string {
  const on = ev.is_reminder_on;
  if (on === undefined || on === null) return "";
  if (on === false) return "none";
  const m = ev.reminder_minutes_before_start;
  return m != null ? String(m) : "none";
}

// Gemeinsame <option>-Liste für ALLE Reminder-Selects (EventRow-Dropdown +
// EditEventModal — DRY, nicht dupliziert). `cur` = aktueller <select>-Wert;
// ist er ein krummer Graph-Wert außerhalb der Standardliste (z. B. 10), wird
// eine dynamische Zusatz-Option ergänzt, damit das Select nie einen falschen
// Wert anzeigt (D5). "" → Platzhalter „–" (disabled, nicht wählbar).
function ReminderOptions({ cur }: { cur: string }) {
  const t = useT();
  const curNum = /^\d+$/.test(cur) ? Number(cur) : null;
  const showExtra = curNum !== null && !REMINDER_STD.has(curNum);
  return (
    <>
      {cur === "" && (
        <option value="" disabled>
          –
        </option>
      )}
      {reminderOptions(t).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {showExtra && curNum !== null && (
        <option value={String(curNum)}>{reminderLabel(t, curNum)}</option>
      )}
    </>
  );
}

// Zustands-Steuerelement (kein CTA): sichtbar im Meta-Body, Ist-Wert dauerhaft
// ablesbar ohne Hover (Outlook-Parität, D4). Klicks blocken die Row-Handler.
// Feuert bei jeder Wahl sofort EINEN PATCH (unverändertes P2.17-Verhalten).
function ReminderSelect({
  ev,
  onReminder,
}: {
  ev: CalEvent;
  onReminder: (ev: CalEvent, minutes: number | null) => void;
}) {
  const t = useT();
  const cur = reminderValue(ev);
  return (
    <span
      className="inline-flex items-center gap-1 min-w-0"
      onClick={(e) => e.stopPropagation()}
    >
      <Bell className="w-3 h-3 shrink-0" />
      <select
        aria-label={t.calendar.termin.erinnerung}
        title={t.calendar.termin.erinnerungTitel}
        value={cur}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") return; // Platzhalter — nicht wählbar
          onReminder(ev, v === "none" ? null : Number(v));
        }}
        className="rounded border border-border bg-surface px-1 py-0.5 text-xs text-text-2 hover:border-accent-300 focus:outline-none focus:border-accent-400 cursor-pointer"
      >
        <ReminderOptions cur={cur} />
      </select>
    </span>
  );
}

// ---- Event-Zeile (mit One-Click-Aktionen) ----------------------------------

interface EventRowProps {
  ev: CalEvent;
  onRespond?: (ev: CalEvent, response: RespondChoice) => void;
  onEdit?: (ev: CalEvent) => void;
  onDelete?: (ev: CalEvent) => void;
  onReminder?: (ev: CalEvent, minutes: number | null) => void;
}

function EventActionButton({
  title,
  onClick,
  children,
  danger,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={clsx(
        "grid place-items-center w-6 h-6 rounded-md border border-border bg-surface text-text-3 transition-colors",
        danger
          ? "hover:bg-danger-soft hover:text-danger hover:border-danger/40"
          : "hover:bg-accent-50 hover:text-accent-600 hover:border-accent-300"
      )}
    >
      {children}
    </button>
  );
}

function EventRow({ ev, onRespond, onEdit, onDelete, onReminder }: EventRowProps) {
  const t = useT();
  const cancelled = ev.is_cancelled;
  const hasActions =
    !!ev.graph_id && (!!onRespond || !!onEdit || !!onDelete);
  const org = organizerLabel(ev);
  return (
    <div
      className={clsx(
        "group relative flex items-start gap-3 rounded-lg border border-border bg-surface px-3 py-2.5",
        cancelled && "opacity-60"
      )}
    >
      <div className="shrink-0 text-right w-[92px]">
        {ev.is_all_day ? (
          <span className="inline-flex items-center rounded-full bg-accent-100 text-accent-700 px-2 py-0.5 text-[11px] font-medium">
            {t.calendar.termin.ganztaegig}
          </span>
        ) : (
          <div className="text-sm tabular-nums text-text font-medium">
            {fmtTime(ev.start)}
            <div className="text-[11px] text-text-3">– {fmtTime(ev.end)}</div>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            "text-sm font-medium text-text truncate",
            cancelled && "line-through"
          )}
          title={ev.subject}
        >
          {ev.subject || t.calendar.termin.keinBetreff}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-3">
          {ev.location && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{ev.location}</span>
            </span>
          )}
          {org && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <UserIcon className="w-3 h-3 shrink-0" />
              <span className="truncate">{org}</span>
            </span>
          )}
          {cancelled && (
            <StatusBadge status="danger">{t.calendar.termin.abgesagt}</StatusBadge>
          )}
          {/* P2.17 — Reminder-Dropdown im SICHTBAREN Meta-Body (D4): ohne
             Hover ablesbar; nur bei vorhandener graph_id (patchbares Event). */}
          {ev.graph_id && onReminder && (
            <ReminderSelect ev={ev} onReminder={onReminder} />
          )}
        </div>
      </div>

      {hasActions && (
        <div
          className={clsx(
            "absolute right-1.5 top-1.5 flex items-center gap-1",
            "opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity",
            "bg-surface/95 rounded-md pl-1"
          )}
        >
          {onRespond && (
            <>
              <EventActionButton
                title={t.calendar.termin.zusagen}
                onClick={() => onRespond(ev, "accept")}
              >
                <Check className="w-3.5 h-3.5" />
              </EventActionButton>
              <EventActionButton
                title={t.calendar.termin.mitVorbehalt}
                onClick={() => onRespond(ev, "tentativelyAccept")}
              >
                <HelpCircle className="w-3.5 h-3.5" />
              </EventActionButton>
              <EventActionButton
                title={t.calendar.termin.absagen}
                onClick={() => onRespond(ev, "decline")}
              >
                <X className="w-3.5 h-3.5" />
              </EventActionButton>
            </>
          )}
          {onEdit && (
            <EventActionButton
              title={t.common.bearbeiten}
              onClick={() => onEdit(ev)}
            >
              <Pencil className="w-3.5 h-3.5" />
            </EventActionButton>
          )}
          {onDelete && (
            <EventActionButton
              title={t.common.loeschen}
              danger
              onClick={() => onDelete(ev)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </EventActionButton>
          )}
        </div>
      )}
    </div>
  );
}

interface EventActions {
  onRespond: (ev: CalEvent, response: RespondChoice) => void;
  onEdit: (ev: CalEvent) => void;
  onDelete: (ev: CalEvent) => void;
  onReminder: (ev: CalEvent, minutes: number | null) => void;
}

function evKey(ev: CalEvent, i: number): string {
  return ev.graph_id || `${ev.subject}-${ev.start}-${i}`;
}

function LoadingRows({ n = 3 }: { n?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyDay({ text }: { text?: string }) {
  const t = useT();
  return (
    <div className="rounded-lg border border-dashed border-border px-3 py-8 text-center text-sm text-text-3">
      {text ?? t.calendar.keineTermine}
    </div>
  );
}

// ---- Tag + Woche (Zeitraster) ------------------------------------------------
// Beide Ansichten sind DASSELBE Raster, nur mit anderer Spaltenzahl (Tag = 1,
// Woche = 7 ab Montag) und Stundenhöhe. Termine sitzen auf ihrer Uhrzeit
// (top = Start, height = Dauer) → Lücken im Tag bleiben sichtbar, statt in einer
// Liste wegzufallen. Klick/Ziehen in eine freie Fläche legt einen Termin an.
// Mathematik + Dry-Run: components/calendarLayout.ts, calendar-grid-dryrun.mts.

interface GridViewProps {
  anchor: Date;
  events: CalEvent[];
  loading: boolean;
  onOpenEvent: (ev: CalEvent) => void;
  /** Geschäftszeiten des Nutzers → Randzeiten im Raster abgeblendet. Kommt aus
   *  `useBusinessHours()` der SEITE, nicht aus dem Raster selbst: das Raster
   *  bleibt darstellend (kein `api`-Import, damit der Dry-Run es laden kann). */
  businessHours: BusinessHours;
  /** `at` = Klick-Punkt (Client-Koordinaten) → Anlege-Dialog mittig am Zeiger. */
  onCreateSlot: (start: Date, end: Date, at: { x: number; y: number }) => void;
}

function DayView({
  anchor,
  events,
  loading,
  onOpenEvent,
  businessHours,
  onCreateSlot,
}: GridViewProps) {
  const days = useMemo(() => [startOfDay(anchor)], [anchor]);
  return (
    <CalendarTimeGrid
      days={days}
      events={events}
      loading={loading}
      hourPx={56}
      showDayHeaders={false}
      onOpenEvent={onOpenEvent}
      businessHours={businessHours}
      onCreateSlot={onCreateSlot}
    />
  );
}

function WeekView({
  anchor,
  events,
  loading,
  onOpenEvent,
  businessHours,
  onCreateSlot,
  onPickDay,
}: GridViewProps & { onPickDay: (d: Date) => void }) {
  const days = useMemo(() => {
    const monday = startOfWeekMon(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [anchor]);
  return (
    <CalendarTimeGrid
      days={days}
      events={events}
      loading={loading}
      hourPx={48}
      onOpenEvent={onOpenEvent}
      businessHours={businessHours}
      onCreateSlot={onCreateSlot}
      onPickDay={onPickDay}
    />
  );
}

// ---- Monat (Monatsraster, Rand-Tage gedimmt) --------------------------------

function MonthView({
  anchor,
  events,
  loading,
  onPickDay,
}: {
  anchor: Date;
  events: CalEvent[];
  loading: boolean;
  onPickDay: (d: Date) => void;
}) {
  const t = useT();
  const first = startOfMonth(anchor);
  const gridStart = startOfWeekMon(first);
  const lead = (first.getDay() + 6) % 7;
  const weeks = Math.ceil((lead + daysInMonth(first)) / 7);
  const today = startOfDay(new Date());
  const byDay = useMemo(() => groupByDay(events), [events]);
  const cells = Array.from({ length: weeks * 7 }, (_, i) => addDays(gridStart, i));
  // Wochentags-Kuerzel bleiben deutsch — dieselbe Spalten-Beschriftung liefert
  // im Zeitraster `toLocaleDateString("de-DE", { weekday: "short" })`, und zwei
  // Sprachen fuer dieselbe Spalte waeren schlechter als eine (docs/i18n.md §2).
  const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  if (loading) return <LoadingRows n={5} />;

  return (
    <div className="min-w-[640px]">
      <div className="grid grid-cols-7 gap-px mb-1">
        {WD.map((w) => (
          <div
            key={w}
            className="text-[11px] uppercase tracking-wide text-text-3 text-center py-1"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
        {cells.map((d) => {
          const inMonth = d.getMonth() === first.getMonth();
          const isToday = sameDay(d, today);
          const list = sortEvents(byDay.get(dayKey(d)) ?? []);
          const shown = list.slice(0, 3);
          const extra = list.length - shown.length;
          return (
            <button
              key={dayKey(d)}
              type="button"
              // Adressierbare Tages-Zelle (Muster data-cal-grid/-event im
              // Zeitraster) — der Sortier-Walk liest die Chip-Reihenfolge
              // genau EINER Zelle, statt sie aus der Zahl im Badge zu raten.
              data-cal-month-day={dayKey(d)}
              onClick={() => onPickDay(d)}
              title={t.calendar.termin.tagOeffnen}
              className={clsx(
                "text-left min-h-[92px] p-1.5 bg-surface transition-colors hover:bg-surface-2",
                !inMonth && "bg-surface-2/40"
              )}
            >
              <div
                className={clsx(
                  "text-xs tabular-nums mb-1 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1",
                  isToday
                    ? "bg-accent-500 text-white font-semibold"
                    : inMonth
                      ? "text-text"
                      : "text-text-3"
                )}
              >
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {shown.map((ev, i) => (
                  <div
                    key={evKey(ev, i)}
                    title={`${fmtTime(ev.start)} ${ev.subject}`}
                    className={clsx(
                      "truncate rounded px-1 py-0.5 text-[11px] leading-tight",
                      ev.is_cancelled
                        ? "bg-danger-soft/50 text-danger line-through"
                        : "bg-accent-50 text-accent-700"
                    )}
                  >
                    {!ev.is_all_day && (
                      <span className="tabular-nums text-text-3 mr-1">
                        {fmtTime(ev.start)}
                      </span>
                    )}
                    {ev.subject || t.calendar.termin.keinBetreff}
                  </div>
                ))}
                {extra > 0 && (
                  <div className="text-[11px] text-text-3 px-1">
                    {t.calendar.termin.mehr(extra)}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- Agenda (flache chronologische Liste) -----------------------------------

function AgendaView({
  anchor,
  events,
  loading,
  actions,
}: {
  anchor: Date;
  events: CalEvent[];
  loading: boolean;
  actions: EventActions;
}) {
  const t = useT();
  const byDay = useMemo(() => groupByDay(events), [events]);
  const start = startOfDay(anchor);
  const days = Array.from({ length: 14 }, (_, i) => addDays(start, i)).filter(
    (d) => (byDay.get(dayKey(d)) ?? []).length > 0
  );

  if (loading) return <LoadingRows n={4} />;
  if (days.length === 0)
    return <EmptyDay text={t.calendar.termin.keineTermineNaechste14} />;

  return (
    <div className="max-w-3xl space-y-5">
      {days.map((d) => {
        const isToday = sameDay(d, startOfDay(new Date()));
        return (
          <section key={dayKey(d)}>
            <div className="flex items-baseline gap-2 mb-2 sticky top-0 bg-canvas/95 py-1">
              <h3
                className={clsx(
                  "text-sm font-semibold capitalize",
                  isToday ? "text-accent-600" : "text-text"
                )}
              >
                {d.toLocaleDateString("de-DE", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </h3>
              {isToday && (
                <span className="text-xs text-accent-600">{t.calendar.heute}</span>
              )}
            </div>
            <div className="space-y-2">
              {sortEvents(byDay.get(dayKey(d)) ?? []).map((ev, i) => (
                <EventRow
                  key={evKey(ev, i)}
                  ev={ev}
                  onRespond={actions.onRespond}
                  onEdit={actions.onEdit}
                  onDelete={actions.onDelete}
                  onReminder={actions.onReminder}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---- Termin bearbeiten (zentriertes Modal) ----------------------------------

// PATCH-Payload des Bearbeiten-Modals. Reminder-Felder sind OPTIONAL und reisen
// NUR mit, wenn der Nutzer die Erinnerung tatsächlich geändert hat (Dirty-
// Tracking) — ein unberührter „–"-Platzhalter (unbekannter Ist-Zustand) darf
// NIE als is_reminder_on:false gesendet werden und so den Ist-Wert überschreiben.
type EditEventPatch = {
  subject: string;
  start: string;
  end: string;
  is_reminder_on?: boolean;
  reminder_minutes_before_start?: number;
};

function EditEventModal({
  ev,
  onClose,
  onSaved,
}: {
  ev: CalEvent;
  onClose: () => void;
  onSaved: (patch: EditEventPatch) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [subject, setSubject] = useState(ev.subject);
  // Deutsches Datum + Uhrzeit-Auswahl statt `datetime-local` (das rendert im
  // englischen Chrome als MM/DD/YYYY + AM/PM). Bearbeiten behält bewusst ein
  // konkretes ENDE statt der Dauer-Lesart des Anlegen-Dialogs: ein bestehender
  // Graph-Termin kann jede Form haben (ganztägig 00:00→00:00, mehrtägig,
  // über Nacht). Das Anlegen-Modell „Start + Dauer + optionaler End-TAG" ist
  // absichtlich eingeschränkt — es VERBIETET ein Tagesfenster ≥ 24:00 —, und
  // genau solche Termine gibt es im Kalender bereits. Mit Dauer würden sie beim
  // Öffnen sofort als ungültig gelten oder ihr Ende still verschieben. Mit dem
  // Endfeld kann der Vertrag „kein stiller Tagesversatz" gar nicht verletzt
  // werden: es wird nichts gerechnet, das Ende steht da. Bewacht wird er hier
  // durch `invalidRange` (Ende muss nach dem Beginn liegen).
  const ref = useMemo(() => {
    const d = new Date(ev.start);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [ev.start]);
  const initial = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? { date: "", time: "09:00" }
      : { date: fmtDeDate(d), time: fmtHm(d) };
  };
  const [startDate, setStartDate] = useState(() => initial(ev.start).date);
  const [startTime, setStartTime] = useState(() => initial(ev.start).time);
  const [endDate, setEndDate] = useState(() => initial(ev.end).date);
  const [endTime, setEndTime] = useState(() => initial(ev.end).time);
  // Erinnerung: Ist-Wert aus dem Event vorbelegen (reused reminderValue, D5:
  // undefined/null → "" = Platzhalter „–"). Dirty = Nutzer hat die Auswahl
  // gegenüber dem Ausgangswert geändert.
  const reminderInit = reminderValue(ev);
  const [reminder, setReminder] = useState(reminderInit);
  const reminderDirty = reminder !== reminderInit;
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const startAt = useMemo(() => {
    const d = parseDeDate(startDate, ref);
    return d ? dateAtMinute(d, minutesFromHm(startTime)) : null;
  }, [startDate, startTime, ref]);
  const endAt = useMemo(() => {
    const d = parseDeDate(endDate, ref);
    return d ? dateAtMinute(d, minutesFromHm(endTime)) : null;
  }, [endDate, endTime, ref]);

  // Beide Datumsfelder sind hier PFLICHT → leer ist ebenso eine sichtbare
  // Fehleingabe wie „31.02.2026" (im Anlegen-Dialog ist das Enddatum optional,
  // deshalb dort die `!== ""`-Ausnahme — hier bewusst nicht).
  const startDateInvalid = !startAt;
  const endDateInvalid = !endAt;
  const invalidRange = !!startAt && !!endAt && endAt.getTime() <= startAt.getTime();
  const canSave = !!subject.trim() && !!startAt && !!endAt && !invalidRange;

  // Krumme Graph-Zeiten (ein Termin startet auch mal 09:07) als Zusatz-Eintrag,
  // damit das <select> nie einen falschen Wert anzeigt.
  const startTimeOptions = useMemo(
    () => withCurrent(TIME_OPTIONS, startTime),
    [startTime]
  );
  const endTimeOptions = useMemo(() => withCurrent(TIME_OPTIONS, endTime), [endTime]);

  /** Beim Verlassen des Feldes auf TT.MM.JJJJ normalisieren (1.2. → 01.02.2026). */
  const normalizeDate = (v: string, set: (s: string) => void) => {
    if (!v.trim()) return;
    const d = parseDeDate(v, ref);
    if (d) set(fmtDeDate(d));
  };

  const save = async () => {
    if (!ev.graph_id || !canSave || !startAt || !endAt) return;
    setSaving(true);
    setErr(null);
    const patch: EditEventPatch = {
      subject: subject.trim(),
      start: startAt.toISOString(),
      end: endAt.toISOString(),
    };
    // Reminder nur bei echter Änderung mitschicken (Dirty-Tracking). „Keine" →
    // {is_reminder_on:false}; Minutenwert N → {is_reminder_on:true,
    // reminder_minutes_before_start:N}. Der Platzhalter "" ist disabled → wenn
    // reminderDirty, ist reminder immer "none" oder ein Minuten-String.
    if (reminderDirty) {
      if (reminder === "none") {
        patch.is_reminder_on = false;
      } else {
        patch.is_reminder_on = true;
        patch.reminder_minutes_before_start = Number(reminder);
      }
    }
    try {
      await api.patch(
        `/calendar/events/${encodeURIComponent(ev.graph_id)}`,
        patch
      );
      onSaved(patch);
      toast.ok(
        t.calendar.bearbeiten.aktualisiert,
        t.calendar.bearbeiten.aenderungGespeichert
      );
      onClose();
    } catch (e) {
      const status = (e as ApiError)?.status;
      setErr(
        status === 403
          ? t.calendar.bearbeiten.keinZugriff
          : status === 503
            ? t.calendar.bearbeiten.dienstNichtErreichbar
            : t.calendar.bearbeiten.speichernFehlgeschlagen
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<Pencil className="w-4 h-4" />}
      title={t.calendar.bearbeiten.titel}
      description={ev.subject}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t.common.abbrechen}
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Check className="w-4 h-4" />}
            loading={saving}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {t.common.speichern}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t.calendar.bearbeiten.betreff}>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t.calendar.bearbeiten.betreff}
            autoFocus
          />
        </Field>
        {/* Beginn: Datum (TT.MM.JJJJ) + Uhrzeit — in jedem Browser identisch. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label={t.calendar.bearbeiten.beginnDatum}
            error={startDateInvalid ? t.calendar.bearbeiten.bitteTtMmJjjj : undefined}
          >
            <Input
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onBlur={(e) => normalizeDate(e.target.value, setStartDate)}
              placeholder={t.calendar.bearbeiten.platzhalterDatum}
              inputMode="numeric"
              aria-label={t.calendar.bearbeiten.ariaBeginnDatum}
            />
          </Field>
          <Field label={t.calendar.bearbeiten.beginnUhrzeit}>
            <Select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              aria-label={t.calendar.bearbeiten.ariaBeginnUhrzeit}
            >
              {startTimeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {/* Ende: bewusst konkret (nicht als Dauer) — Begründung oben am State. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label={t.calendar.bearbeiten.endeDatum}
            error={endDateInvalid ? t.calendar.bearbeiten.bitteTtMmJjjj : undefined}
          >
            <Input
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onBlur={(e) => normalizeDate(e.target.value, setEndDate)}
              placeholder={t.calendar.bearbeiten.platzhalterDatum}
              inputMode="numeric"
              aria-label={t.calendar.bearbeiten.ariaEndeDatum}
            />
          </Field>
          <Field label={t.calendar.bearbeiten.endeUhrzeit}>
            <Select
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              aria-label={t.calendar.bearbeiten.ariaEndeUhrzeit}
            >
              {endTimeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label={t.calendar.termin.erinnerung}>
          <div className="inline-flex items-center gap-1.5">
            <Bell className="w-4 h-4 text-text-3 shrink-0" />
            <select
              aria-label={t.calendar.termin.erinnerung}
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              className="rounded-md border border-border bg-canvas px-2.5 py-1.5 text-sm text-text focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 cursor-pointer"
            >
              <ReminderOptions cur={reminder} />
            </select>
          </div>
        </Field>
        {invalidRange && (
          <div data-cal-edit-range-error className="text-xs text-danger">
            {t.calendar.bearbeiten.endeNachBeginn}
          </div>
        )}
        {err && (
          <div className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- Online-Meeting-Toggle (geteilt: Wizard + „Neuer Termin") ---------------
// AN → `online_meeting: true` im POST /calendar/events; der email-service setzt
// isOnlineMeeting und liefert `join_url` zurück.
function OnlineMeetingToggle({
  on,
  onToggle,
}: {
  on: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2.5 text-left hover:border-border-strong transition-colors"
    >
      <span className="flex items-center gap-2 text-sm text-text">
        <Video className="w-4 h-4 text-text-3" />
        {t.calendar.neu.onlineMeeting}
      </span>
      <span
        className={clsx(
          "relative h-5 w-9 rounded-full transition-colors shrink-0",
          on ? "bg-accent-500" : "bg-surface-3"
        )}
      >
        <span
          className={clsx(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
            on ? "left-4" : "left-0.5"
          )}
        />
      </span>
    </button>
  );
}

// ---- Termin-Details (Klick auf einen Raster-Block) --------------------------
// Im Zeitraster ist ein 30-Minuten-Block ~24 px hoch — die vier Hover-Icons der
// Listen-Zeile passen dort nicht hinein. Statt eines Höhen-Schwellwerts (ab dem
// Icons erscheinen) öffnet ein Klick dieses Fenster: EIN Ort, an dem ALLE
// Aktionen des Termins sichtbar beieinander stehen (Outlook: „Termin öffnen").
function EventDetailsModal({
  ev,
  onClose,
  actions,
}: {
  ev: CalEvent;
  onClose: () => void;
  actions: EventActions;
}) {
  const t = useT();
  const org = organizerLabel(ev);
  const canAct = !!ev.graph_id;
  const cur = reminderValue(ev);
  // F-32 — der Organisator hat seine eigene Zeile darüber; ihn hier ein zweites
  // Mal zu führen wäre Rauschen. Verglichen wird die ADRESSE (kleingeschrieben),
  // nicht der Name: Graph liefert denselben Menschen je nach Quelle mal als
  // „Fischer, Annemarie", mal als „Annemarie Fischer".
  const orgAdresse = (ev.organizer_address ?? "").trim().toLowerCase();
  const teilnehmer = (ev.attendees ?? []).filter(
    (a) => a.address.trim().toLowerCase() !== orgAdresse
  );
  const [alleTeilnehmer, setAlleTeilnehmer] = useState(false);
  const sichtbare = alleTeilnehmer
    ? teilnehmer
    : teilnehmer.slice(0, TEILNEHMER_KURZ);
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<CalendarClock className="w-4 h-4" />}
      title={ev.subject || t.calendar.termin.keinBetreff}
      description={
        ev.is_all_day
          ? `${fmtWeekdayDate(ev.start)} · ${t.calendar.termin.ganztaegig}`
          : `${fmtWeekdayDate(ev.start)} · ${fmtTime(ev.start)} – ${fmtTime(ev.end)}`
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t.common.schliessen}
          </Button>
          {canAct && (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={<Pencil className="w-4 h-4" />}
                onClick={() => {
                  onClose();
                  actions.onEdit(ev);
                }}
              >
                {t.common.bearbeiten}
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={() => {
                  onClose();
                  actions.onDelete(ev);
                }}
              >
                {t.common.loeschen}
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5 text-sm text-text-2">
          {ev.location && (
            <div className="flex items-start gap-2 min-w-0">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
              <span className="min-w-0 break-words">{ev.location}</span>
            </div>
          )}
          {org && (
            <div className="flex items-start gap-2 min-w-0">
              <UserIcon className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
              <span className="min-w-0 break-words">
                {org}
                {ev.organizer_address ? ` · ${ev.organizer_address}` : ""}
              </span>
            </div>
          )}
          {/* F-32 — „wer ist ausser mir noch eingeladen?". Die Unterscheidung
              `undefined` vs. `[]` ist Absicht: liefert der email-service das
              Feld noch nicht (alter Stand), steht hier GAR NICHTS statt der
              Falschaussage „keine weiteren Teilnehmer". */}
          {ev.attendees &&
            (teilnehmer.length === 0 ? (
              <div className="flex items-start gap-2 min-w-0">
                <Users className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
                <span className="min-w-0 text-text-3">
                  {t.calendar.termin.keineWeiterenTeilnehmer}
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 min-w-0">
                <Users className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
                <div data-termin-teilnehmer className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-text">
                    {t.calendar.termin.teilnehmerAnzahl(teilnehmer.length)}
                  </div>
                  <ul
                    className={clsx(
                      "mt-1 space-y-0.5",
                      alleTeilnehmer && "max-h-40 overflow-y-auto overscroll-contain"
                    )}
                  >
                    {sichtbare.map((a) => (
                      <li key={a.address} className="min-w-0 break-words">
                        <span title={a.address}>{a.name || a.address}</span>
                        <span className="text-text-3">
                          {" · "}
                          {antwortText(t, a.response)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {teilnehmer.length > TEILNEHMER_KURZ && (
                    <button
                      type="button"
                      data-teilnehmer-mehr
                      onClick={() => setAlleTeilnehmer((v) => !v)}
                      className="mt-1 text-xs font-medium text-accent-600 hover:underline"
                    >
                      {alleTeilnehmer
                        ? t.calendar.termin.wenigerAnzeigen
                        : t.calendar.termin.weitereAnzeigen(
                            teilnehmer.length - TEILNEHMER_KURZ
                          )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          {ev.is_cancelled && (
            <StatusBadge status="danger">{t.calendar.termin.abgesagt}</StatusBadge>
          )}
        </div>

        {/* Teams-Beitreten (X2 Slice C: join_url kommt aus /calendar/view). */}
        {ev.join_url && (
          <a
            href={ev.join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:underline"
          >
            <Video className="w-4 h-4" />
            {t.calendar.termin.teamsBeitreten}
          </a>
        )}

        {canAct ? (
          <>
            <Field label={t.calendar.termin.erinnerung}>
              <div className="inline-flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-text-3 shrink-0" />
                <select
                  aria-label={t.calendar.termin.erinnerung}
                  value={cur}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") return;
                    actions.onReminder(ev, v === "none" ? null : Number(v));
                  }}
                  className="rounded-md border border-border bg-canvas px-2.5 py-1.5 text-sm text-text focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 cursor-pointer"
                >
                  <ReminderOptions cur={cur} />
                </select>
              </div>
            </Field>

            <div>
              <div className="text-xs font-medium text-text mb-1.5">
                {t.calendar.termin.antwortAnOrganisator}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Check className="w-4 h-4" />}
                  onClick={() => {
                    actions.onRespond(ev, "accept");
                    onClose();
                  }}
                >
                  {t.calendar.termin.zusagen}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<HelpCircle className="w-4 h-4" />}
                  onClick={() => {
                    actions.onRespond(ev, "tentativelyAccept");
                    onClose();
                  }}
                >
                  {t.calendar.termin.mitVorbehalt}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<X className="w-4 h-4" />}
                  onClick={() => {
                    actions.onRespond(ev, "decline");
                    onClose();
                  }}
                >
                  {t.calendar.termin.absagen}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-3" />
            <span>{t.calendar.termin.keineTerminId}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- Neuer Termin (Klick/Ziehen im Raster oder Toolbar-Button) --------------
// Der schnelle Weg für „Termin um 10:00, 30 Minuten" — ohne Verfügbarkeits-
// Suche. Teilnehmer sind OPTIONAL und eingeklappt: leer = Termin nur für mich
// (der email-service erlaubt `attendee_upns: []`), ausgeklappt = Einladung.
//
// Eingabe-Modell wie in Outlook: Datum (TT.MM.JJJJ) + Startzeit + DAUER. Das
// Ende wird gerechnet, nicht getippt. Ein Enddatum braucht nur, wer einen
// mehrtägigen Termin anlegt — dann bestimmt die Dauer die End-UHRZEIT und das
// Enddatum den End-TAG (z. B. Messe 12.–14.03., 09:00 + 8 Std. = bis 17:00).
function NewEventModal({
  slot,
  colleagues,
  onClose,
  onCreated,
}: {
  // `at` = Klick-Punkt (Client-Koordinaten); das Fenster geht mittig um den
  // Zeiger auf. Fehlt er (Tastatur-Auslösung), bleibt es in der Viewport-Mitte.
  slot: { start: Date; end: Date; at?: { x: number; y: number } };
  colleagues: Colleague[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [subject, setSubject] = useState("");
  const [startDate, setStartDate] = useState(() => fmtDeDate(slot.start));
  const [startTime, setStartTime] = useState(() => fmtHm(slot.start));
  const [duration, setDuration] = useState(() =>
    Math.max(5, Math.round((slot.end.getTime() - slot.start.getTime()) / 60_000))
  );
  // Leer = eintägig (Regelfall). Der Klick-Slot ist immer eintägig, deshalb
  // startet das Feld leer und das Ende ergibt sich aus Start + Dauer.
  const [endDate, setEndDate] = useState("");
  const [attendees, setAttendees] = useState<Colleague[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Teams-Meeting ist der Normalfall (Hybrid-Alltag) → beim Öffnen AN.
  const [onlineMeeting, setOnlineMeeting] = useState(true);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const startAt = useMemo(() => {
    const d = parseDeDate(startDate, slot.start);
    return d ? dateAtMinute(d, minutesFromHm(startTime)) : null;
  }, [startDate, startTime, slot.start]);

  const endDay = endDate.trim() ? parseDeDate(endDate, slot.start) : null;
  const endDateInvalid = endDate.trim() !== "" && !endDay;
  const startMin = minutesFromHm(startTime);

  // MIT Enddatum ist „Startzeit + Dauer" das TAGESFENSTER eines mehrtägigen
  // Termins (Messe 12.–14.03., täglich 09:00–17:00). Ein Fenster, das Mitternacht
  // erreicht oder überschreitet, ist in dieser Lesart widersprüchlich — und darf
  // vor allem NICHT still auf den Tag NACH dem eingetragenen Enddatum rutschen
  // (genau das tat die frühere Rechnung: 18:00 + 8 Std. + Enddatum 12.06. endete
  // am 13.06. um 02:00). Deshalb: sichtbarer Eingabefehler, Speichern gesperrt.
  // OHNE Enddatum bleibt der Übernacht-Termin gültig (18:00 + 8 Std. → 02:00).
  const dayWindowOverflow = !!endDay && startMin + duration >= 1440;

  const endAt = useMemo(() => {
    if (!startAt) return null;
    if (endDay && startMin + duration >= 1440) return null; // ungültige Eingabe
    // BEIDE Zweige rechnen in WANDUHR-Zeit (Kalender-Semantik wie Outlook):
    // „09:00 + 8 Std." ist 17:00 — auch am Zeitumstellungstag, wo die real
    // verstrichene Zeit 7 bzw. 9 Stunden beträgt. `dateAtMinute` rollt Minuten
    // ≥ 24 h in den Folgetag, deshalb funktioniert der Übernacht-Fall ohne
    // Enddatum damit ebenfalls. (Vorher rechnete der Zweig ohne Enddatum
    // absolut in ms und lieferte am Umstellungstag 420 statt 480 Minuten.)
    return dateAtMinute(endDay ?? startAt, startMin + duration);
  }, [startAt, endDay, startMin, duration]);

  const startDateInvalid = startDate.trim() !== "" && !startAt;
  const invalidRange = !!startAt && !!endAt && endAt.getTime() <= startAt.getTime();
  const canSave =
    !!subject.trim() &&
    !!startAt &&
    !!endAt &&
    !endDateInvalid &&
    !dayWindowOverflow &&
    !invalidRange;

  // Krumme Werte aus einem Zieh-Slot (z. B. 10:07 gibt es nicht, 75 Min. schon)
  // als Zusatz-Eintrag ergänzen, damit die Liste nie einen falschen Wert zeigt.
  const timeOptions = useMemo(() => withCurrent(TIME_OPTIONS, startTime), [startTime]);
  const durationOptions = useMemo(
    () => withCurrent(DURATION_OPTIONS, duration),
    [duration]
  );

  /** Beim Verlassen des Feldes auf TT.MM.JJJJ normalisieren (1.2. → 01.02.2026). */
  const normalizeDate = (v: string, set: (s: string) => void) => {
    if (!v.trim()) return;
    const d = parseDeDate(v, slot.start);
    if (d) set(fmtDeDate(d));
  };

  const toggle = (c: Colleague) =>
    setAttendees((prev) =>
      prev.some((p) => p.email.toLowerCase() === c.email.toLowerCase())
        ? prev.filter((p) => p.email.toLowerCase() !== c.email.toLowerCase())
        : [...prev, c]
    );

  const externalCount = externalParticipants(attendees).length;

  const save = async () => {
    if (!canSave || !startAt || !endAt) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await api.post<EventCreateResponse>("/calendar/events", {
        subject: subject.trim(),
        start: startAt.toISOString(),
        end: endAt.toISOString(),
        attendee_upns: attendees.map((a) => a.email),
        body_text: description.trim() || undefined,
        // Nur senden, wenn AN — payload-gleich zum Bestandsflow (Wizard).
        ...(onlineMeeting ? { online_meeting: true } : {}),
      });
      toast.ok(
        attendees.length > 0
          ? t.calendar.neu.einladungGesendet
          : t.calendar.neu.angelegt,
        res.join_url ? t.calendar.neu.teamsLinkImTermin : subject.trim()
      );
      onCreated();
      onClose();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      anchorPoint={slot.at}
      icon={<CalendarPlus className="w-4 h-4" />}
      title={t.calendar.neu.titel}
      description={rangeSummary(t, startAt, endAt)}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t.common.abbrechen}
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={
              attendees.length > 0 ? (
                <Send className="w-4 h-4" />
              ) : (
                <Check className="w-4 h-4" />
              )
            }
            loading={saving}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {attendees.length > 0
              ? t.calendar.neu.einladungSenden
              : t.calendar.neu.terminAnlegen}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t.calendar.neu.betreff}>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t.calendar.neu.betreffPlatzhalter}
            autoFocus
          />
        </Field>
        {/* Datum · Beginn · Dauer — das Ende rechnet der Dialog. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field
            label={t.calendar.neu.datum}
            error={startDateInvalid ? t.calendar.bearbeiten.bitteTtMmJjjj : undefined}
          >
            <Input
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onBlur={(e) => normalizeDate(e.target.value, setStartDate)}
              placeholder={t.calendar.bearbeiten.platzhalterDatum}
              inputMode="numeric"
              aria-label={t.calendar.neu.ariaDatum}
            />
          </Field>
          <Field label={t.calendar.neu.beginn}>
            <Select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              aria-label={t.calendar.neu.ariaStartzeit}
            >
              {timeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.calendar.neu.dauer}>
            <Select
              value={String(duration)}
              onChange={(e) => setDuration(Number(e.target.value))}
              aria-label={t.calendar.neu.ariaDauer}
            >
              {durationOptions.map((d) => (
                <option key={d} value={d}>
                  {durationLabel(d)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {/* Enddatum: nur für mehrtägige Termine; die Dauer bleibt die
            End-Uhrzeit an diesem Tag. Sitzt bewusst unter „Datum" (gleiche
            Spalte = gleiche Bedeutung). */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field
            label={t.calendar.neu.enddatum}
            optional
            hint={t.calendar.neu.enddatumHinweis}
            error={endDateInvalid ? t.calendar.bearbeiten.bitteTtMmJjjj : undefined}
          >
            <Input
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onBlur={(e) => normalizeDate(e.target.value, setEndDate)}
              placeholder={t.calendar.bearbeiten.platzhalterDatum}
              inputMode="numeric"
              aria-label={t.calendar.neu.ariaEnddatum}
            />
          </Field>
        </div>

        {/* Tagesfenster über Mitternacht + Enddatum = widersprüchlich. Sichtbar
            melden statt still auf den Folgetag zu rutschen (siehe endAt). */}
        {dayWindowOverflow && (
          <div
            data-cal-daywindow-error
            className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {t.calendar.neu.mitternachtBeginn}{" "}
              <span className="tabular-nums">{startTime}</span> +{" "}
              {durationLabel(duration)} {t.calendar.neu.mitternachtRest1}{" "}
              <span className="font-medium">{t.calendar.neu.mitternachtTaeglich}</span>{" "}
              {t.calendar.neu.mitternachtRest2}
            </span>
          </div>
        )}

        {/* Teilnehmer: eingeklappt = „nur für mich" ohne einen einzigen Klick. */}
        <div className="rounded-lg border border-border">
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            aria-expanded={pickerOpen}
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
          >
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text">
              <Users className="w-4 h-4 text-text-3" />
              {attendees.length === 0
                ? t.calendar.neu.teilnehmerEinladen
                : t.calendar.neu.teilnehmerEingeladen(attendees.length)}
              {/* Externe auch im EINGEKLAPPTEN Zustand sichtbar — sonst wäre
                  „Einladung verlässt das Haus" einen Klick weit weg. */}
              {externalCount > 0 && (
                <span
                  data-attendee-external-count
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-warning-soft text-warning border border-warning/40 px-2 py-0.5 text-[11px] font-medium"
                >
                  <Building2 className="w-3 h-3" />
                  {t.calendar.neu.extern(externalCount)}
                </span>
              )}
            </span>
            <span className="shrink-0 text-xs text-text-3">
              {pickerOpen ? t.calendar.neu.zuklappen : t.calendar.neu.auswaehlen}
            </span>
          </button>
          {pickerOpen && (
            <div className="border-t border-border p-3">
              <ParticipantPicker
                colleagues={colleagues}
                selected={attendees}
                onToggle={toggle}
              />
            </div>
          )}
        </div>

        <OnlineMeetingToggle
          on={onlineMeeting}
          onToggle={() => setOnlineMeeting((v) => !v)}
        />

        <Field label={t.calendar.neu.beschreibung} optional>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[70px]"
            placeholder={t.calendar.neu.beschreibungPlatzhalter}
          />
        </Field>

        {invalidRange && (
          <div className="text-xs text-danger">{t.calendar.neu.endeNachBeginn}</div>
        )}
        {err && (
          <div className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {err.status === 422
                ? t.calendar.neu.fehlerNichtEinladbar
                : err.status === 503
                  ? t.calendar.neu.fehlerDienst
                  : t.calendar.neu.fehlerAnlegen}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- „Termin finden"-Wizard ------------------------------------------------

type PeriodPreset = "this_week" | "next_week" | "next_14";

/** Reihenfolge der Zeitraum-Knöpfe. War bis zur Uebersetzung die
 *  Schluessel-Reihenfolge von `PERIOD_LABELS` (`Object.keys`); jetzt steht sie
 *  explizit hier, weil die Beschriftungen aus dem Katalog kommen. */
const PERIOD_PRESETS: PeriodPreset[] = ["this_week", "next_week", "next_14"];

function periodLabel(t: Katalog, p: PeriodPreset): string {
  if (p === "this_week") return t.calendar.wizard.dieseWoche;
  if (p === "next_week") return t.calendar.wizard.naechsteWoche;
  return t.calendar.wizard.naechste14Tage;
}

const DURATIONS = [30, 45, 60] as const;

/** Wie viele Vorschläge der Server rechnen soll, und wie viele der Wizard
 *  zunächst zeigt.
 *
 *  Beides zusammen und nicht nur eine Zahl, weil die Antwort seit W10
 *  PRÄFIX-STABIL ist (`services/calendar.py::_select_spread` im email-service):
 *  die ersten 12 der 20 sind exakt dieselben 12, die eine Anfrage mit 12
 *  geliefert hätte. Also EINMAL das Maximum holen und clientseitig aufklappen —
 *  „mehr anzeigen" kostet dann keinen zweiten Egress und keine Wartezeit.
 *
 *  20 ist die Server-Grenze (`max_suggestions … le=20`). 12 statt der bisherigen
 *  8 als Anzeige-Menge: die Klage lautete „immer nur 8", und 12 sind in der
 *  zweispaltigen Liste sechs Zeilen — noch überfliegbar. 20 sofort zu zeigen
 *  wäre eine Wand, aus der niemand wählt.
 *
 *  Bewusst KEINE Nutzer-Einstellung, obwohl die Geschäftszeiten eine sind: wie
 *  viele Vorschläge gerade passen, hängt am einzelnen Termin (kurzfristig? viele
 *  Teilnehmer?) und nicht an einer dauerhaften Haltung des Nutzers. Dafür ist
 *  ein Knopf am Ort der Entscheidung richtig und ein Schalter in den
 *  Einstellungen falsch — den würde niemand pflegen. */
const SLOT_FETCH_MAX = 20;
const SLOT_VISIBLE_DEFAULT = 12;

/** #58 — Selbst-Aktualisierung des Kalenders. Derselbe Takt wie im Mail-Tab
 *  (`Mail.tsx::REFRESH_MS`): zwei Minuten sind schnell genug, damit ein von
 *  einem Kollegen gebuchter Termin auftaucht, bevor jemand danach fragt, und
 *  langsam genug, dass ein offener Tab keine Last erzeugt. */
const CAL_REFRESH_MS = 120_000;

/** Graph `getSchedule` prüft höchstens 20 Kalender pro Aufruf — Organisator
 *  eingerechnet. Darüber antwortet der Dienst nicht mit einer Teilmenge,
 *  sondern gar nicht. Der Wizard muss die Grenze deshalb SELBST einhalten und
 *  sichtbar sagen, was er weglässt. */
const MAX_CALENDARS = 20;

/** Höchstzahl Termine, die gleichzeitig zur Abstimmung gestellt werden können.
 *
 *  Bewusst 5 und nicht die Server-Grenze (20, `MAX_SLOTS` in
 *  app/schemas/calendar_poll.py) und auch nicht die 8 Vorschläge, die die Suche
 *  liefert: die Zahl begrenzt nicht den Server, sondern die Arbeit des
 *  Empfängers. Wer 8 Termine zur Wahl bekommt, muss 8 Kalender-Zeilen prüfen,
 *  bevor er antworten kann — die Rückmeldequote fällt mit jeder Option. Fünf
 *  decken „zwei Tage, je zwei bis drei Zeiten" ab und bleiben in einer Mail
 *  überfliegbar.
 *
 *  Die Grenze greift NICHT still: ist sie erreicht, sagt der Wizard es (Notiz
 *  über der Liste) und sperrt die ungewählten Karten sichtbar, statt Klicks
 *  wortlos verfallen zu lassen. */
const MAX_POLL_SLOTS = 5;

/** Identität eines Slots. Die Vorschläge tragen keine Id (der Server rechnet
 *  sie pro Suche neu aus) — Beginn + Ende sind der Schlüssel. */
function slotKey(s: Slot): string {
  return `${s.start}|${s.end}`;
}

function periodWindow(preset: PeriodPreset): { start: Date; end: Date } {
  const now = new Date();
  if (preset === "next_14") {
    return { start: now, end: addDays(now, 14) };
  }
  // Montag-basierte Woche (Mo=0).
  const dow = (now.getDay() + 6) % 7;
  const monday = startOfDay(addDays(now, -dow));
  if (preset === "this_week") {
    const friday = new Date(addDays(monday, 4));
    friday.setHours(18, 0, 0, 0);
    return { start: now, end: friday > now ? friday : addDays(now, 3) };
  }
  // next_week
  const nextMonday = new Date(monday);
  nextMonday.setDate(nextMonday.getDate() + 7);
  nextMonday.setHours(8, 0, 0, 0);
  const nextFriday = new Date(addDays(nextMonday, 4));
  nextFriday.setHours(18, 0, 0, 0);
  return { start: nextMonday, end: nextFriday };
}

// Drei Schritte, seit Teilnehmer und Zeitraum auf EINER Seite stehen (#79).
// Beides gehört zur selben Frage („wer, und wann ungefähr?"), und der Zeitraum
// war der kürzeste der vier Schritte — ein eigener Halt dafür kostete einen
// Klick, ohne eine Entscheidung zu tragen.
const STEP_COUNT = 3;

function stepLabels(t: Katalog): string[] {
  return [
    t.calendar.wizard.schrittTeilnehmerZeitraum,
    t.calendar.wizard.schrittSlot,
    t.calendar.wizard.schrittEinladung,
  ];
}

/** `lastLabel` überschreibt NUR den letzten Schritt: steht die Runde auf
 *  Abstimmung statt Einladung, darf der Fortschritt nicht weiter „Einladung"
 *  sagen — das ist die dritte Stelle (neben Hauptknopf und Zusammenfassung),
 *  an der der Nutzer liest, was gleich passiert. */
function Stepper({ step, lastLabel }: { step: number; lastLabel?: string }) {
  const t = useT();
  const alle = stepLabels(t);
  const labels = lastLabel ? [...alle.slice(0, -1), lastLabel] : alle;
  return (
    <div className="flex items-center gap-1.5">
      {labels.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const active = n === step;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <div className="flex items-center gap-1.5">
              <span
                className={clsx(
                  "w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold shrink-0",
                  done && "bg-accent-500 text-white",
                  active && "bg-accent-100 text-accent-700 ring-2 ring-accent-500",
                  !done && !active && "bg-surface-3 text-text-3"
                )}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : n}
              </span>
              <span
                data-stepper-label
                className={clsx(
                  "text-xs hidden sm:inline",
                  active ? "text-text font-medium" : "text-text-3"
                )}
              >
                {label}
              </span>
            </div>
            {n < STEP_COUNT && (
              <span
                className={clsx(
                  "w-4 sm:w-6 h-px",
                  done ? "bg-accent-500" : "bg-border"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Zwei Gruppen, wie Outlook den Verteiler liest: An → Pflicht, Cc → Optional.
 *  Die Rolle ist zugleich die Droppable-Id — ein gezogener Chip landet über
 *  `over.id` direkt in der Zielgruppe. */
type AttendeeRole = "required" | "optional";

function roleLabel(t: Katalog, role: AttendeeRole): string {
  return role === "required"
    ? t.calendar.picker.rollePflicht
    : t.calendar.picker.rolleOptional;
}
function roleHint(t: Katalog, role: AttendeeRole): string {
  return role === "required"
    ? t.calendar.picker.rollePflichtHinweis
    : t.calendar.picker.rolleOptionalHinweis;
}
function roleEmpty(t: Katalog, role: AttendeeRole): string {
  return role === "required"
    ? t.calendar.picker.rollePflichtLeer
    : t.calendar.picker.rolleOptionalLeer;
}

/** Maße der beiden Chip-Knöpfe an EINER Stelle: der Knopf selbst und der
 *  unsichtbare Platzhalter der Zieh-Kopie müssen exakt gleich breit sein (siehe
 *  `preview`). Zwei getrennte Klassenlisten liefen sonst auseinander, und der
 *  Fehler wäre wieder ein Versatz unter dem Zeiger. */
const CHIP_MOVE_BOX =
  "inline-flex items-center gap-1 rounded-full bg-surface border border-current/40 shadow-sm px-1.5 py-0.5 text-[11px] font-medium";
const CHIP_REMOVE_BOX = "rounded-full p-0.5";

/** Chip eines gewählten Teilnehmers. Extern (Adresse außerhalb des Hauses) ist
 *  sichtbar anders: Warn-Farbe, Wort „Extern" + Firma — der Nutzer muss VOR dem
 *  Senden sehen, dass die Einladung das Haus verlässt.
 *
 *  `move`/`drag` sind NUR im Zwei-Gruppen-Modus des Wizards gesetzt. Der
 *  Schnell-Dialog (NewEventModal) gibt beide nicht mit → der Chip rendert exakt
 *  wie bisher, mit dem X als einzigem Knopf. */
function ParticipantChip({
  c,
  onRemove,
  move,
  drag,
  preview,
}: {
  c: Colleague;
  /** Fehlt der Handler, fällt das X weg. Genau ein Aufrufer nutzt das: die
   *  `DragOverlay`-Kopie, die nur mitfliegt und nichts bedient. Ein sichtbarer
   *  Knopf, der nichts tut, ist ein Versprechen, das die UI nicht hält. */
  onRemove?: () => void;
  /** Nur-Optik-Kopie für die `DragOverlay`. Rendert die BREITE der beiden
   *  Knöpfe als unsichtbare Platzhalter nach, ohne Knöpfe zu sein.
   *
   *  Warum das nötig ist: dnd-kit verankert die Hülle am Rect des GEGRIFFENEN
   *  Chips (also inklusive seiner Knöpfe) und verschiebt sie um das
   *  Zeiger-Delta. Der Zeiger sitzt damit immer an derselben Stelle INNERHALB
   *  der Hülle, an der er den Chip gegriffen hat. Ist die Kopie schmaler als
   *  die Hülle, stimmt das nur noch für einen einzigen Griffpunkt:
   *  linksbündig für den linken Rand, zentriert für die Mitte — bei
   *  „Maximiliane von Grünberg-Hohenstein" lag der Zeiger sonst bis zu 198 px
   *  neben der Kopie, und auf Touch ist ein Griff außerhalb der Mitte der
   *  Normalfall. Gleiche Breite heißt: deckungsgleich bei JEDEM Griffpunkt.
   *
   *  Platzhalter sind `<span>`, KEIN `<button>` — ein Knopf ohne Funktion war
   *  hier schon einmal der Fehler (t9-verteiler-walk misst deshalb, dass die
   *  Kopie null Knöpfe trägt). `visibility: hidden` belegt Platz, malt nichts,
   *  ist nicht fokussierbar und nicht im Screenreader. */
  preview?: { moveTo: AttendeeRole; remove: boolean };
  /** Sichtbar beschrifteter, tastaturerreichbarer Weg in die andere Gruppe.
   *  Steht NEBEN dem Ziehen, nicht statt seiner: reines Drag&Drop ist weder mit
   *  der Tastatur bedienbar noch auf Touch verlässlich, und ein Weg, den man
   *  nicht sieht, existiert für den Nutzer nicht (Doc 04). */
  move?: { to: AttendeeRole; onMove: () => void };
  /** dnd-kit-Verdrahtung. BEWUSST ohne dessen `attributes`: die setzen
   *  role="button" + tabIndex auf den Chip — der enthält aber selbst zwei
   *  Knöpfe (verschieben, entfernen), und Interaktives in Interaktivem ist für
   *  Screenreader/Tastatur kaputt. Der Tastaturweg ist `move`. */
  drag?: {
    ref: (el: HTMLElement | null) => void;
    listeners: ReturnType<typeof useDraggable>["listeners"];
    dragging: boolean;
  };
}) {
  const t = useT();
  const external = isExternalParticipant(c);
  return (
    <span
      ref={drag?.ref}
      {...(drag?.listeners ?? {})}
      data-participant-chip
      data-external={external ? "true" : "false"}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-1 text-xs font-medium border",
        external
          ? "bg-warning-soft text-warning border-warning/40"
          : "bg-accent-100 text-accent-700 border-transparent",
        // `touch-none` ist Pflicht, sonst gewinnt das Scrollen den Zeiger und
        // der Drag startet auf Touch nie.
        drag && "cursor-grab active:cursor-grabbing touch-none",
        drag?.dragging && "opacity-40"
      )}
    >
      {external ? (
        <Building2 className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <UserIcon className="w-3.5 h-3.5 shrink-0" />
      )}
      <span className="truncate max-w-[16rem]">{c.name}</span>
      {external && (
        <span data-participant-chip-badge className="font-normal opacity-90">
          {t.calendar.picker.chipExtern}
          {c.account ? ` · ${c.account}` : ""}
          {(c.accountCount ?? 1) > 1 ? ` +${(c.accountCount ?? 1) - 1}` : ""}
        </span>
      )}
      {move && (
        // Der Knopf muss auf den ERSTEN Blick eine Aktion sein, nicht ein
        // zweites Etikett am Namen. Vorher trug er die nackte Zielgruppe hinter
        // einem Pfeil („→ Optional") in derselben Fläche und Farbe wie das
        // „· Extern"-Etikett daneben — im Pflicht-Kasten las sich das wie
        // „Kollege 01 IST optional", also das Gegenteil der Lage, und bei 25
        // Chips 25 Mal. Zwei Änderungen, beide sichtbar ohne Hover (versteckte
        // Aktionen sind auf Touch unauffindbar):
        //   • eigene Fläche (`bg-surface` + Rand + Schatten) statt eines
        //     randlosen Etiketts — ein Knopf sitzt AUF dem Chip, nicht IN ihm.
        //   • „Zu Optional" mit Tausch-Pfeil statt „→ Optional": eine Richtung
        //     kann kein Zustand sein.
        // Die Zielgruppe bleibt ausgeschrieben (nicht nur im Tooltip): ein Weg,
        // den man nicht liest, existiert für den Nutzer nicht (Doc 04).
        <button
          type="button"
          data-chip-move
          data-chip-move-to={move.to}
          onClick={move.onMove}
          aria-label={t.calendar.picker.chipVerschiebenAria(
            c.name,
            roleLabel(t, move.to)
          )}
          title={t.calendar.picker.chipVerschiebenAria(c.name, roleLabel(t, move.to))}
          className={clsx(CHIP_MOVE_BOX, "hover:bg-surface-3 active:translate-y-px")}
        >
          <ArrowRightLeft className="w-3 h-3 shrink-0" aria-hidden />
          {t.calendar.picker.chipZu(roleLabel(t, move.to))}
        </button>
      )}
      {/* Platzhalter der Zieh-Kopie: exakt die Fläche des Knopfes, unsichtbar. */}
      {preview && (
        <span aria-hidden className={clsx(CHIP_MOVE_BOX, "invisible")}>
          <ArrowRightLeft className="w-3 h-3 shrink-0" />
          {t.calendar.picker.chipZu(roleLabel(t, preview.moveTo))}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          data-chip-remove
          onClick={onRemove}
          aria-label={t.calendar.picker.chipEntfernenAria(c.name)}
          title={t.calendar.picker.chipEntfernenAria(c.name)}
          className={clsx(CHIP_REMOVE_BOX, "hover:bg-black/10")}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {preview?.remove && (
        <span aria-hidden className={clsx(CHIP_REMOVE_BOX, "invisible")}>
          <X className="w-3.5 h-3.5" />
        </span>
      )}
    </span>
  );
}

// ---- Pflicht/Optional-Gruppen des Wizards ----------------------------------

function DraggableParticipantChip({
  c,
  role,
  onMove,
  onRemove,
}: {
  c: Colleague;
  role: AttendeeRole;
  onMove: (c: Colleague, to: AttendeeRole) => void;
  onRemove: (c: Colleague) => void;
}) {
  const { listeners, setNodeRef, isDragging } = useDraggable({ id: c.email });
  const other: AttendeeRole = role === "required" ? "optional" : "required";
  return (
    <ParticipantChip
      c={c}
      onRemove={() => onRemove(c)}
      move={{ to: other, onMove: () => onMove(c, other) }}
      drag={{ ref: setNodeRef, listeners, dragging: isDragging }}
    />
  );
}

/** Eine Gruppe = eine real gerenderte Drop-Fläche mit `min-h`, damit auch die
 *  LEERE Gruppe ein Ziel ist (sonst kann man den letzten Chip nie zurückholen). */
function AttendeeGroup({
  role,
  members,
  onMove,
  onRemove,
}: {
  role: AttendeeRole;
  members: Colleague[];
  onMove: (c: Colleague, to: AttendeeRole) => void;
  onRemove: (c: Colleague) => void;
}) {
  const t = useT();
  const { setNodeRef, isOver } = useDroppable({ id: role });
  return (
    <div>
      <div className="flex items-center justify-between px-0.5 pb-1">
        <span
          data-attendee-group-heading={role}
          className="text-xs font-medium text-text"
        >
          {roleLabel(t, role)}
        </span>
        <span className="text-xs text-text-3 tabular-nums">{members.length}</span>
      </div>
      <div
        ref={setNodeRef}
        data-attendee-group={role}
        className={clsx(
          "rounded-lg border p-2 flex flex-wrap content-start gap-1.5 min-h-[64px] transition-colors",
          isOver
            ? "border-accent-500 bg-accent-500/5"
            : "border-border bg-surface-2/40"
        )}
      >
        {members.length === 0 ? (
          <span className="w-full self-center text-center text-xs text-text-3 italic select-none">
            {roleEmpty(t, role)}
          </span>
        ) : (
          members.map((c) => (
            <DraggableParticipantChip
              key={c.email}
              c={c}
              role={role}
              onMove={onMove}
              onRemove={onRemove}
            />
          ))
        )}
      </div>
      <div className="px-0.5 pt-1 text-[11px] text-text-3">{roleHint(t, role)}</div>
    </div>
  );
}

/** Die beiden Gruppen samt Ziehen zwischen ihnen.
 *
 *  DnD-Mechanik gespiegelt von `pages/ImageLibrary.tsx` (dort ausführlich
 *  begründet): PointerSensor 6 px trennt Klick von Drag (die Chips tragen zwei
 *  Knöpfe), TouchSensor 150 ms/8 px lässt Wischen Scrollen bleiben,
 *  `pointerWithin` mit `rectIntersection`-Fallback, `MeasuringStrategy.Always`.
 *
 *  EIN sichtbares Layout, keine zweite versteckte Kopie: ein per CSS verstecktes
 *  Parallel-Layout hat in diesem Repo schon einmal 0×0-Droppable-Rects erzeugt —
 *  DnD war tot und nichts hat es gemeldet (siehe Kanban.tsx:1606). */
function AttendeeGroups({
  required,
  optional,
  onMove,
  onRemove,
}: {
  required: Colleague[];
  optional: Colleague[];
  onMove: (c: Colleague, to: AttendeeRole) => void;
  onRemove: (c: Colleague) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const within = pointerWithin(args);
    return within.length > 0 ? within : rectIntersection(args);
  }, []);

  const all = useMemo(() => [...required, ...optional], [required, optional]);
  const active = activeId
    ? all.find((c) => c.email === activeId) ?? null
    : null;
  // Aus WELCHER Gruppe der gezogene Chip stammt — die Kopie muss denselben
  // Knopf-Platz nachbilden, und dessen Beschriftung („Zu Optional" bzw.
  // „Zu Pflicht") ist unterschiedlich breit.
  const activeRole: AttendeeRole | null = !activeId
    ? null
    : required.some((c) => c.email === activeId)
      ? "required"
      : optional.some((c) => c.email === activeId)
        ? "optional"
        : null;

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active: a, over } = e;
    if (!over) return;
    const c = all.find((x) => x.email === String(a.id));
    if (!c) return;
    const to = String(over.id) as AttendeeRole;
    if (to !== "required" && to !== "optional") return;
    onMove(c, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {/* Untereinander, jede Gruppe über die volle Breite — nicht nebeneinander:
          in zwei Spalten bleiben einem externen Chip („Name · Extern · Firma"
          plus Verschiebe-Knopf) keine 340 px, der Name wird abgeschnitten und
          das Firmen-Kürzel bricht um. Die Größe folgt hier dem Inhalt, nicht
          einem Raster. Die Reihenfolge Pflicht → Optional ist zugleich die
          Rangfolge. */}
      <div data-attendee-groups className="space-y-3">
        <AttendeeGroup
          role="required"
          members={required}
          onMove={onMove}
          onRemove={onRemove}
        />
        <AttendeeGroup
          role="optional"
          members={optional}
          onMove={onMove}
          onRemove={onRemove}
        />
      </div>
      {/* Die mitfliegende Kopie ist reine Optik — OHNE `onRemove`, damit sie
          kein X trägt. Ein X am Overlay wäre ein Bedienelement, das sichtbar
          ist und nichts tut (Klicken geht während des Ziehens ohnehin nicht).

          AN `document.body` GEHÄNGT, nicht hier ins Modal — sonst klebt die
          Kopie neben dem Zeiger statt an ihm:
          `DragOverlay` positioniert sich mit `position: fixed` und setzt als
          `top`/`left` das VIEWPORT-Rect des gegriffenen Chips
          (@dnd-kit/core, PositionedOverlay). Ein Vorfahr mit `transform` wird
          laut CSS zum umschließenden Block für `fixed` — und der Modal-Rahmen
          trägt genau so einen (`-translate-x-1/2 -translate-y-1/2` zum
          Zentrieren, ui/modal.tsx). Die Viewport-Koordinaten wurden dadurch
          gegen die Modal-Ecke aufgelöst; gemessen wurden 441/206 px Versatz bei
          einem Modal-Ursprung von 440/197 (frontend/termin-finden-dragdiag.mjs).
          Der Portal hängt den Knoten AUS dem Modal heraus, `fixed` meint wieder
          das Fenster. React-Kontext läuft durch Portale — die Verdrahtung mit
          dem DndContext bleibt.

          KEIN Eingriff in ui/modal.tsx: die Zentrier-transform tragen alle
          Modals der App, und sie zu entfernen hieße, jedes davon neu zu
          zentrieren. */}
      {createPortal(
        // Die Hülle ist so BREIT WIE DER GEGRIFFENE CHIP — also inklusive seiner
        // zwei Knöpfe. Die Kopie bildet deren Fläche über `preview` unsichtbar
        // nach und ist damit gleich breit: sie deckt sich mit dem Original, egal
        // WO der Nutzer gegriffen hat.
        // Verworfen: die schmalere Kopie in der Hülle zentrieren. Das trifft nur
        // den Griff in der Chip-Mitte; am linken Rand gegriffen lag der Zeiger
        // dann um die halbe Breitendifferenz daneben (bei langen Namen bis
        // 198 px, also ganz außerhalb der Kopie) — und auf Touch ist ein Griff
        // außerhalb der Mitte der Normalfall.
        <DragOverlay dropAnimation={null}>
          {active ? (
            <ParticipantChip
              c={active}
              preview={
                activeRole
                  ? {
                      moveTo: activeRole === "required" ? "optional" : "required",
                      remove: true,
                    }
                  : undefined
              }
            />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

/** Sichtbare Warnung, sobald mindestens ein Externer eingetragen ist. Steht
 *  direkt unter den Chips — ein versehentlich eingeladener Kunde ist echter
 *  Schaden, das darf nicht nur im Chip stehen. */
export function ExternalAttendeeWarning({ selected }: { selected: Colleague[] }) {
  const t = useT();
  const ext = externalParticipants(selected);
  if (ext.length === 0) return null;
  const firmen = Array.from(
    new Set(ext.map((e) => e.account).filter((a): a is string => !!a))
  );
  return (
    <div
      data-participant-external-warning
      className="flex items-start gap-2 rounded-md bg-warning-soft border border-warning/40 px-3 py-2 text-xs text-warning"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        {ext.length === 1
          ? t.calendar.picker.externWarnungEiner
          : t.calendar.picker.externWarnungMehrere(ext.length)}
        {firmen.length > 0 ? ` (${firmen.join(", ")})` : ""}:{" "}
        <span className="font-medium">{ext.map((e) => e.email).join(", ")}</span>
      </span>
    </div>
  );
}

/** Befund des Prefill-Abgleichs zu einer vorbelegten EXTERNEN Adresse.
 *
 *  Über den Picker gewählte Externe sind per Konstruktion SF-Kontakte (sie
 *  KOMMEN aus `/calendar/contacts`); nur der Prefill aus „Termin aus dieser
 *  Mail" trägt eine ungeprüfte Adresse ein. Der Event-Pfad verlangt für Externe
 *  einen `Contact` (app/routers/calendar.py:153-165) — ohne diesen Hinweis
 *  erführe der Nutzer das erst als 422 NACH dem Klick. Wortlaut bewusst wie im
 *  Picker (`data-participant-status="not-in-sf"`), damit beide Wege dieselbe
 *  Begründung nennen.
 *
 *  Drei Zustände, drei Konsequenzen:
 *    `checking`  — Abgleich läuft, Senden gesperrt (ein hängendes Salesforce
 *                  darf sich nicht als „geprüft" ausgeben). Endet spätestens
 *                  nach `CONTACT_CHECK_TIMEOUT_MS` im `unchecked`-Zweig.
 *    `unknown`   — kein SF-Kontakt, Senden gesperrt.
 *    `unchecked` — Salesforce nicht erreichbar ODER Zeitschranke gerissen. Die
 *                  Aussage bleibt offen, Senden ERLAUBT: das ist keine Aussage
 *                  über die Adresse (serverseitig dieselbe Trennung — 503
 *                  statt 422).
 *
 *  Ansage-Regeln: `unknown` sperrt → `role="alert"` (assertiv, wird auch als
 *  frisch eingefügter Knoten angesagt). `unchecked` sperrt nicht → höfliche
 *  Region — und die muss VOR der Inhaltsänderung im DOM stehen, sonst sagen
 *  Screenreader sie oft nicht an. Ihr Container wird deshalb schon leer
 *  gerendert (ohne eigene Optik) und behält über den Übergang `checking` →
 *  `unchecked` denselben Knoten; getauscht wird nur der Inhalt. Sind ALLE drei
 *  Listen leer, verschwindet die Komponente samt Region — dann gibt es aber
 *  auch nichts anzusagen. `checking` steht schon im ersten Paint und ist
 *  damit unkritisch. */
function UnknownContactWarning({
  checking,
  unknown,
  unchecked,
}: {
  checking: string[];
  unknown: string[];
  unchecked: string[];
}) {
  const t = useT();
  if (checking.length === 0 && unknown.length === 0 && unchecked.length === 0)
    return null;
  return (
    <>
      {checking.length > 0 && (
        <div
          data-participant-contact-checking
          role="status"
          aria-live="polite"
          className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2"
        >
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
          <span>
            {t.calendar.picker.pruefenKopf}{" "}
            <span className="font-medium">{checking.join(", ")}</span>{" "}
            {checking.length === 1
              ? t.calendar.picker.pruefenWird
              : t.calendar.picker.pruefenWerden}{" "}
            {t.calendar.picker.pruefenRest(
              Math.round(CONTACT_CHECK_TIMEOUT_MS / 1000)
            )}
          </span>
        </div>
      )}
      {unknown.length > 0 && (
        <div
          data-participant-unknown-contact
          role="alert"
          className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <span className="font-medium">{unknown.join(", ")}</span>{" "}
            {unknown.length === 1
              ? t.calendar.picker.unbekanntIst
              : t.calendar.picker.unbekanntSind}{" "}
            {t.calendar.picker.unbekanntRest}
          </span>
        </div>
      )}
      {/* Zunächst LEERER Container (s. Kopfkommentar): die höfliche Live-Region
          steht damit schon im DOM, bevor der Befund eintrifft, und bleibt über
          den Übergang `checking` → `unchecked` derselbe Knoten. */}
      <div
        data-participant-contact-uncheckable-region
        role="status"
        aria-live="polite"
      >
        {unchecked.length > 0 && (
          <div
            data-participant-contact-uncheckable
            className="flex items-start gap-2 rounded-md bg-warning-soft border border-warning/40 px-3 py-2 text-xs text-warning"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {t.calendar.picker.ungeprueftKopf}{" "}
              <span className="font-medium">{unchecked.join(", ")}</span>{" "}
              {t.calendar.picker.ungeprueftRest}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

/** Schon die Pflichtteilnehmer sprengen die 20-Kalender-Grenze. Hier hilft kein
 *  Beschneiden — der Nutzer muss entscheiden, wer wirklich dabei sein MUSS. Die
 *  Zahl steht dran, damit er weiß, wie weit er kürzen muss.
 *
 *  Steht GENAU EINMAL, oben an den Chips. Bis #79 waren Teilnehmer und
 *  Zeitraum zwei einander ausschließende Schritte, und der Block stand in
 *  beiden — doppelt zu sehen war er nie. Auf der zusammengelegten Seite wäre
 *  er es, deshalb bleibt nur die Stelle, an der man das Problem auch behebt:
 *  bei den Teilnehmern. */
function RequiredCapBlock({
  requiredInternalCount,
  requiredCalendars,
  requiredOverflow,
}: {
  requiredInternalCount: number;
  requiredCalendars: number;
  requiredOverflow: number;
}) {
  const t = useT();
  if (requiredOverflow <= 0) return null;
  return (
    <div
      data-attendee-cap-block
      role="alert"
      className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        {t.calendar.wizard.capOutlookPrueft}{" "}
        <span className="font-medium">
          {t.calendar.wizard.capKalender(MAX_CALENDARS)}
        </span>{" "}
        {t.calendar.wizard.capPflichtMitte(requiredInternalCount, requiredCalendars)}{" "}
        <span className="font-medium">
          {t.calendar.wizard.capZuViel(requiredOverflow)}
        </span>
        {t.calendar.wizard.capEntferne(requiredOverflow)}{" "}
        {requiredOverflow === 1
          ? t.calendar.wizard.capIhn
          : t.calendar.wizard.capSie}{" "}
        {t.calendar.wizard.capPflichtRest}
      </span>
    </div>
  );
}

/** Die Optionalen passen nicht mehr in die 20-Kalender-Grenze und bleiben bei
 *  der Slot-Suche außen vor. Das MUSS dastehen: sonst liest der Nutzer eine
 *  Slot-Liste, die stillschweigend weniger Leute berücksichtigt, als er
 *  eingetragen hat. Die Einladung bekommen sie trotzdem. */
function OptionalCapNote({
  optionalCount,
  requiredCalendars,
}: {
  optionalCount: number;
  requiredCalendars: number;
}) {
  const t = useT();
  if (optionalCount === 0) return null;
  return (
    <div
      data-optional-cap-note
      className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
      <span>
        {t.calendar.wizard.capOutlookPrueft}{" "}
        <span className="font-medium">
          {t.calendar.wizard.capKalender(MAX_CALENDARS)}
        </span>{" "}
        {t.calendar.wizard.capOptionalMitte(requiredCalendars)}{" "}
        {optionalCount === 1
          ? t.calendar.wizard.capOptionalEiner
          : t.calendar.wizard.capOptionalMehrere(optionalCount)}{" "}
        {t.calendar.wizard.capOptionalDeshalb}{" "}
        <span className="font-medium">
          {t.calendar.wizard.capOptionalNichtGeprueft}
        </span>{" "}
        {t.calendar.wizard.capOptionalEinladung}{" "}
        {optionalCount === 1
          ? t.calendar.wizard.capOptionalBekommtEr
          : t.calendar.wizard.capOptionalBekommenSie}{" "}
        {t.calendar.wizard.capOptionalTrotzdem}
      </span>
    </div>
  );
}

/** Name für die Zusammenfassung. Extern wird ausgeschrieben — vor dem Senden
 *  muss sichtbar sein, dass die Einladung das Haus verlässt. */
function summaryName(t: Katalog, c: Colleague): string {
  const ext = isExternalParticipant(c)
    ? t.calendar.wizard.externSuffix(c.account ?? null)
    : "";
  return `${c.name}${ext}`;
}

/** Adressen → Menschen: der Nutzer denkt in Namen, nicht in UPNs. */
function optionalNames(addrs: string[], optional: Colleague[]): string[] {
  return addrs.map((addr) => {
    const hit = optional.find(
      (o) => o.email.toLowerCase() === addr.toLowerCase()
    );
    return hit ? hit.name : addr;
  });
}

/** Wie `optionalNames`, aber für den Slot-Status: die einzige geprüfte Adresse,
 *  die NICHT in der Teilnehmerliste steht, ist der Organisator selbst — der
 *  Server fragt genau ihn plus die übergebenen Teilnehmer ab
 *  (`api/sb_calendar.py::post_slot_status`). Ihn mit seiner E-Mail-Adresse
 *  anzuschreiben, während alle anderen mit Namen dastehen, wäre schlicht
 *  schlechte Auskunft. Seine Adresse kennt das Frontend nicht (sie kommt aus dem
 *  Token) — die Zugehörigkeit ist deshalb ABGELEITET, nicht verglichen. */
function slotStatusNames(
  t: Katalog,
  addrs: string[],
  people: Colleague[]
): string[] {
  return addrs.map((addr) => {
    const hit = people.find((p) => p.email.toLowerCase() === addr.toLowerCase());
    return hit ? hit.name : t.calendar.wizard.du;
  });
}

/** Markierung an EINEM Slot: können die optionalen Teilnehmer auch?
 *
 *  „Alle können" ist die einzige ZUSAGE hier, und sie hat vier Bedingungen —
 *  alle vier müssen gelten:
 *    1. `busy_optional` ist am Slot VORHANDEN. Fehlt der Schlüssel, hat der
 *       Server die Frage nie beantwortet (Stand ohne diesen Vertrag; er reicht
 *       die Slots durch, statt mit 503 die ganze Suche zu töten).
 *    2. `busy_optional` ist leer — niemand Bekanntes ist belegt.
 *    3. `optional_unknown` ist leer — von JEDEM Optionalen liegt überhaupt ein
 *       Kalender vor. Sonst gälte die Zusage für Leute, über die wir nichts
 *       wissen.
 *    4. KEIN externer Teilnehmer ist eingeladen. Für Externe gibt Graph
 *       organisationsübergreifend gar kein Free/Busy her — das ist DIESELBE
 *       Aussage wie 3. („keine Verfügbarkeit bekannt"), nur mit anderer
 *       Ursache, und sie verdient dieselbe Abschwächung. Ohne diese Bedingung
 *       stand der Kasten „Für 1 externen Teilnehmer ist keine Verfügbarkeit
 *       bekannt" drei Zeilen über einer Karte, die „Alle können" behauptete.
 *
 *  Daraus vier Lagen:
 *    `all`     — alle vier erfüllt.
 *    `busy`    — jemand Bekanntes ist belegt: benennt WEN. Der Slot bleibt
 *                wählbar, Optionale blockieren nichts.
 *    `partial` — niemand Bekanntes belegt, aber nicht von allen Eingeladenen
 *                gibt es einen Kalender. Die schwächere, wahre Aussage.
 *    `unknown` — der Server hat zu den Optionalen gar nichts gesagt.
 *
 *  Mit einem Externen im Verteiler sagt damit KEINE Karte mehr „Alle können".
 *  Das ist kein Verlust: die Karten unterscheiden sich weiterhin über die
 *  „X kann nicht"-Variante, und die Zusage wäre schlicht unwahr gewesen.
 *
 *  Ein `?? []` an einer dieser Stellen würde aus fehlenden Daten eine Zusage
 *  machen — genau der Fehler, den diese Markierung verhindert.
 *
 *  Ohne mitgeschickte optionale Adressen (`askedOptional` leer) gibt es nichts
 *  zu markieren: der Slot ist per Konstruktion für alle Pflichtteilnehmer frei.
 *  Dann bleibt die Karte wie bisher — kein Rauschen im Bestandsfluss. */
function SlotOptionalMark({
  slot,
  askedOptional,
  optionalUnknown,
  optional,
  externals,
}: {
  slot: Slot;
  askedOptional: string[];
  /** `null` = Schlüssel fehlte in der Antwort (Frage unbeantwortet). */
  optionalUnknown: string[] | null;
  optional: Colleague[];
  /** Eingeladene außerhalb des Hauses — für sie gibt es nie Free/Busy. */
  externals: Colleague[];
}) {
  const t = useT();
  if (askedOptional.length === 0) return null;
  if (!Array.isArray(slot.busy_optional)) {
    return (
      <div
        data-slot-optional="unknown"
        className="mt-1 flex items-center gap-1.5 text-[11px] text-text-3"
      >
        <HelpCircle className="w-3 h-3 shrink-0" />
        {t.calendar.wizard.optionalUnbekannt}
      </div>
    );
  }
  if (slot.busy_optional.length > 0) {
    const names = optionalNames(slot.busy_optional, optional);
    return (
      <div
        data-slot-optional="busy"
        className="mt-1 flex items-start gap-1.5 text-[11px] text-warning"
      >
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          {t.calendar.wizard.optionalKannNicht(names.join(", "), names.length)}
        </span>
      </div>
    );
  }
  // Niemand Bekanntes belegt. Ob das „alle können" heißt, hängt daran, ob von
  // allen Eingeladenen überhaupt ein Kalender vorlag — ein interner ohne
  // abrufbares Free/Busy und ein Externer sind dafür derselbe Fall.
  if (
    optionalUnknown === null ||
    optionalUnknown.length > 0 ||
    externals.length > 0
  ) {
    return (
      <div
        data-slot-optional="partial"
        className="mt-1 flex items-center gap-1.5 text-[11px] text-text-3"
      >
        <Check className="w-3 h-3 shrink-0" />
        {t.calendar.wizard.alleMitBekanntemKalender}
      </div>
    );
  }
  return (
    <div
      data-slot-optional="all"
      className="mt-1 flex items-center gap-1.5 text-[11px] text-success"
    >
      <Check className="w-3 h-3 shrink-0" />
      {t.calendar.wizard.alleKoennen}
    </div>
  );
}

/** Interne Entsprechung zu `ExternalAvailabilityNote`: auch für einen INTERNEN
 *  optionalen Teilnehmer kann Graph ohne verwertbares Free/Busy dastehen (leeres
 *  availabilityView, Fehler-Zeile — z.B. ein Postfach, das in `users` steht, es
 *  aber nicht wirklich gibt). Ihn wegzulassen hieße „kann", ihn als belegt zu
 *  führen hieße „kann nicht" — beides gelogen.
 *
 *  Bewusst derselbe Ton und dieselbe Form wie die Externen-Variante: es ist
 *  dieselbe Sorte Wahrheit („keine Verfügbarkeit bekannt"), und zwei
 *  Formsprachen für eine Aussage machen sie nur unglaubwürdig. */
function OptionalUnknownNote({
  addresses,
  optional,
}: {
  addresses: string[];
  optional: Colleague[];
}) {
  const t = useT();
  if (addresses.length === 0) return null;
  const names = optionalNames(addresses, optional);
  return (
    <div
      data-optional-unknown-note
      className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
    >
      <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
      <span>
        {t.calendar.wizard.fuer}{" "}
        {names.length === 1
          ? t.calendar.wizard.einOptionalerTeilnehmer
          : t.calendar.wizard.optionaleTeilnehmer(names.length)}{" "}
        {t.calendar.wizard.ist}{" "}
        <span className="font-medium">
          {t.calendar.wizard.keineVerfuegbarkeitBekannt}
        </span>{" "}
        {t.calendar.wizard.optionalUnbekanntRest(names.join(", "))}{" "}
        {names.length === 1
          ? t.calendar.wizard.bekommtErOderSie
          : t.calendar.wizard.bekommenSie}{" "}
        {t.calendar.wizard.trotzdem}
      </span>
    </div>
  );
}

/** Versand-Stand der Abstimmungs-Einladungen, gelesen aus der Antwort von
 *  `POST /calendar/polls`.
 *
 *  Drei Lagen, und die erste ist der Grund für dieses Bauteil:
 *    - Der Server sagt zum Versand NICHTS (Feld fehlt an allen Empfängern) →
 *      hier steht nichts. Ein „die Mails sind raus" wäre erfunden, ein „sie
 *      gehen nicht raus" ebenso — beides sind Behauptungen über einen
 *      Server-Stand, den diese Oberfläche nicht kennt.
 *    - Alle Einladungen raus → eine Zeile, die das sagt.
 *    - Mindestens eine nicht → WEN es betrifft. Die Umfrage steht trotzdem
 *      (der Server rollt sie nicht zurück), aber diese Empfänger wissen nichts
 *      davon — und nur wer das liest, kann sie anrufen. */
function PollInvitationNote({
  invitations,
}: {
  invitations?: PollInvitationOut[];
}) {
  const t = useT();
  const known = (invitations ?? []).filter(
    (t) => typeof t.invitation_sent === "boolean"
  );
  if (known.length === 0) return null;
  const failed = known.filter((t) => t.invitation_sent === false);
  if (failed.length === 0) {
    return (
      <div
        data-poll-invitations="sent"
        className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2"
      >
        <Check className="w-4 h-4 mt-0.5 shrink-0 text-success" />
        <span>
          {known.length === 1
            ? t.calendar.wizard.einladungEinerBekommen
            : t.calendar.wizard.einladungAlleBekommen(known.length)}
        </span>
      </div>
    );
  }
  return (
    <div
      data-poll-invitations="partial"
      role="alert"
      className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
      <span>
        <span className="font-medium">
          {t.calendar.wizard.einladungenFehlgeschlagen(failed.length, known.length)}
        </span>{" "}
        {t.calendar.wizard.einladungenAbstimmungSteht(
          failed.map((f) => f.email).join(", ")
        )}{" "}
        {failed.length === 1
          ? t.calendar.wizard.dieserEmpfaengerWeiss
          : t.calendar.wizard.dieseEmpfaengerWissen}{" "}
        {t.calendar.wizard.nochNichtsDavon}
      </span>
    </div>
  );
}

/** EINE wählbare Termin-Karte.
 *
 *  Herausgezogen, weil es seit W11 ZWEI Listen gibt (gefundene Vorschläge und
 *  vom Kunden genannte Zeiten) und beide dieselbe Karte zeigen müssen. Zwei
 *  Kopien desselben Markups wären genau die Stelle, an der Auswahl-Zustand,
 *  Grenz-Sperre oder Tastatur-Verhalten später auseinanderlaufen. `badge` ist
 *  der einzige Unterschied: unten steht bei den einen die Optional-Markierung,
 *  bei den anderen die interne Verfügbarkeit. */
function SlotCard({
  slot,
  chosen,
  blocked,
  badge,
  onToggle,
  onRemove,
}: {
  slot: Slot;
  chosen: boolean;
  blocked: boolean;
  badge: React.ReactNode;
  onToggle: (s: Slot) => void;
  /** Nur bei selbst eingetragenen Zeiten: was der Nutzer eingetragen hat, muss
   *  er auch wieder loswerden. Gefundene Vorschläge werden nicht entfernt —
   *  sie kommen bei der nächsten Suche ohnehin neu. */
  onRemove?: (s: Slot) => void;
}) {
  const t = useT();
  return (
    <div className="relative">
      <button
        type="button"
        data-slot-card
        data-slot-chosen={chosen ? "1" : "0"}
        aria-pressed={chosen}
        disabled={blocked}
        title={
          blocked ? t.calendar.wizard.slotGesperrt(MAX_POLL_SLOTS) : undefined
        }
        onClick={() => onToggle(slot)}
        className={clsx(
          "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
          chosen
            ? "border-accent-500 bg-accent-50/60 ring-1 ring-accent-500"
            : "border-border hover:border-accent-300",
          blocked && "opacity-50 cursor-not-allowed hover:border-border"
        )}
      >
        <div className="text-sm font-medium text-text capitalize">
          {fmtWeekdayDate(slot.start)}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-2 tabular-nums">
          <Clock className="w-3.5 h-3.5 text-text-3" />
          {fmtTime(slot.start)} – {fmtTime(slot.end)}
          {chosen && <Check className="w-3.5 h-3.5 text-accent-600 ml-auto" />}
        </div>
        {badge}
      </button>
      {onRemove && (
        <button
          type="button"
          data-custom-slot-remove
          aria-label={t.calendar.wizard.zeitEntfernenAria(
            fmtWeekdayDate(slot.start),
            fmtTime(slot.start)
          )}
          onClick={() => onRemove(slot)}
          className="absolute top-1.5 right-1.5 rounded p-1 text-text-3 hover:text-text hover:bg-surface-2"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/** W11 — eine vom Kunden genannte Zeit von Hand eintragen.
 *
 *  Bewusst dieselben Bausteine wie „Neuer Termin" (`ui/germanDate.ts`: freies
 *  TT.MM.JJJJ-Feld + Uhrzeit-Select) und NICHT `<input type="date">`. Das ist
 *  keine Stilfrage: das native Feld rendert nach der Sprache des BROWSER-
 *  PROZESSES, nicht nach der Seite — der Nutzer sähe MM/DD/YYYY, und ein
 *  Playwright-Lauf mit gesetztem `locale` (aber ohne `--lang`) wäre trotzdem
 *  grün. Der Kopfkommentar in `ui/germanDate.ts` führt das aus.
 *
 *  Die Dauer kommt aus Schritt 1 und wird hier nur angezeigt: eine Abstimmung,
 *  in der Vorschlag A 30 und Vorschlag B 60 Minuten dauert, kann der Server gar
 *  nicht abbilden (`PollSlotIn` trägt nur den Beginn, das Ende rechnet er aus
 *  der EINEN `duration_minutes`). Zwei Dauern anzubieten hieße, eine davon
 *  stillschweigend zu verwerfen. */
function CustomSlotEntry({
  duration,
  disabled,
  fullNote,
  onAdd,
}: {
  duration: number;
  disabled: boolean;
  fullNote: string | null;
  onAdd: (start: Date) => "ok" | "duplicate" | "full" | "past";
}) {
  const t = useT();
  const ref = useMemo(() => new Date(), []);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [problem, setProblem] = useState<string | null>(null);

  const day = date.trim() ? parseDeDate(date, ref) : null;
  const dateInvalid = date.trim().length > 0 && day === null;
  const timeOptions = useMemo(() => withCurrent(TIME_OPTIONS, time), [time]);

  const submit = () => {
    if (!day) {
      setProblem(t.calendar.wizard.bitteDatumEintragen);
      return;
    }
    const start = dateAtMinute(day, minutesFromHm(time));
    const verdict = onAdd(start);
    setProblem(
      verdict === "duplicate"
        ? t.calendar.wizard.zeitSchonInListe
        : verdict === "full"
          ? t.calendar.wizard.mehrAlsNZeiten(MAX_POLL_SLOTS)
          : verdict === "past"
            ? t.calendar.wizard.terminInVergangenheit
            : null
    );
    if (verdict === "ok") setDate("");
  };

  return (
    <div
      data-custom-slot-entry
      className="rounded-lg border border-border bg-surface-2 p-3 space-y-3"
    >
      <div className="flex items-start gap-2 text-xs text-text-2">
        <UserIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-3" />
        <span>
          <span className="font-medium">{t.calendar.wizard.kundenzeitenKopf}</span>{" "}
          {t.calendar.wizard.kundenzeitenRest}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <Field
          label={t.calendar.wizard.datum}
          error={dateInvalid ? t.calendar.wizard.bitteTtMmJjjj : undefined}
        >
          <Input
            data-custom-slot-date
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setProblem(null);
            }}
            onBlur={(e) => {
              const d = parseDeDate(e.target.value, ref);
              if (d) setDate(fmtDeDate(d));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={t.calendar.wizard.platzhalterDatum}
            inputMode="numeric"
            aria-label={t.calendar.wizard.ariaKundenzeitDatum}
          />
        </Field>
        <Field label={t.calendar.wizard.beginn}>
          <Select
            data-custom-slot-time
            value={time}
            onChange={(e) => setTime(e.target.value)}
            aria-label={t.calendar.wizard.ariaKundenzeitUhrzeit}
          >
            {timeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          data-custom-slot-add
          variant="secondary"
          size="sm"
          icon={<Plus className="w-4 h-4" />}
          disabled={disabled}
          title={fullNote ?? undefined}
          onClick={submit}
        >
          {t.calendar.wizard.zeitUebernehmen}
        </Button>
      </div>
      <div className="text-[11px] text-text-3">
        {t.calendar.wizard.dauerWieSchrittEins(durationLabel(duration))}
      </div>
      {(problem || fullNote) && (
        <div
          data-custom-slot-problem
          role="alert"
          className="flex items-start gap-1.5 text-[11px] text-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{problem ?? fullNote}</span>
        </div>
      )}
    </div>
  );
}

/** Interne Verfügbarkeit EINER selbst eingetragenen Zeit.
 *
 *  Der eigentliche Nutzen von W11 — „der Kunde will Dienstag 14 Uhr" wird erst
 *  brauchbar, wenn danebensteht, ob das Haus dann kann. Vier Zustände, und der
 *  erste ist der Grund für dieses Bauteil:
 *
 *    - noch keine Antwort (läuft, fehlgeschlagen, Feld fehlt) → „wird geprüft"
 *      bzw. „unbekannt". NIE „frei": das wäre eine Zusage auf Nicht-Daten,
 *      genau der Fehler, den `busy_optional` an den Vorschlägen vermeidet.
 *    - ein PFLICHT-Teilnehmer ist belegt → das ist die Ansage, die zählt.
 *    - nur Optionale belegt → geht, aber jemand fehlt.
 *    - niemand belegt → intern frei. */
function CustomSlotAvailability({
  status,
  checking,
  people,
}: {
  status: SlotStatus | undefined;
  checking: boolean;
  people: Colleague[];
}) {
  const t = useT();
  if (!status) {
    return (
      <div
        data-custom-slot-status={checking ? "checking" : "unknown"}
        className="mt-1 flex items-center gap-1.5 text-[11px] text-text-3"
      >
        {checking ? (
          <RefreshCw className="w-3 h-3 shrink-0 animate-spin" />
        ) : (
          <HelpCircle className="w-3 h-3 shrink-0" />
        )}
        {checking
          ? t.calendar.wizard.verfuegbarkeitWirdGeprueft
          : t.calendar.wizard.verfuegbarkeitUnbekannt}
      </div>
    );
  }
  // `Array.isArray` und nicht `?? []`: ein fehlendes Feld ist keine Antwort.
  if (!Array.isArray(status.busy_required) || !Array.isArray(status.busy_optional)) {
    return (
      <div
        data-custom-slot-status="unknown"
        className="mt-1 flex items-center gap-1.5 text-[11px] text-text-3"
      >
        <HelpCircle className="w-3 h-3 shrink-0" />
        {t.calendar.wizard.verfuegbarkeitUnbekannt}
      </div>
    );
  }
  if (status.busy_required.length > 0) {
    const names = slotStatusNames(t, status.busy_required, people);
    return (
      <div
        data-custom-slot-status="busy"
        className="mt-1 flex items-start gap-1.5 text-[11px] text-warning"
      >
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>{t.calendar.wizard.istBelegt(names.join(", "), names.length)}</span>
      </div>
    );
  }
  if (status.busy_optional.length > 0) {
    const names = slotStatusNames(t, status.busy_optional, people);
    return (
      <div
        data-custom-slot-status="partial"
        className="mt-1 flex items-start gap-1.5 text-[11px] text-text-3"
      >
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          {t.calendar.wizard.internFreiAber(names.join(", "), names.length)}
        </span>
      </div>
    );
  }
  const unknown = Array.isArray(status.unknown) ? status.unknown : [];
  return (
    <div
      data-custom-slot-status={unknown.length > 0 ? "partial" : "free"}
      className={clsx(
        "mt-1 flex items-center gap-1.5 text-[11px]",
        unknown.length > 0 ? "text-text-3" : "text-success"
      )}
    >
      <Check className="w-3 h-3 shrink-0" />
      {unknown.length > 0
        ? t.calendar.wizard.freiSoweitKalender
        : t.calendar.wizard.internFrei}
    </div>
  );
}

/** Ehrlicher Hinweis im Verfügbarkeits-Pfad: für Externe ist KEIN Free/Busy
 *  bekannt (Graph gibt organisationsübergreifend keins her). Die Slot-Suche
 *  rechnet deshalb ohne sie — das muss dastehen, sonst liest der Nutzer die
 *  Vorschläge als „alle können". */
function ExternalAvailabilityNote({ externals }: { externals: Colleague[] }) {
  const t = useT();
  if (externals.length === 0) return null;
  return (
    <div
      data-external-availability-note
      className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
    >
      <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
      <span>
        {t.calendar.wizard.fuer}{" "}
        {externals.length === 1
          ? t.calendar.wizard.einExternerTeilnehmer
          : t.calendar.wizard.externeTeilnehmer(externals.length)}{" "}
        {t.calendar.wizard.ist}{" "}
        <span className="font-medium">
          {t.calendar.wizard.keineVerfuegbarkeitBekannt}
        </span>{" "}
        {t.calendar.wizard.externAvailRest}{" "}
        {externals.map((e) => e.name).join(", ")}{" "}
        {t.calendar.wizard.externAvailBekommt}
      </span>
    </div>
  );
}

/** Teilnehmer eintragen wie in Outlook: tippen → kurze Trefferliste (max. 3) →
 *  Enter übernimmt den markierten Treffer → Zeile ist sofort frei für den
 *  nächsten Namen. Durchsucht Kollegen (lokal) UND externe Salesforce-Kontakte
 *  (`GET /calendar/contacts`, debounced).
 *
 *  Tastatur (Outlook-nativ, Doc 04): ↑/↓ durch die Liste, Enter übernimmt,
 *  Rücktaste im leeren Feld entfernt den letzten Chip. Entf, Leertaste, Escape
 *  und Tab bleiben unangetastet (Escape schließt weiterhin das Fenster). */
export function ParticipantPicker({
  colleagues,
  selected,
  onToggle,
  notice,
  groups,
  dense = false,
}: {
  colleagues: Colleague[];
  selected: Colleague[];
  onToggle: (c: Colleague) => void;
  /** Zusätzlicher Hinweis DIREKT unter den Chips (der Wizard hängt dort den
   *  Prefill-Abgleich ein). Optional — der Schnell-Dialog gibt nichts mit. */
  notice?: ReactNode;
  /** Ersetzt die flache Chip-Zeile durch die Gruppen-Darstellung des Aufrufers
   *  (Wizard: Pflicht/Optional inkl. Ziehen). `selected` bleibt dabei die
   *  VEREINIGUNG beider Gruppen — Trefferfilter, Rücktaste und Zähler rechnen
   *  weiter über alle Eingetragenen, egal in welcher Gruppe sie stehen.
   *
   *  Ohne diese Prop ist der Picker einbucketig wie bisher (Schnell-Dialog). */
  groups?: ReactNode;
  /** Gedrängte Fassung für den Wizard, seit Teilnehmer und Zeitraum auf EINER
   *  Seite stehen: drei Hilfszeilen fallen weg, damit der Zeitraum ohne Scrollen
   *  sichtbar bleibt (Auftrag #79). Betroffen sind GENAU
   *    • der Schalter „Alle Kollegen anzeigen" (samt Stöber-Liste),
   *    • „Mindestens 2 Zeichen — Kollegen und externe Salesforce-Kontakte.",
   *    • „Rücktaste entfernt den letzten Teilnehmer" in ALLEN drei Zweigen des
   *      Kürzel-Hinweises.
   *
   *  NUR die Anzeige: die Rücktaste entfernt weiterhin den letzten Chip
   *  (`onKeyDown`), und ↑↓/Enter bleiben beschriftet — ein Weg, den man nicht
   *  liest, existiert für den Nutzer nicht (Doc 04). Deshalb fällt der
   *  Kürzel-Hinweis nicht als Ganzes weg, sondern nur dessen Rücktaste-Teil.
   *
   *  Default `false` → „Neuer Termin" und „Termin aus dieser Mail" rendern
   *  unverändert. Der Auftrag galt dem Fenster „Termin finden"; die anderen
   *  beiden Einstiege still mitzuändern wäre eine nicht bestellte Änderung
   *  (und würde die M4/M5-Zusagen in attendee-walk.mjs brechen). */
  dense?: boolean;
}) {
  const txt = useT();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  // Die eigene Adresse kommt aus dem Auth-Kontext (`/auth/me`), NICHT als Prop.
  //
  // Verworfen: ein Pflicht-Prop durch NewEventModal, FindMeetingWizard und
  // Mail.tsx durchreichen. Das sind drei Wege zu EINER Regel, und jeder ist eine
  // Gelegenheit, sie zu vergessen — genau der „drei gefixt, einen uebersehen"-
  // Fehler. Hier liest sie GENAU EINE Stelle, und damit gilt sie an allen drei
  // Einstiegen (Neuer Termin · Termin finden · Termin aus dieser Mail).
  //
  // Die Regel selbst gab es bisher NUR im Vorbelegen (Mail.tsx::dedupeAddrList).
  // Ueber Tippen oder Stoebern liess sich die eigene Adresse weiterhin eintragen.
  const { email: selfEmail } = useAuth();
  // Geholte Kontakte sind AN DEN BEGRIFF GEBUNDEN, der sie erzeugt hat, und der
  // Render nutzt sie nur, solange `q` der aktuelle Begriff ist. Sonst läuft im
  // Debounce-/Antwort-Fenster eines NEUEN Begriffs die Liste des alten weiter
  // mit — und Enter trägt einen Externen aus einer längst verworfenen Eingabe
  // ein („sander" → „muster" → Enter). Die Prüfung sitzt bewusst am
  // VERWENDUNGSORT und nicht als „Liste leeren"-Seiteneffekt: damit ist
  // Veralten strukturell nicht darstellbar, unabhängig von Effekt-Reihenfolge,
  // Render-Timing und doppelt laufenden Effekten (StrictMode).
  const [contactRes, setContactRes] = useState<{
    q: string;
    list: Colleague[];
    state: "idle" | "loading" | "error";
  }>({ q: "", list: [], state: "idle" });
  const [browse, setBrowse] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Läufer-Nummer: eine verspätet eintreffende Antwort einer ÄLTEREN Eingabe
  // darf die Liste zur aktuellen Eingabe nicht überschreiben.
  const seq = useRef(0);
  const needle = q.trim();

  const selectedEmails = useMemo(
    () => new Set(selected.map((s) => s.email.toLowerCase())),
    [selected]
  );

  // Externe Kontakte: Mindestlänge 2 + Debounce → NICHT pro Tastendruck eine
  // SOQL. Der Server kappt zusätzlich (LIMIT) und sortiert deterministisch.
  useEffect(() => {
    if (needle.length < MIN_CONTACT_QUERY) {
      setContactRes({ q: "", list: [], state: "idle" });
      return;
    }
    const mine = ++seq.current;
    // `q` und `list` werden IMMER GEMEINSAM gesetzt — darauf beruht der
    // Frische-Vergleich unten. Beim Begriffswechsel heißt das: neuer Begriff,
    // noch keine Antwort → leere Liste. Ein `{...prev, q: needle}` würde die
    // Invariante brechen (neuer Begriff mit alter Liste) und den Guard aushebeln.
    setContactRes({ q: needle, list: [], state: "loading" });
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await api.get<ContactSuggestion[]>(
            `/calendar/contacts?q=${encodeURIComponent(needle)}`
          );
          if (seq.current !== mine) return;
          setContactRes({ q: needle, list: res.map(toParticipant), state: "idle" });
        } catch {
          if (seq.current !== mine) return;
          setContactRes({ q: needle, list: [], state: "error" });
        }
      })();
    }, CONTACT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [needle]);

  // Kontakt-Ergebnis gilt nur für GENAU diesen Begriff (s. `contactRes`). Mit
  // dem gemeinsamen Setzen von q+list oben ist „Liste eines verworfenen
  // Begriffs" damit nicht darstellbar — belegt durch die H1-Regressionsprobe in
  // frontend/attendee-walk.mjs (ohne diese Zeile: veralteter Externer in der
  // Liste und per Enter eintragbar).
  const contactsFresh = contactRes.q === needle;
  const contactState = contactsFresh ? contactRes.state : "loading";

  const hits = useMemo(() => {
    if (needle.length < MIN_CONTACT_QUERY) return [];
    const internal = withoutSelf(
      rankColleagues(colleagues, needle, MAX_HITS),
      selfEmail
    ).filter((c) => !selectedEmails.has(c.email.toLowerCase()));
    const internalEmails = new Set(colleagues.map((c) => c.email.toLowerCase()));
    const external = withoutSelf(
      contactsFresh ? contactRes.list : [],
      selfEmail
    ).filter(
      (c) =>
        !selectedEmails.has(c.email.toLowerCase()) &&
        !internalEmails.has(c.email.toLowerCase())
    );
    return mergeHits(internal, external);
  }, [colleagues, contactRes.list, contactsFresh, needle, selectedEmails, selfEmail]);

  // Stöber-Liste (M5): das ganze Verzeichnis ohne die schon Eingetragenen —
  // und ohne einen selbst.
  const browseList = useMemo(
    () =>
      withoutSelf(colleagues, selfEmail).filter(
        (c) => !selectedEmails.has(c.email.toLowerCase())
      ),
    [colleagues, selectedEmails, selfEmail]
  );

  /** Wurde die eigene Adresse gerade WEGGEFILTERT? Nur dann steht die
   *  Begruendung da.
   *
   *  Sie ist der eigentliche Auftrag: verschwindet der eigene Name lautlos aus
   *  einer Trefferliste, sucht der Mitarbeiter den Fehler bei sich (falsch
   *  getippt? nicht im Verzeichnis?). Deshalb wird geprueft, ob der Treffer OHNE
   *  die Regel dagestanden HAETTE — und nur dann erklaert. Ein Hinweis, der
   *  immer steht, erklaert nichts, er ist Deko.
   *
   *  `rankColleagues` bekommt hier dasselbe Limit wie oben: waere die eigene
   *  Adresse erst auf Platz vier gelandet, waere sie ohnehin nicht zu sehen
   *  gewesen, und es gibt nichts zu erklaeren. */
  const selfHiddenInHits = useMemo(() => {
    if (!selfEmail || needle.length < MIN_CONTACT_QUERY) return false;
    const traf =
      rankColleagues(colleagues, needle, MAX_HITS).some((c) =>
        isSameAddress(c.email, selfEmail)
      ) ||
      (contactsFresh ? contactRes.list : []).some((c) =>
        isSameAddress(c.email, selfEmail)
      );
    // Steht die eigene Adresse (aus dem Vorbelegen o.ae.) schon als Chip, ist
    // NICHT die Regel der Grund, warum sie fehlt — dann waere die Begruendung
    // falsch.
    return traf && !selectedEmails.has(selfEmail.trim().toLowerCase());
  }, [colleagues, contactRes.list, contactsFresh, needle, selfEmail, selectedEmails]);

  /** Steht man selbst im Verzeichnis? Dann fehlt man in der Stoeber-Liste, und
   *  auch das gehoert erklaert (sonst „warum bin ich nicht dabei?"). */
  const selfHiddenInBrowse = useMemo(
    () =>
      !!selfEmail &&
      colleagues.some((c) => isSameAddress(c.email, selfEmail)) &&
      !selectedEmails.has(selfEmail.trim().toLowerCase()),
    [colleagues, selfEmail, selectedEmails]
  );

  // Der oberste Treffer ist vorausgewählt (Outlook-Autocomplete) → bleibt genau
  // ein Treffer übrig, übernimmt Enter ihn ohne weiteres Zutun.
  useEffect(() => {
    setActive(0);
  }, [needle, hits.length]);
  const activeIdx = Math.min(active, Math.max(0, hits.length - 1));

  const take = (c: Colleague) => {
    // Riegel, nicht Deko: die eigene Adresse ist aus BEIDEN Listen gefiltert,
    // aber die Zusage lautet „kann nicht als Teilnehmer landen" — und die haelt
    // nur, wenn sie an der EINEN Stelle sitzt, durch die jedes Uebernehmen geht
    // (Klick auf einen Treffer, Enter, Klick in der Stoeber-Liste). Ein Filter
    // ist eine Anzeige-Entscheidung; dieser Riegel ist die Regel.
    if (isSameAddress(c.email, selfEmail ?? "")) return;
    onToggle(c);
    setQ("");
    setActive(0);
    setContactRes({ q: "", list: [], state: "idle" });
    inputRef.current?.focus();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      const hit = hits[activeIdx];
      if (hit) {
        e.preventDefault();
        take(hit);
      }
      return;
    }
    if (e.key === "Backspace" && q === "" && selected.length > 0) {
      // Outlook: Rücktaste im leeren Feld nimmt den letzten Chip weg.
      e.preventDefault();
      onToggle(selected[selected.length - 1]);
    }
  };

  // Das Eingabefeld samt Trefferliste — EIN Block, damit die Reihenfolge
  // „Feld, dann Treffer" in beiden Anordnungen unteilbar bleibt.
  const searchField = (
    <>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-3 pointer-events-none" />
        <Input
          ref={inputRef}
          data-participant-input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={txt.calendar.picker.suchePlatzhalter}
          className="pl-8"
          role="combobox"
          aria-expanded={hits.length > 0}
          aria-controls="participant-hits"
          aria-autocomplete="list"
          aria-label={txt.calendar.picker.sucheAria}
        />
      </div>

      {hits.length > 0 && (
        <div
          id="participant-hits"
          role="listbox"
          className="rounded-md border border-border divide-y divide-border overflow-hidden"
        >
          {hits.map((c, i) => {
            const external = isExternalParticipant(c);
            // Funktionspostfach: interne Adresse, aber kein Mensch. Eigene
            // Marke (Gruppen-Symbol + Badge) statt Personen-Symbol — sonst hält
            // der Nutzer `empfang@bitop.de` für eine Kollegin und duzt sie.
            const shared = !external && isFunctionMailbox(c);
            const on = i === activeIdx;
            return (
              <button
                key={c.email}
                type="button"
                role="option"
                aria-selected={on}
                data-participant-hit
                data-external={external ? "true" : "false"}
                data-participant-kind={shared ? "shared" : external ? "extern" : "person"}
                data-participant-active={on ? "true" : "false"}
                onMouseEnter={() => setActive(i)}
                onClick={() => take(c)}
                className={clsx(
                  "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors",
                  on ? "bg-accent-50/60" : "hover:bg-surface-2"
                )}
              >
                <span
                  className={clsx(
                    "w-6 h-6 rounded-full grid place-items-center shrink-0",
                    external
                      ? "bg-warning-soft text-warning"
                      : shared
                        ? "bg-surface-3 text-text-2"
                        : "bg-accent-100 text-accent-700"
                  )}
                >
                  {external ? (
                    <Building2 className="w-3.5 h-3.5" />
                  ) : shared ? (
                    <Users className="w-3.5 h-3.5" />
                  ) : (
                    <UserIcon className="w-3.5 h-3.5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-text truncate">
                    {c.name}
                    {external && (
                      <span className="ml-1.5 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-warning-soft text-warning">
                        {txt.calendar.picker.badgeExtern}
                      </span>
                    )}
                    {shared && (
                      <span className="ml-1.5 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-surface-3 text-text-2">
                        {txt.calendar.picker.badgeFunktionspostfach}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-text-3 truncate">
                    {c.email}
                    {external
                      ? [
                          c.title || null,
                          c.account || null,
                          (c.accountCount ?? 1) > 1
                            ? txt.calendar.picker.weitererAccount(
                                (c.accountCount ?? 1) - 1
                              )
                            : null,
                        ]
                          .filter(Boolean)
                          .map((s) => ` · ${s}`)
                          .join("")
                      : // Beim Funktionspostfach trägt das Badge die Klasse
                        // schon — die Zeile darunter würde sie nur wiederholen.
                        !shared && c.department
                        ? ` · ${c.department}`
                        : ""}
                  </span>
                </span>
                {/* Nicht ein Häkchen (das hieße „schon eingetragen"), sondern die
                    Taste, die diesen Treffer übernimmt. */}
                {on && (
                  <span
                    aria-hidden
                    className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[11px] leading-none text-text-3"
                  >
                    ↵
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // Die gewählten Teilnehmer (Gruppen oder flache Zeile) samt der Befunde, die
  // sich auf GENAU DIESE Chips beziehen.
  const chosenBlock = (groups || selected.length > 0) && (
    <div className="space-y-2">
      {groups ?? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <ParticipantChip key={c.email} c={c} onRemove={() => onToggle(c)} />
          ))}
        </div>
      )}
      <ExternalAttendeeWarning selected={selected} />
      {/* Slot für Befunde ZU DIESEN Chips (Prefill-Abgleich des Wizards).
          Gehört direkt an die Chips — unter Suchfeld und Statuszeile wäre
          der Hinweis mehrere Zeilen vom auslösenden Chip getrennt. */}
      {notice}
    </div>
  );

  return (
    <div className="space-y-3" data-participant-picker>
      {/* Reihenfolge: gedrängt steht das Eingabefeld OBEN, sonst wie bisher
          unter den Chips.
          Der Auftrag (#79) nennt die Wirkung: man tippt zuerst und sieht dann,
          wohin der Name gefallen ist — die Gruppen wachsen unter dem Feld nach
          unten, statt es bei jedem Namen weiter nach unten zu schieben. Genau
          dieses Wandern war der Grund, warum der Zeitraum auf einer
          zusammengelegten Seite aus dem Bild lief.
          Gruppen stehen IMMER (auch leer): sie zeigen, wo ein neu getippter Name
          landet, und die leere Gruppe muss als Drop-Ziel erreichbar bleiben. Die
          flache Bestandsliste erscheint dagegen wie bisher erst mit dem ersten
          Chip. */}
      {dense ? (
        <>
          {searchField}
          {chosenBlock}
        </>
      ) : (
        <>
          {chosenBlock}
          {searchField}
        </>
      )}

      {/* Stöbern: die Suche bleibt kurz (max. 3 Treffer), das Kollegen-
          verzeichnis bleibt aber EXPLIZIT durchblätterbar („wie schreibt sich
          die Kollegin aus dem QM?"). Bewusst keine Treffer-Liste — deshalb ein
          eigener Haken (`data-participant-browse-item`) und nur bei leerer
          Suche, damit es nicht mit den Treffern konkurriert.
          Gedrängt (#79) fällt der Schalter weg — mit ihm die aufklappbare
          Liste, die auf der zusammengelegten Seite den Zeitraum aus dem Bild
          geschoben hätte. */}
      {!dense && needle.length < MIN_CONTACT_QUERY && (
        <div className="space-y-2">
          <button
            type="button"
            data-participant-browse-toggle
            onClick={() => setBrowse((b) => !b)}
            className="text-xs text-accent-700 hover:underline"
          >
            {browse
              ? txt.calendar.picker.kollegenlisteAusblenden
              : txt.calendar.picker.alleKollegenAnzeigen}
          </button>
          {browse && (
            <div
              data-participant-browse
              className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border"
            >
              {/* Man fehlt in dieser Liste — und erfaehrt hier, warum. Ohne die
                  Zeile ist die Frage „wieso stehe ich selbst nicht drin?"
                  unbeantwortbar, und die Liste wirkt unvollstaendig. */}
              {selfHiddenInBrowse && (
                <div
                  data-participant-self-note="browse"
                  className="px-3 py-2 text-xs text-text-2 bg-surface-2"
                >
                  {txt.calendar.picker.selbstNichtInListe}
                </div>
              )}
              {browseList.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-text-3">
                  {txt.calendar.picker.keineWeiterenKollegen}
                </div>
              ) : (
                browseList.map((c) => (
                  <button
                    key={c.email}
                    type="button"
                    data-participant-browse-item
                    onClick={() => take(c)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-2"
                  >
                    <span className="w-6 h-6 rounded-full grid place-items-center shrink-0 bg-accent-100 text-accent-700">
                      <UserIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-text truncate">{c.name}</span>
                      <span className="block text-xs text-text-3 truncate">
                        {c.email}
                        {c.department ? ` · ${c.department}` : ""}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Ruhige Statuszeile: was passiert gerade — UND (immer, auch bei null
          Treffern) welche Taste was tut. Die Erkennbarkeit darf nicht genau
          dann verschwinden, wenn der Nutzer sich schwertut (Doc 04). */}
      <div className="space-y-1 text-xs text-text-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {needle.length < MIN_CONTACT_QUERY ? (
            // Gedrängt (#79) bleibt die Zeile leer: der Platzhalter im Feld
            // („Name oder E-Mail tippen …") sagt dasselbe, und auf der
            // zusammengelegten Seite zählt jede Zeile.
            dense ? null : (
              <span>{txt.calendar.picker.mindestensZeichen(MIN_CONTACT_QUERY)}</span>
            )
          ) : contactState === "loading" && hits.length === 0 ? (
            <span data-participant-status="loading">
              {txt.calendar.picker.wirdGesucht}
            </span>
          ) : hits.length === 0 ? (
            // Vollständige Adresse ohne Treffer ist ein ERGEBNIS, keine
            // Sackgasse: der Grund gehört hin, sonst erreicht die
            // Server-Begründung (422 invalid_attendees) den Nutzer nie.
            looksLikeEmailAddress(needle) ? (
              <span data-participant-status="not-in-sf" className="text-text-2">
                <span className="font-medium">{needle}</span>{" "}
                {txt.calendar.picker.nichtInSalesforce}
              </span>
            ) : (
              <span data-participant-status="empty">
                {txt.calendar.picker.keineTreffer}
              </span>
            )
          ) : (
            <span>
              <span className="tabular-nums">{hits.length}</span>{" "}
              {txt.calendar.picker.trefferVonMax(MAX_HITS)}
            </span>
          )}
          {contactState === "error" && (
            <span data-participant-status="error" className="text-warning">
              {txt.calendar.picker.externeNichtDurchsuchbar}
            </span>
          )}
        </div>
        {/* Die eigene Adresse HAETTE hier gestanden. Sie lautlos wegzulassen ist
            das eigentliche Problem: der Mitarbeiter sucht den Fehler dann bei
            sich („falsch getippt?"). Also wird gesagt, dass sie absichtlich
            fehlt — und warum. */}
        {selfHiddenInHits && (
          <div data-participant-self-note="hits" className="text-text-2">
            {txt.calendar.picker.selbstNichtEinladbar}
          </div>
        )}
        {/* Der Hinweis nennt nur, was im aktuellen Zustand WIRKLICH wirkt: ↑↓
            steuern die Trefferliste, die Stöber-Liste läuft über Tab, und ohne
            beides bleibt nur die Rücktaste.

            Gedrängt (#79) fällt der Rücktaste-Teil in ALLEN DREI Zweigen weg —
            nicht nur in dem, der gerade im Bild war. Bleibt danach nichts übrig
            (dritter Zweig), entfällt die Zeile ganz statt leer dazustehen.
            ↑↓/Enter bleiben: sie sind der einzige sichtbare Hinweis darauf, wie
            man einen Treffer übernimmt.
            Das ist eine reine ANZEIGE-Entscheidung — `onKeyDown` oben entfernt
            mit der Rücktaste weiterhin den letzten Chip. */}
        {(() => {
          const hintArrows = hits.length > 0;
          const hintTab = browse && needle.length < MIN_CONTACT_QUERY;
          if (dense && !hintArrows && !hintTab) return null;
          return (
            <div data-participant-keyhints>
              {hintArrows ? (
                <>
                  <span className="font-medium">↑↓</span>{" "}
                  {txt.calendar.picker.hinweisAuswaehlen}{" "}
                  <span className="font-medium">
                    {txt.calendar.picker.tasteEnter}
                  </span>{" "}
                  {txt.calendar.picker.hinweisUebernimmt}
                  {!dense && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="font-medium">
                        {txt.calendar.picker.tasteRuecktaste}
                      </span>{" "}
                      {txt.calendar.picker.hinweisEntferntLetzten}
                    </>
                  )}
                </>
              ) : hintTab ? (
                <>
                  <span className="font-medium">
                    {txt.calendar.picker.tasteTab}
                  </span>{" "}
                  {txt.calendar.picker.hinweisTabSpringt}{" "}
                  <span className="font-medium">
                    {txt.calendar.picker.tasteEnter}
                  </span>{" "}
                  {txt.calendar.picker.hinweisUebernimmt}
                  {!dense && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="font-medium">
                        {txt.calendar.picker.tasteRuecktaste}
                      </span>{" "}
                      {txt.calendar.picker.hinweisEntferntLetzten}
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className="font-medium">
                    {txt.calendar.picker.tasteRuecktaste}
                  </span>{" "}
                  {txt.calendar.picker.hinweisEntferntLetztenTeilnehmer}
                </>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export function FindMeetingWizard({
  colleagues,
  onClose,
  onCreated,
  initialSubject,
  initialAttendees,
  initialOptionalAttendees,
  initialWindow,
  onlineMeetingDefault,
  contextNote,
}: {
  colleagues: Colleague[];
  onClose: () => void;
  onCreated: (start: string) => void;
  // Prefill-Props (P2.16 „Termin aus dieser Mail"), alle OPTIONAL/undefined-
  // defaultet → der Bestandsaufruf aus CalendarMs bleibt unverändert.
  initialSubject?: string;
  /** Vorbelegte PFLICHT-Teilnehmer (Mail: Absender + An). */
  initialAttendees?: Colleague[];
  /** Vorbelegte OPTIONALE Teilnehmer (Mail: Cc). Sie werden eingeladen,
   *  blockieren aber keinen Slot. */
  initialOptionalAttendees?: Colleague[];
  // Erkanntes Datum-Fenster (aus parseGermanDate). Gesetzt → aktives „custom"-
  // Fenster in Schritt 1 (Presets bleiben wählbar).
  initialWindow?: { start: Date; end: Date };
  onlineMeetingDefault?: boolean;
  // Herkunfts-Hinweis (z.B. „Absender: kunde@…") — dezent, dauerhaft sichtbar.
  contextNote?: string;
}) {
  const t = useT();
  const toast = useToast();
  const [step, setStep] = useState(1);

  const [required, setRequired] = useState<Colleague[]>(initialAttendees ?? []);
  const [optional, setOptional] = useState<Colleague[]>(
    initialOptionalAttendees ?? []
  );
  // Vereinigung beider Gruppen. Alles, was NICHT zwischen Pflicht und Optional
  // unterscheidet (Picker-Trefferfilter, Extern-Warnung, Kontakt-Abgleich,
  // Event-Teilnehmer), rechnet weiter über diese eine Liste — die Gruppen sind
  // eine Einteilung, keine zweite Teilnehmer-Wahrheit.
  const selected = useMemo(() => [...required, ...optional], [required, optional]);
  // „custom" = erkanntes Fenster aus der Mail; sonst Preset-Ladder.
  const [period, setPeriod] = useState<PeriodPreset | "custom">(
    initialWindow ? "custom" : "this_week"
  );
  const [duration, setDuration] = useState<number>(30);

  const [slots, setSlots] = useState<Slot[] | null>(null);
  // Für WELCHE optionalen Adressen die aktuelle Slot-Liste eine Aussage trägt.
  // Wird in JEDEM Zweig von `loadSlots` zusammen mit `slots` gesetzt: nur so
  // kann die Markierung nicht auf einen Stand von vorhin verweisen, nachdem der
  // Nutzer die Gruppen umsortiert hat.
  const [askedOptional, setAskedOptional] = useState<string[]>([]);
  // Optionale ohne abrufbares Free/Busy — aus der Antwort, zusammen mit den
  // Slots gesetzt. `null` heißt: der Server hat den Schlüssel gar nicht
  // geliefert, die Frage ist also unbeantwortet. Bewusst NICHT auf `[]`
  // normalisiert: „hat niemanden gemeldet" und „hat nichts gesagt" sind zwei
  // verschiedene Dinge, und nur das erste erlaubt ein „alle können".
  const [optionalUnknown, setOptionalUnknown] = useState<string[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<ApiError | null>(null);
  // Gewählte Vorschläge. Eine LISTE, obwohl der Regelfall genau einer ist:
  // sind Externe dabei, stellt der Organisator mehrere Termine zur Wahl (für
  // Externe gibt Graph kein Free/Busy her — ihre Verfügbarkeit lässt sich nur
  // erfragen). Reihenfolge = Klick-Reihenfolge; der Server sortiert die Slots
  // ohnehin nach Beginn (app/routers/calendar_polls.py).
  const [chosenSlots, setChosenSlots] = useState<Slot[]>([]);
  // W11 — vom KUNDEN genannte Zeiten, die der Nutzer selbst einträgt. Sie
  // stammen NICHT aus der Suche und stehen deshalb in einer eigenen Liste: die
  // Suche wirft ihre Ergebnisse bei jeder neuen Runde weg (`setSlots`), und
  // genau das darf mit einer von Hand eingetragenen Zeit nicht passieren.
  const [customSlots, setCustomSlots] = useState<Slot[]>([]);
  // Interne Belegung je selbst eingetragenem Slot (`POST /calendar/slot-status`).
  //
  // Der Befund hängt an DREI Dingen: an der Zeit, an den Pflicht- und an den
  // optionalen Teilnehmern. Deshalb wird er zusammen mit der TEILNEHMER-SIGNATUR
  // gespeichert, für die er gilt — und beim Rendern nur benutzt, wenn die
  // Signatur noch die aktuelle ist.
  //
  // Das ist kein Feinschliff, sondern der Riegel gegen eine falsche Aussage über
  // einen Menschen: über Schritt 2 → Zurück sind die Teilnehmer
  // änderbar, während die eingetragenen Zeiten stehen bleiben. Ohne diese
  // Bindung stünde an einer Karte weiter „Intern frei", nachdem jemand
  // dazugekommen ist, der zu dieser Zeit belegt ist.
  //
  // Die Signatur ist bewusst der GESPEICHERTE Schlüssel und nicht nur ein
  // Auslöser für ein Neuladen: eine Antwort, die noch für die alte Runde
  // unterwegs war, trägt beim Eintreffen die alte Signatur und wird beim
  // Rendern verworfen. Ein Effekt allein hätte dieses Rennen offen gelassen.
  const [customStatus, setCustomStatus] = useState<{
    sig: string;
    byKey: Record<string, SlotStatus>;
  }>({ sig: "", byKey: {} });
  // Welche Karten gerade geprüft werden — als Menge, nicht als globales Flag:
  // beim Eintragen einer zweiten Zeit soll nicht auch die erste „wird geprüft"
  // sagen, obwohl über sie längst etwas bekannt ist.
  const [checkingKeys, setCheckingKeys] = useState<string[]>([]);
  // Wie viele Vorschläge die Liste zeigt. Startet bei `SLOT_VISIBLE_DEFAULT`;
  // „mehr anzeigen" hebt es auf alles Geladene an — ohne neuen Request, weil die
  // Server-Auswahl präfix-stabil ist (Begründung an `SLOT_FETCH_MAX`).
  const [visibleSlots, setVisibleSlots] = useState(SLOT_VISIBLE_DEFAULT);
  // NUR fuer die Anzeige (siehe ui/calendarBusinessHours.ts): der Request traegt
  // die Geschaeftszeiten bewusst NICHT, der Server holt sie aus dem Konto.
  const { hours: bizHours, ready: bizReady, failed: bizFailed } =
    useBusinessHours();

  const [subject, setSubject] = useState(initialSubject ?? "");
  const [description, setDescription] = useState("");
  // Teams-Meeting ist der Normalfall → default AN. `onlineMeetingDefault` bleibt
  // als expliziter Schalter erhalten (Mail-Flow setzt ihn), kann den Default aber
  // auch bewusst auf AUS ziehen.
  const [onlineMeeting, setOnlineMeeting] = useState(onlineMeetingDefault ?? true);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<ApiError | null>(null);
  const [created, setCreated] = useState<EventCreateResponse | null>(null);
  // Angelegte Abstimmung. Zweiter Endzustand neben `created` und bewusst nicht
  // derselbe: hier ist NICHTS gebucht, und der Abschluss-Text muss das sagen.
  const [pollCreated, setPollCreated] = useState<PollCreateResponse | null>(null);

  /** X am Chip: raus aus BEIDEN Gruppen. Eine Adresse steht zwar immer nur in
   *  einer — „weg ist weg" darf aber nicht davon abhängen, in welcher sie
   *  gerade liegt. */
  const removeAttendee = useCallback((c: Colleague) => {
    const key = c.email.toLowerCase();
    const without = (prev: Colleague[]) =>
      prev.filter((p) => p.email.toLowerCase() !== key);
    setRequired(without);
    setOptional(without);
  }, []);

  /** Verschieben zwischen den Gruppen (Ziehen ODER Knopf am Chip). Erst aus der
   *  Quelle raus, dann in das Ziel — nie in beiden gleichzeitig. */
  const moveAttendee = useCallback((c: Colleague, to: AttendeeRole) => {
    const key = c.email.toLowerCase();
    const without = (prev: Colleague[]) =>
      prev.filter((p) => p.email.toLowerCase() !== key);
    const withC = (prev: Colleague[]) =>
      prev.some((p) => p.email.toLowerCase() === key) ? prev : [...prev, c];
    setRequired(to === "required" ? withC : without);
    setOptional(to === "optional" ? withC : without);
  }, []);

  /** Picker-Semantik unverändert: kennt der Wizard die Adresse schon (in
   *  welcher Gruppe auch immer), nimmt der Klick sie weg. Neu Eingetragene
   *  kommen als PFLICHT dazu — wer aktiv eingetragen wird, soll können. */
  const toggle = (c: Colleague) => {
    const key = c.email.toLowerCase();
    if (selected.some((p) => p.email.toLowerCase() === key)) {
      removeAttendee(c);
      return;
    }
    setRequired((prev) => [...prev, c]);
  };

  // Externe Teilnehmer sind eingeladen, aber NICHT prüfbar: Graph liefert für
  // Adressen außerhalb der Organisation kein belastbares Free/Busy (der
  // email-service liest ein leeres/all-'0'-availabilityView als „frei"). Sie
  // gehen deshalb NICHT in die Slot-Suche — und das steht sichtbar in der UI,
  // statt eine „alle können"-Aussage auf Nicht-Daten zu bauen. Der Server
  // riegelt denselben Pfad zusätzlich ab (422 external_attendees_unsupported).
  const externals = useMemo(() => externalParticipants(selected), [selected]);

  // Nur INTERNE Adressen haben einen für uns lesbaren Kalender.
  const requiredInternal = useMemo(() => internalUpns(required), [required]);
  const optionalInternal = useMemo(() => internalUpns(optional), [optional]);
  // Der Organisator zählt mit — sein eigener Kalender ist der erste der 20.
  const requiredCalendars = 1 + requiredInternal.length;
  const requiredOverflow = Math.max(0, requiredCalendars - MAX_CALENDARS);
  const optionalsFit =
    requiredCalendars + optionalInternal.length <= MAX_CALENDARS;
  // Optionale gehen nur GESCHLOSSEN mit. Eine Teilmenge wäre schlimmer als
  // keine: `busy_optional` bliebe für die Weggelassenen leer und läse sich als
  // „die können auch" — eine Zusage auf Nicht-Daten. Lieber gar keine Aussage
  // über die Optionalen, dafür eine sichtbare.
  const optionalsInSearch = useMemo(
    () => (optionalsFit ? optionalInternal : []),
    [optionalsFit, optionalInternal]
  );
  const optionalsDropped = optionalInternal.length > 0 && !optionalsFit;
  /** WOFÜR ein Slot-Status-Befund gilt: genau die Kalender, die in die Abfrage
   *  gehen. Als STRING, damit sie sich vergleichen lässt — die beiden Listen
   *  sind bei jedem Render neue Arrays und taugen deshalb weder als
   *  Effekt-Abhängigkeit noch als Gültigkeits-Schlüssel.
   *
   *  Sortiert, weil die Reihenfolge im Verteiler nichts über die Kalender sagt:
   *  „Petra, Eva" und „Eva, Petra" sind dieselbe Frage und dürfen keine
   *  Neuprüfung auslösen. Der Organisator steht in JEDER Abfrage mit drin
   *  (Server-Seite) und ist deshalb kein Bestandteil der Signatur. */
  const participantSig = useMemo(
    () =>
      [...requiredInternal].sort().join(",") +
      "|" +
      [...optionalsInSearch].sort().join(","),
    [requiredInternal, optionalsInSearch]
  );
  // Die JEWEILS aktuelle Signatur, lesbar aus einem State-Updater heraus (der
  // sieht sonst nur den alten State, nicht die neuen Props/Memos).
  const sigRef = useRef(participantSig);
  sigRef.current = participantSig;
  // Reichen schon die Pflichtteilnehmer über die Grenze, gibt es nichts zu
  // beschneiden — dann wird gar nicht erst gesucht, sondern gesagt, wie viele
  // zu viel sind. Still Teilnehmer verschlucken ist keine Option.
  const canSearch = requiredOverflow === 0;

  // Prefill-Abgleich (P2.16): `initialAttendees` ist der EINZIGE Weg, auf dem
  // eine externe Adresse in die Auswahl kommt, ohne aus `/calendar/contacts` zu
  // stammen — „Termin aus dieser Mail" trägt den Absender direkt ein. Genau
  // diese Adressen werden beim Öffnen einmal gegen SF abgeglichen:
  //   läuft noch → `checking` (Hinweis + Senden gesperrt).
  //   Treffer    → Chip bekommt Firma/Funktion wie ein gepickter Kontakt.
  //   kein Treffer → `unknown` (sichtbarer Hinweis + Senden gesperrt).
  //   SF-Störung → `unchecked` (Hinweis, aber NICHT gesperrt).
  //
  // `checking` ist KEIN Schönheitszustand: ohne ihn ist zwischen Öffnen und
  // Antwort weder gesperrt noch etwas sichtbar. Bei einem HÄNGENDEN (nicht
  // fehlschlagenden) Salesforce umginge ein schneller Klick die Prüfung
  // komplett — die Zusage „unbekannter Kontakt wird VOR dem Senden gemeldet"
  // gälte dann nur, wenn der Request scheitert. Gesperrt wird bewusst statt nur
  // hingewiesen: der Hinweis kommt zu spät, wenn die Einladung schon raus ist,
  // und die Wartezeit trifft nur den Mail-Einstieg mit externem Absender.
  //
  // Der Startwert wird beim Mount aus DENSELBEN Daten gerechnet wie die
  // Bedingung des Effekts — sonst hinge ein Termin ohne externen Prefill
  // dauerhaft in „wird geprüft".
  //
  // Die Prüfung läuft NICHT über `selected`: was der Nutzer im Picker wählt,
  // ist bereits ein SF-Kontakt, und ein Lauf pro Auswahl-Änderung wäre eine
  // SOQL pro Klick.
  const [contactCheck, setContactCheck] = useState<{
    checking: string[];
    unknown: string[];
    unchecked: string[];
  }>(() => ({
    checking: externalParticipants([
      ...(initialAttendees ?? []),
      ...(initialOptionalAttendees ?? []),
    ]).map((p) => p.email.trim().toLowerCase()),
    unknown: [],
    unchecked: [],
  }));
  useEffect(() => {
    const pending = externalParticipants([
      ...(initialAttendees ?? []),
      ...(initialOptionalAttendees ?? []),
    ]);
    if (pending.length === 0) return;
    let alive = true;
    // ALLE laufenden Requests, damit das Cleanup jeden abbrechen kann — sonst
    // liefen sie nach dem Schließen des Dialogs noch bis zur Schranke weiter.
    const inflight: AbortController[] = [];
    void (async () => {
      // PARALLEL, nicht nacheinander: ein Verteiler bringt mehrere externe
      // Adressen mit, und die Schranke gilt PRO Adresse. Sequenziell addierten
      // sich die Wartezeiten (n × 10 s) — bei einem hängenden Salesforce wäre
      // das Senden minutenlang gesperrt, obwohl jede einzelne Anfrage nach 10 s
      // aufgibt. Die Zusagen bleiben unberührt: dieselbe Schranke, dieselben
      // drei Befunde, und `checking` fällt weiterhin erst, wenn ALLE durch sind.
      const outcomes = await Promise.all(
        pending.map(async (p) => {
          const addr = p.email.trim().toLowerCase();
          // Zeitschranke pro Adresse: ohne sie hätte „wird geprüft" KEINEN
          // Terminalzustand — ein hängendes (nicht fehlschlagendes) Salesforce
          // ließe das Senden für immer gesperrt. `fetch` bricht nie von allein ab.
          const ctrl = new AbortController();
          inflight.push(ctrl);
          const bell = window.setTimeout(
            () => ctrl.abort(),
            CONTACT_CHECK_TIMEOUT_MS
          );
          try {
            const res = await api.get<ContactSuggestion[]>(
              `/calendar/contacts?q=${encodeURIComponent(p.email.trim())}`,
              { signal: ctrl.signal }
            );
            const hit = res.find(
              (r) => (r.email || "").trim().toLowerCase() === addr
            );
            return hit
              ? { addr, found: toParticipant(hit) }
              : { addr, verdict: "unknown" as const };
          } catch {
            // 503 (SF down), 422 (Query zu kurz) ODER der eigene Abbruch nach
            // Ablauf der Schranke — nichts davon ist eine Aussage ÜBER DIE
            // ADRESSE. Deshalb derselbe Zweig: Hinweis, aber Senden frei.
            return { addr, verdict: "unchecked" as const };
          } finally {
            window.clearTimeout(bell);
          }
        })
      );
      if (!alive) return;
      const unknown = outcomes
        .filter((o) => "verdict" in o && o.verdict === "unknown")
        .map((o) => o.addr);
      const unchecked = outcomes
        .filter((o) => "verdict" in o && o.verdict === "unchecked")
        .map((o) => o.addr);
      const found = outcomes
        .map((o) => ("found" in o ? o.found : null))
        .filter((f): f is Colleague => f !== null);
      // Erst wenn ALLE Adressen durch sind — `checking: []` ist damit die
      // einzige Stelle, an der die Sperre fällt.
      setContactCheck({ checking: [], unknown, unchecked });
      if (found.length > 0) {
        // Anreicherung trifft BEIDE Gruppen: ein Cc-Kunde bekommt Firma/Funktion
        // genauso wie ein An-Kunde.
        const enrich = (prev: Colleague[]) =>
          prev.map(
            (s) =>
              found.find(
                (f) => f.email.toLowerCase() === s.email.toLowerCase()
              ) ?? s
          );
        setRequired(enrich);
        setOptional(enrich);
      }
    })();
    return () => {
      alive = false;
      for (const c of inflight) c.abort();
    };
    // Bewusst nur beim Öffnen: `initialAttendees` ist der Prefill, nicht die
    // laufende Auswahl (der Wizard wird pro Öffnen frisch gemountet).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alle drei Befunde zählen nur für Adressen, die NOCH ausgewählt sind —
  // entfernt der Nutzer den Chip, ist der Weg wieder frei (ohne zweite SOQL),
  // und eine noch laufende Prüfung sperrt dann auch nichts mehr.
  const selectedAddrs = useMemo(
    () => new Set(selected.map((s) => s.email.trim().toLowerCase())),
    [selected]
  );
  const checkingExternals = useMemo(
    () => contactCheck.checking.filter((e) => selectedAddrs.has(e)),
    [contactCheck.checking, selectedAddrs]
  );
  const blockedExternals = useMemo(
    () => contactCheck.unknown.filter((e) => selectedAddrs.has(e)),
    [contactCheck.unknown, selectedAddrs]
  );
  const uncheckedExternals = useMemo(
    () => contactCheck.unchecked.filter((e) => selectedAddrs.has(e)),
    [contactCheck.unchecked, selectedAddrs]
  );
  // Senden ist gesperrt, solange ein Befund AUSSTEHT oder NEGATIV ist. Eine
  // SF-STÖRUNG (`unchecked`) sperrt bewusst NICHT: der Server antwortet dann
  // 503 `salesforce_unavailable` (app/routers/calendar.py:155-159), nicht 422 —
  // eine CRM-Störung ist keine Aussage über die Adresse und darf keinen Termin
  // verhindern.
  const sendBlocked = blockedExternals.length > 0 || checkingExternals.length > 0;

  // ── Ein Termin buchen ODER mehrere zur Wahl stellen ──────────────────────
  //
  // Mehrfachauswahl gibt es IMMER — auch in einer rein internen Runde. Bis W11
  // hing sie an `externals.length > 0`, mit der Begründung: bei Internen kennen
  // wir die Kalender, also sei eine Abstimmung reine Reibung.
  //
  // Diese Begründung deckt den Fall NICHT ab, um den es hier geht. Wenn der
  // KUNDE Zeiten vorschlägt, ist die offene Frage nicht „wann ist frei" (das
  // steht ja schon fest), sondern „passt es den internen Teilnehmern" — und
  // „frei laut Kalender" ist keine Antwort darauf. Wer drei Stunden am Stück
  // freihat, will trotzdem gefragt werden, welche der drei Kundenzeiten ihm
  // passt. Genau diese Frage war bisher nicht stellbar.
  //
  // Der Server konnte es die ganze Zeit: `POST /calendar/polls` behandelt
  // interne und externe Teilnehmer gleich (app/routers/calendar_polls.py) —
  // gesperrt war es allein hier in der Oberfläche.
  const multiSlotAllowed = true;
  // Bleibt als eigene Größe stehen (statt `chosenSlots` überall direkt zu
  // benutzen): die Grenze zwischen „gewählt" und „geht in den Versand" ist die
  // Stelle, an der MAX_POLL_SLOTS und der Buchungs-/Abstimmungs-Fall hängen.
  const pickedSlots = chosenSlots;
  // DIE Weiche des ganzen Schritts: ab zwei Terminen wird abgestimmt, bei einem
  // gebucht. Ein einzelner Vorschlag ist kein Sonderfall, sondern der Fall
  // „ich habe mich entschieden" (z.B. telefonisch geklärt) — und der bucht wie
  // eh und je.
  const willPoll = pickedSlots.length >= 2;
  const slotCapReached = pickedSlots.length >= MAX_POLL_SLOTS;
  // Rein interne Runde MIT Abstimmung: der Kunde, der die Zeiten genannt hat,
  // steht dann nicht im Verteiler — und die Auto-Buchung lädt nur ein, wer in
  // der Runde ist (app/services/calendar_polls.py::settle_after_vote). Das ist
  // kein Fehler, aber es muss dastehen, bevor jemand einen Termin bucht, zu dem
  // der Kunde nicht eingeladen ist.
  const internalOnlyPoll = willPoll && externals.length === 0;
  // Der Server verlangt für eine Abstimmung mindestens einen PFLICHT-Teilnehmer
  // (422 `no_required_participant`, app/routers/calendar_polls.py:188): sonst
  // wäre „alle Pflicht-Teilnehmer können" über der leeren Menge wahr und die
  // erste Stimme buchte blind. Das ist im Wizard erreichbar — eine Mail, die
  // nur mich in An und einen Externen in Cc hat, füllt genau so vor. Also hier
  // sichtbar sperren statt in den 422 laufen.
  const pollNeedsRequired = willPoll && required.length === 0;

  /** Slot-Klick — seit W11 in JEDER Runde ein Umschalter. Die Grenze fängt der
   *  gesperrte Zustand der Karten ab, die Prüfung hier ist der Riegel dahinter. */
  const toggleSlot = (s: Slot) => {
    setChosenSlots((prev) => {
      const key = slotKey(s);
      if (prev.some((p) => slotKey(p) === key))
        return prev.filter((p) => slotKey(p) !== key);
      if (prev.length >= MAX_POLL_SLOTS) return prev;
      return [...prev, s];
    });
  };

  /** Was in Schritt 2 als Karte steht: die selbst eingetragenen Zeiten ZUERST
   *  (sie sind die Vorgabe des Kunden, nicht ein Vorschlag unter vielen), dann
   *  die gefundenen — jeweils chronologisch.
   *
   *  Die gefundenen kommen vom Server in AUSWAHL-Reihenfolge (beste zuerst,
   *  siehe `_select_spread` im email-service). Gekappt wird deshalb VOR dem
   *  Sortieren: die ersten `visibleSlots` sind die besten `visibleSlots`,
   *  angezeigt werden sie danach nach Uhrzeit — eine Liste, die nach „Güte"
   *  sortiert dasteht, liest sich wie ein durcheinandergeratener Kalender. */
  const byStart = (a: Slot, b: Slot) => a.start.localeCompare(b.start);
  const foundShown = useMemo(
    () => (slots ?? []).slice(0, visibleSlots).sort(byStart),
    [slots, visibleSlots]
  );
  const customShown = useMemo(
    () => [...customSlots].sort(byStart),
    [customSlots]
  );
  const hiddenCount = Math.max(0, (slots?.length ?? 0) - visibleSlots);

  /** Eine selbst eingetragene Zeit übernehmen. Duplikate (auch gegen die
   *  gefundenen Vorschläge) werden abgewiesen: zwei identische Karten wären
   *  zwei Stimmen über denselben Termin, und der Server lehnt sie ohnehin ab
   *  (422 `duplicate_slots`, app/routers/calendar_polls.py). */
  const addCustomSlot = (s: Slot): "ok" | "duplicate" | "full" => {
    const key = slotKey(s);
    if (
      customSlots.some((c) => slotKey(c) === key) ||
      (slots ?? []).some((f) => slotKey(f) === key)
    )
      return "duplicate";
    if (customSlots.length >= MAX_POLL_SLOTS) return "full";
    setCustomSlots((prev) => [...prev, s]);
    return "ok";
  };

  const removeCustomSlot = (s: Slot) => {
    const key = slotKey(s);
    setCustomSlots((prev) => prev.filter((c) => slotKey(c) !== key));
    setChosenSlots((prev) => prev.filter((c) => slotKey(c) !== key));
  };

  /** Interne Belegung der selbst eingetragenen Zeiten holen.
   *
   *  Der eigentliche Nutzen von W11: „der Kunde will Dienstag 14 Uhr" ist erst
   *  dann eine brauchbare Information, wenn danebensteht, ob das Haus dann kann.
   *  Nur INTERNE Adressen gehen mit (`internalUpns`) — für Externe gibt es kein
   *  Free/Busy, und sie als „frei" zu melden wäre erfunden.
   *
   *  Ein Fehlschlag löscht die Karten NICHT: die Zeiten bleiben stehen, nur der
   *  Befund fehlt. Er wird dann auch als fehlend angezeigt statt als „frei". */
  const checkCustomSlots = useCallback(
    async (list: Slot[]) => {
      if (list.length === 0) return;
      // Die Signatur, für die DIESE Antwort gelten wird — festgehalten VOR dem
      // Absenden. Ändert der Nutzer die Teilnehmer, während die Anfrage läuft,
      // trägt das Ergebnis beim Eintreffen die alte Signatur und wird beim
      // Rendern nicht mehr benutzt.
      const sig = participantSig;
      const keys = list.map(slotKey);
      setCheckingKeys((prev) => [...new Set([...prev, ...keys])]);
      try {
        const res = await api.post<SlotStatusResponse>("/calendar/slot-status", {
          attendee_upns: requiredInternal,
          optional_upns: optionalsInSearch,
          slots: list.map((s) => ({ start: s.start, end: s.end })),
        });
        setCustomStatus((prev) => {
          // VERSPAETETE Antwort einer vorigen Runde: verwerfen, nicht speichern.
          //
          // Sie ist nicht bloss unbrauchbar, sie waere schaedlich — sie wuerde
          // einen inzwischen geholten, RICHTIGEN Befund („belegt") wieder
          // ueberschreiben. Die Anzeige-Sperre weiter unten faengt die
          // Falschaussage zwar ab, degradierte die Karte dann aber auf
          // „unbekannt", und dort bliebe sie kleben: der Neupruef-Effekt haengt
          // an der Signatur, und die hat sich ja nicht noch einmal geaendert.
          // Also gar nicht erst hineinschreiben.
          if (sig !== sigRef.current) return prev;
          // Befunde einer ANDEREN Runde nicht weiterschleppen: sie gelten für
          // andere Kalender. Nur bei gleicher Signatur wird ergänzt.
          const next = prev.sig === sig ? { ...prev.byKey } : {};
          for (const s of res.slots ?? []) {
            // Über den ZEITPUNKT zuordnen, nicht über die Listenposition: eine
            // Antwort mit weniger Zeilen als angefragt würde sonst alle
            // folgenden Befunde um eins verschieben — und dann stünde an einer
            // Karte die Belegung einer anderen. Bei einer Aussage über die
            // Verfügbarkeit eines Menschen ist das kein Schönheitsfehler.
            //
            // Verglichen wird der geparste Zeitpunkt und nicht die Zeichenkette:
            // hin geht ISO mit Zone (`Date.toISOString()`), zurück kommt die
            // Berlin-Ortszeit ohne Zone (`_local_naive` im email-service) —
            // dieselbe Sekunde, zwei Schreibweisen. Genau so liest der Rest
            // dieser Datei die Vorschläge auch (`fmtTime` → `new Date(iso)`).
            const at = new Date(s.start).getTime();
            const hit = Number.isNaN(at)
              ? undefined
              : list.find((c) => new Date(c.start).getTime() === at);
            if (hit) next[slotKey(hit)] = s;
          }
          return { sig, byKey: next };
        });
      } catch {
        /* Kein Toast: die Zeiten stehen, der Befund fehlt — und die Karte sagt
           das selbst („Verfügbarkeit unbekannt"). */
      } finally {
        setCheckingKeys((prev) => prev.filter((k) => !keys.includes(k)));
      }
    },
    [requiredInternal, optionalsInSearch, participantSig]
  );

  /** Teilnehmer geändert → die Befunde der eingetragenen Zeiten gelten nicht
   *  mehr, also neu prüfen.
   *
   *  Die RICHTIGKEIT hängt nicht an diesem Effekt (dafür sorgt die
   *  Signatur-Bindung oben — ohne passende Signatur zeigt die Karte
   *  „unbekannt"). Er sorgt für die Bequemlichkeit: statt eines Fragezeichens
   *  steht kurz darauf wieder ein echter Befund da.
   *
   *  Abhängig von der SIGNATUR, nicht von den Listen: `requiredInternal` und
   *  `optionalsInSearch` sind bei jedem Render neue Arrays, der Effekt liefe
   *  sonst dauernd. Und bewusst NICHT von `customSlots`: das Eintragen einer
   *  Zeit prüft schon an der Eintrag-Stelle, und zwar nur die neue — hier
   *  nochmal alle zu prüfen wäre eine getSchedule-Abfrage pro Klick. */
  const customSlotsRef = useRef<Slot[]>([]);
  customSlotsRef.current = customSlots;
  const firstSigRun = useRef(true);
  useEffect(() => {
    if (firstSigRun.current) {
      // Beim Öffnen gibt es noch keine eingetragenen Zeiten — der erste Lauf
      // ist kein Wechsel und darf keine Abfrage auslösen.
      firstSigRun.current = false;
      return;
    }
    if (customSlotsRef.current.length > 0) {
      void checkCustomSlots(customSlotsRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantSig]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    setSlotsError(null);
    // Neue Suche = neue Vorschläge: eine Auswahl aus der vorigen Runde träfe
    // Termine, die es in dieser Liste gar nicht mehr gibt. Die selbst
    // eingetragenen Zeiten bleiben — die kommen vom Kunden und nicht aus der
    // Suche, eine neue Suche macht sie also nicht ungültig. Aus der AUSWAHL
    // fallen sie trotzdem, sonst wäre nach der Suche etwas gewählt, was in der
    // Liste nicht mehr markiert ist.
    setChosenSlots([]);
    setVisibleSlots(SLOT_VISIBLE_DEFAULT);
    // „custom" → erkanntes Mail-Fenster; sonst der Preset-Window-Rechner.
    const { start, end } =
      period === "custom" && initialWindow
        ? initialWindow
        : periodWindow(period as PeriodPreset);
    try {
      const res = await api.post<AvailabilityResponse>("/calendar/availability", {
        // NUR interne Adressen (internalUpns filtert über die Adress-Domain).
        // `attendee_upns` sind die PFLICHT-Teilnehmer: sie blockieren einen
        // Slot. `optional_upns` blockieren keinen — der Server meldet pro Slot
        // über `busy_optional` zurück, wer von ihnen belegt ist.
        attendee_upns: requiredInternal,
        optional_upns: optionalsInSearch,
        window_start: start.toISOString(),
        window_end: end.toISOString(),
        duration_minutes: duration,
        // Das Maximum holen, nicht die Anzeige-Menge (W10). Angezeigt werden
        // zunächst `SLOT_VISIBLE_DEFAULT`; „mehr anzeigen" klappt den Rest ohne
        // zweiten Request auf, weil die Server-Auswahl präfix-stabil ist.
        // Die Geschäftszeiten stehen bewusst NICHT im Body: die liest der
        // Server aus dem Konto des Token-Users (app/routers/calendar.py::
        // _business_hours) — sonst hinge die Zusage „kein Vorschlag vor 9 Uhr"
        // an diesem Frontend statt am Nutzer.
        max_suggestions: SLOT_FETCH_MAX,
      });
      setSlots(res.slots ?? []);
      setAskedOptional(optionalsInSearch);
      setOptionalUnknown(
        Array.isArray(res.optional_unknown) ? res.optional_unknown : null
      );
    } catch (e) {
      setSlots(null);
      setAskedOptional([]);
      setOptionalUnknown(null);
      setSlotsError(e as ApiError);
    } finally {
      setLoadingSlots(false);
    }
  }, [period, duration, requiredInternal, optionalsInSearch, initialWindow]);

  const goToSlots = () => {
    setStep(2);
    void loadSlots();
  };

  const send = async () => {
    const slot = pickedSlots[0];
    // Genau EINER — mehr wäre der Abstimmungs-Fall, und der bucht nicht.
    if (pickedSlots.length !== 1 || !slot || !subject.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await api.post<EventCreateResponse>("/calendar/events", {
        subject: subject.trim(),
        start: slot.start,
        end: slot.end,
        // EINLADUNG ≠ Verfügbarkeits-Prüfung: hier gehen ALLE Teilnehmer mit —
        // interne wie externe, Pflicht wie Optional. `optional_upns` ist die
        // TEILMENGE davon, die Outlook als optional einlädt (Graph
        // `attendees[].type = "optional"`). Die 20-Kalender-Grenze gilt hier
        // NICHT, sie betrifft nur `getSchedule`.
        attendee_upns: selected.map((s) => s.email),
        optional_upns: optional.map((s) => s.email),
        body_text: description.trim() || undefined,
        // online_meeting nur senden, wenn AN (Server-Default ist false —
        // app/schemas/calendar.py::EventRequest; reines Passthrough-Flag).
        // Der Toggle steht an JEDEM Einstieg default AN, das Feld reist also
        // im Regelfall mit; AUS = Feld fehlt wie bisher.
        ...(onlineMeeting ? { online_meeting: true } : {}),
      });
      setCreated(res);
      toast.ok(
        t.calendar.wizard.toastEinladungGesendet,
        t.calendar.wizard.toastTerminErstellt
      );
      onCreated(slot.start);
    } catch (e) {
      setConfirming(false);
      setSendError(e as ApiError);
    } finally {
      setSending(false);
    }
  };

  /** Mehrere Termine zur Wahl stellen (`POST /calendar/polls`).
   *
   *  Bewusst NICHT derselbe Aufruf wie `send()` mit einem Flag: hier entsteht
   *  KEIN Termin. Es wird nichts gebucht, es wandert nichts in einen Kalender,
   *  und `onCreated` (Kalender neu laden) hat deshalb nichts zu tun. */
  const startPoll = async () => {
    if (pickedSlots.length < 2 || !subject.trim() || pollNeedsRequired) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await api.post<PollCreateResponse>("/calendar/polls", {
        subject: subject.trim(),
        description: description.trim() || undefined,
        duration_minutes: duration,
        // Anders als im Event-Pfad IMMER mitschicken: der Server-Default ist
        // hier `true` (app/schemas/calendar_poll.py::PollCreateRequest), ein
        // weggelassenes Feld wäre also nicht „aus", sondern „an".
        online_meeting: onlineMeeting,
        // NUR der Beginn je Vorschlag — das Ende rechnet der Server aus
        // `duration_minutes` (PollSlotIn), damit Slot-Länge und Termin-Länge
        // nicht auseinanderlaufen können. Beides kommt aus derselben Suche.
        //
        // Der Beginn muss MIT Zeitzone raus (PollSlotIn::_require_aware), die
        // Slot-Suche liefert ihn aber naiv — `toOffsetIso` schließt genau diese
        // Lücke, ohne die schon behafteten Zeiten aus `CustomSlotEntry` ein
        // zweites Mal zu verschieben. Begründung: ./calendarSlotTime.ts
        slots: pollSlotsPayload(pickedSlots),
        // Dieselbe Einteilung wie im Event-Pfad: Pflicht blockiert die Buchung,
        // Optional wird gefragt, hält sie aber nicht auf. Externe stecken in
        // beiden Gruppen — sie sind der Grund für die Abstimmung.
        participants: [
          ...required.map((c) => ({
            email: c.email,
            name: c.name,
            role: "required",
          })),
          ...optional.map((c) => ({
            email: c.email,
            name: c.name,
            role: "optional",
          })),
        ],
      });
      setPollCreated(res);
      toast.ok(
        t.calendar.wizard.toastAbstimmungAngelegt,
        t.calendar.wizard.toastTermineZurWahl
      );
    } catch (e) {
      setConfirming(false);
      setSendError(e as ApiError);
    } finally {
      setSending(false);
    }
  };

  // Ein 422 ist NICHT gleich ein 422. Der Server antwortet in zwei Gestalten,
  // die von außen identisch aussehen (beide: Status 422):
  //
  //   Handler-Fehler  → {"detail": {"error": "invalid_attendees", …}}   (Objekt)
  //   Schema-Fehler   → {"detail": [{"loc": …, "msg": …, "ctx": …}]}    (Liste)
  //
  // Beide wurden hier früher zu „eine Teilnehmer-Adresse ist nicht einladbar" —
  // also wurde ein Formfehler in der eigenen Nutzlast dem Nutzer als Vorwurf
  // gegen seine Teilnehmer angezeigt. Genau das ist passiert, als die Slot-Zeit
  // ohne Zeitzone rausging (s. ./calendarSlotTime.ts): der Server wies die FORM
  // ab, das UI beschuldigte Personen.
  //
  // Deshalb wird der Code aus dem Rumpf gelesen statt aus dem Status geraten.
  // Geprüft wird auf „`detail` ist ein Objekt MIT string-`error`" und nicht bloß
  // auf das Vorkommen des Wortes: die Pydantic-Antwort trägt in
  // `detail[0].ctx.error` selbst ein `error`-Feld (ein leeres Objekt).
  const serverErrorCode = (e: ApiError | null): string | null => {
    const detail = (e?.body as { detail?: unknown } | undefined)?.detail;
    if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
    const code = (detail as { error?: unknown }).error;
    return typeof code === "string" ? code : null;
  };

  const slotsCode = serverErrorCode(slotsError);
  const sendCode = serverErrorCode(sendError);
  // NUR die echte Adress-Abfuhr des Servers darf zu einer Aussage über
  // Teilnehmer führen.
  const attendeeErr =
    slotsCode === "invalid_attendees" || sendCode === "invalid_attendees";
  // Eigener Fall, nicht „auch Teilnehmer": die Adresse ist gültig, nur hat der
  // Verfügbarkeits-Pfad für Externe kein Free/Busy (`allow_external=False`,
  // app/routers/calendar.py:149). Das ist eine andere Auskunft als „Adresse
  // ungültig" und darf nicht mit ihr verschmelzen.
  const externalErr =
    slotsCode === "external_attendees_unsupported" ||
    sendCode === "external_attendees_unsupported";
  // 422 ohne Handler-Code = Schema-Fehler: die Nutzlast passt nicht zum Vertrag.
  // Ein Fehler des Clients, nicht des Nutzers — er bekommt eine ehrliche
  // technische Meldung und verschwindet nicht stillschweigend.
  const formatErr =
    (slotsError?.status === 422 && slotsCode === null) ||
    (sendError?.status === 422 && sendCode === null);
  const serviceErr =
    slotsError?.status === 503 || sendError?.status === 503;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      icon={<CalendarClock className="w-4 h-4" />}
      title={t.calendar.wizard.titel}
      description={t.calendar.wizard.beschreibung}
    >
      <div className="space-y-5">
        <Stepper
          step={created || pollCreated ? 3 : step}
          lastLabel={
            willPoll || pollCreated ? t.calendar.wizard.schrittAbstimmung : undefined
          }
        />

        {/* Herkunfts-Hinweis (P2.16): dezent + dauerhaft, damit klar bleibt, aus
            welcher Mail der Termin stammt. Der Absender steht zusätzlich als
            Teilnehmer in der Auswahl — extern sichtbar als Warn-Chip. */}
        {contextNote && (
          <div className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2">
            <UserIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-3" />
            <span>{contextNote}</span>
          </div>
        )}

        {/* Schritt 1 — Teilnehmer UND Zeitraum (#79 zusammengelegt) */}
        {step === 1 && !created && (
          <>
            <ParticipantPicker
              colleagues={colleagues}
              selected={selected}
              onToggle={toggle}
              // Gedrängte Fassung: drei Hilfszeilen und die Stöber-Liste fallen
              // weg, und das Eingabefeld steht ÜBER den Gruppen. Sonst schöbe
              // jeder neue Chip den Zeitraum weiter aus dem Bild — und der
              // steht auf dieser Seite jetzt darunter.
              dense
              // Zwei Gruppen statt einer flachen Chip-Zeile: Pflicht und
              // Optional sind für die Slot-Suche zwei verschiedene Dinge, also
              // müssen sie auch zwei sichtbare Orte sein.
              groups={
                <AttendeeGroups
                  required={required}
                  optional={optional}
                  onMove={moveAttendee}
                  onRemove={removeAttendee}
                />
              }
              // Prefill-Befund direkt an den Chips — nicht erst in Schritt 3
              // und nicht unter Suchfeld + Statuszeile.
              notice={
                <>
                  <UnknownContactWarning
                    checking={checkingExternals}
                    unknown={blockedExternals}
                    unchecked={uncheckedExternals}
                  />
                  {/* Die 20-Kalender-Grenze gehört an die Chips, nicht erst in
                      den Zeitraum-Schritt: gewählt wird der Verteiler HIER. Wer
                      das Problem einen Schritt später erfährt, muss zurück, um
                      es zu beheben — und bei einer Rundmail mit 25 Chips ist
                      genau das der Regelfall.
                      Sprengen schon die Pflichtteilnehmer die Grenze, nennt der
                      rote Block den Ausweg bereits (»auf Optional verschieben«)
                      — der Optional-Hinweis wäre dann eine zweite Meldung über
                      dieselbe Grenze. */}
                  <RequiredCapBlock
                    requiredInternalCount={requiredInternal.length}
                    requiredCalendars={requiredCalendars}
                    requiredOverflow={requiredOverflow}
                  />
                  {optionalsDropped && canSearch && (
                    <OptionalCapNote
                      optionalCount={optionalInternal.length}
                      requiredCalendars={requiredCalendars}
                    />
                  )}
                </>
              }
            />

            {/* Trennlinie statt Schrittwechsel: „wer" und „wann ungefähr" sind
                zwei Fragen, aber eine Seite. Ohne die sichtbare Kante liefe die
                Teilnehmer-Liste optisch in die Zeitraum-Knöpfe. */}
            <div className="border-t border-border" />

            <div className="space-y-4">
              <div>
                <div className="text-xs font-medium text-text mb-1.5">
                  {t.calendar.wizard.zeitraum}
                </div>
                <div data-period-choice className="grid grid-cols-3 gap-2">
                  {/* Erkanntes Mail-Fenster (P2.16) als aktive erste Option; der
                      Nutzer kann jederzeit auf die Presets wechseln. */}
                  {initialWindow && (
                    <button
                      type="button"
                      data-on={period === "custom" ? "true" : "false"}
                      onClick={() => setPeriod("custom")}
                      className={clsx(
                        "rounded-lg border px-3 py-3 text-sm text-center transition-colors capitalize",
                        period === "custom"
                          ? "border-accent-500 bg-accent-50/60 text-text font-medium"
                          : "border-border text-text-2 hover:border-border-strong"
                      )}
                    >
                      {fmtWeekdayDate(initialWindow.start.toISOString())}
                    </button>
                  )}
                  {PERIOD_PRESETS.map((p) => {
                    const on = period === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        data-on={on ? "true" : "false"}
                        onClick={() => setPeriod(p)}
                        className={clsx(
                          "rounded-lg border px-3 py-3 text-sm text-center transition-colors",
                          on
                            ? "border-accent-500 bg-accent-50/60 text-text font-medium"
                            : "border-border text-text-2 hover:border-border-strong"
                        )}
                      >
                        {periodLabel(t, p)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-text mb-1.5">
                  {t.calendar.wizard.dauer}
                </div>
                <div
                  data-duration-choice
                  className="inline-flex rounded-md border border-border overflow-hidden"
                >
                  {DURATIONS.map((d) => {
                    const on = duration === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        data-on={on ? "true" : "false"}
                        onClick={() => setDuration(d)}
                        className={clsx(
                          "px-4 py-2 text-sm transition-colors inline-flex items-center gap-1.5",
                          on
                            ? "bg-accent-500 text-white"
                            : "bg-surface text-text-2 hover:bg-surface-3"
                        )}
                      >
                        <Clock className="w-3.5 h-3.5" />
                        {t.calendar.wizard.minuten(d)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Der Überlauf-Block steht auf dieser Seite GENAU EINMAL, oben an
                  den Chips (im `notice`-Slot des Pickers). Bis #79 waren
                  Teilnehmer und Zeitraum zwei Schritte, die einander
                  ausschlossen — derselbe Satz an beiden Stellen war deshalb nie
                  doppelt zu sehen. Auf EINER Seite wäre er es; und er gehört an
                  die Chips, weil man das Problem dort behebt. Hier bleibt nur
                  der Umfangs-Hinweis für den Fall, dass die Grenze hält. */}
              {requiredOverflow > 0 ? null : (
                <div className="rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2">
                  {requiredInternal.length === 0
                    ? t.calendar.wizard.nurDeinKalender
                    : t.calendar.wizard.pflichtteilnehmerGeprueft(
                        requiredInternal.length
                      )}
                  {optionalsInSearch.length > 0 &&
                    t.calendar.wizard.optionaleMitgelesen(optionalsInSearch.length)}
                  {/* W9 — die Geschäftszeiten stehen dort, wo sie wirken. Ohne
                      diese Zeile sieht der Nutzer nur, dass keine frühen Slots
                      kommen, und weiß nicht, dass ER das eingestellt hat. Der
                      Wert ist reine ANZEIGE: gerechnet wird mit dem Wert aus dem
                      Konto (app/routers/calendar.py::_business_hours). */}
                  {bizReady && !bizFailed && (
                    <span data-biz-hours-hint className="block mt-1 text-text-3">
                      {t.calendar.wizard.vorschlaegeZwischen(
                        hourLabel(bizHours.start_hour),
                        hourLabel(bizHours.end_hour)
                      )}
                    </span>
                  )}
                  {/* Konnte die Einstellung nicht gelesen werden, wird KEINE
                      Uhrzeit genannt. Der Server rechnet trotzdem mit dem
                      echten Wert des Kontos — eine hier hingeschriebene Zahl
                      wäre die Annahme dieses Browsers und würde bei jedem
                      Vorschlag Lügen strafen (08:00 behauptet, 09:00 geliefert). */}
                  {bizReady && bizFailed && (
                    <span
                      data-biz-hours-hint="unbekannt"
                      className="block mt-1 text-text-3"
                    >
                      {t.calendar.wizard.geschaeftszeitenNichtGeladen}
                    </span>
                  )}
                </div>
              )}

              {/* Der Optional-Cap-Hinweis steht — wie der Überlauf-Block —
                  genau einmal oben an den Chips. */}

              <ExternalAvailabilityNote externals={externals} />
            </div>

            {/* Kein „Zurück" mehr: das ist die erste Seite. */}
            <div className="flex justify-end pt-1">
              <Button
                variant="primary"
                size="sm"
                icon={<Search className="w-4 h-4" />}
                // Über der 20-Kalender-Grenze würde der Dienst gar nicht
                // antworten — dann lieber gar nicht erst suchen als eine
                // Fehlermeldung ohne Handlungsanweisung. Der Grund steht darüber.
                disabled={!canSearch}
                title={
                  canSearch
                    ? undefined
                    : t.calendar.wizard.zuVielePflichtteilnehmer(MAX_CALENDARS)
                }
                onClick={goToSlots}
              >
                {t.calendar.wizard.freieSlotsSuchen}
              </Button>
            </div>
          </>
        )}

        {/* Schritt 2 — Slots (vor #79 Schritt 3) */}
        {step === 2 && !created && (
          <>
            {/* W11 — vom KUNDEN genannte Zeiten. Steht VOR der Suche, weil es
                der Fall ist, in dem die Suche gar nicht gebraucht wird: die
                Zeiten stehen schon fest, offen ist nur, ob das Haus kann.
                Bleibt auch sichtbar, wenn die Suche nichts gefunden hat oder
                fehlgeschlagen ist — sonst wäre ausgerechnet in der Lage, in der
                man von Hand eintragen MUSS, das Feld dafür weg. */}
            <CustomSlotEntry
              duration={duration}
              disabled={customSlots.length >= MAX_POLL_SLOTS}
              fullNote={
                customSlots.length >= MAX_POLL_SLOTS
                  ? t.calendar.wizard.mehrAlsNZeitenEntfernen(MAX_POLL_SLOTS)
                  : null
              }
              onAdd={(startAt) => {
                if (startAt.getTime() <= Date.now()) return "past";
                const slot: Slot = {
                  start: startAt.toISOString(),
                  end: new Date(
                    startAt.getTime() + duration * 60_000
                  ).toISOString(),
                };
                const verdict = addCustomSlot(slot);
                // Die Verfügbarkeit erst NACH der Übernahme holen, und nur für
                // die neue Zeit: ein Lauf über alle wäre bei jedem Eintrag eine
                // weitere getSchedule-Abfrage über dieselben Kalender.
                if (verdict === "ok") void checkCustomSlots([slot]);
                return verdict;
              }}
            />

            {customShown.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-text-3">
                  {t.calendar.wizard.eingetrageneZeiten(customShown.length, duration)}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {customShown.map((s, i) => {
                    const on = pickedSlots.some((p) => slotKey(p) === slotKey(s));
                    const blocked = slotCapReached && !on;
                    return (
                      <SlotCard
                        key={`custom-${s.start}-${i}`}
                        slot={s}
                        chosen={on}
                        blocked={blocked}
                        onToggle={toggleSlot}
                        onRemove={removeCustomSlot}
                        badge={
                          <CustomSlotAvailability
                            /* NUR wenn der Befund fuer die AKTUELLE Runde gilt.
                               Passt die Signatur nicht (Teilnehmer gewechselt,
                               oder eine Antwort der vorigen Runde ist spaeter
                               eingetroffen), gibt es keinen Befund — und die
                               Karte sagt „unbekannt" statt „frei". */
                            status={
                              customStatus.sig === participantSig
                                ? customStatus.byKey[slotKey(s)]
                                : undefined
                            }
                            checking={checkingKeys.includes(slotKey(s))}
                            people={selected}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {loadingSlots && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-text-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-accent-500" />
                  {t.calendar.wizard.slotsWerdenGesucht}
                </div>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            )}

            {!loadingSlots && slotsError && (
              <div className="rounded-lg border border-border bg-surface-2 p-4 text-center space-y-3">
                <p className="text-sm text-text">
                  {attendeeErr
                    ? t.calendar.wizard.fehlerNichtEinladbar
                    : externalErr
                      ? t.calendar.wizard.fehlerNichtPruefbar
                      : serviceErr
                        ? t.calendar.wizard.fehlerDienstSuche
                        : formatErr
                          ? t.calendar.wizard.fehlerFormatSuche
                          : t.calendar.wizard.fehlerSuche}
                </p>
                <div className="flex items-center justify-center gap-2">
                  {attendeeErr || externalErr ? (
                    <Button variant="secondary" size="sm" onClick={() => setStep(1)}>
                      {t.calendar.wizard.teilnehmerPruefen}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<RefreshCw className="w-4 h-4" />}
                      onClick={() => void loadSlots()}
                    >
                      {t.common.erneutVersuchen}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!loadingSlots && !slotsError && slots && slots.length === 0 && (
              <div className="rounded-lg border border-dashed border-border px-3 py-8 text-center space-y-3">
                <p className="text-sm text-text-2">
                  {t.calendar.wizard.keineGemeinsamenSlots}
                  {/* Der Ausweg, den es seit W11 gibt: eine Zeit von Hand
                      eintragen. Ohne diesen Satz sieht der Nutzer eine
                      Sackgasse, obwohl das Feld dafür direkt darüber steht. */}
                  <span className="block mt-1 text-text-3">
                    {t.calendar.wizard.kundenzeitTrotzdem}
                  </span>
                </p>
                <Button variant="secondary" size="sm" onClick={() => setStep(1)}>
                  {t.calendar.wizard.zeitraumErweitern}
                </Button>
              </div>
            )}

            {!loadingSlots && !slotsError && slots && slots.length > 0 && (
              <div className="space-y-2">
                {/* Die Zahl muss zu den KARTEN passen, die darunter stehen.
                    Solange die Liste gekappt ist, wäre „20 freie Zeitfenster"
                    über zwölf Karten schlicht falsch — der Nutzer zählt zwölf
                    und liest zwanzig. Also beides nennen, solange beides gilt. */}
                <div data-slot-count className="text-xs text-text-3">
                  {hiddenCount > 0
                    ? t.calendar.wizard.slotZaehlerGekappt(
                        foundShown.length,
                        slots.length,
                        duration
                      )
                    : t.calendar.wizard.slotZaehler(slots.length, duration)}
                </div>
                {/* Im Moment der Slot-Wahl noch einmal: die Vorschläge kennen
                    die Kalender der Externen nicht. */}
                <ExternalAvailabilityNote externals={externals} />
                {optionalsDropped && (
                  <OptionalCapNote
                    optionalCount={optionalInternal.length}
                    requiredCalendars={requiredCalendars}
                  />
                )}
                {/* Optionale, für die es gar keinen Kalender gibt. Response-
                    Ebene, also einmal über der Liste statt an jeder Karte —
                    wer keine Daten hat, hat in JEDEM Slot keine. */}
                {askedOptional.length > 0 && optionalUnknown !== null && (
                  <OptionalUnknownNote
                    addresses={optionalUnknown}
                    optional={optional}
                  />
                )}
                {/* Der Server hat zu den mitgeschickten Optionalen NICHTS
                    gemeldet (Feld fehlt). Dann bleibt die Aussage offen — und
                    das gehört einmal ausgeschrieben über die Liste, nicht nur
                    als Kürzel an den Karten. */}
                {askedOptional.length > 0 &&
                  slots.some((s) => !Array.isArray(s.busy_optional)) && (
                    <div
                      data-slot-optional-unknown-note
                      className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2"
                    >
                      <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
                      <span>
                        {t.calendar.wizard.optionaleNichtsGemeldetKopf}{" "}
                        <span className="font-medium">
                          {t.calendar.wizard.offen}
                        </span>
                        {t.calendar.wizard.optionaleNichtsGemeldetRest}
                      </span>
                    </div>
                  )}
                {/* Mit Externen ist die Slot-Wahl eine MEHRFACH-Auswahl. Der
                    Kasten steht DIREKT über den Karten, die er meint — und er
                    sagt in einem Atemzug, wie viele gewählt sind und was der
                    Hauptknopf damit tun wird. Beides gehört hierher, nicht erst
                    in Schritt 3: wer dort erfährt, dass abgestimmt statt
                    gebucht wird, hat seine Auswahl längst getroffen.
                    Den GRUND (kein Free/Busy für Externe) nennt der Kasten
                    darüber — zweimal dieselbe Begründung macht sie nicht
                    wahrer, nur länger. */}
                {multiSlotAllowed && (
                  <div
                    data-poll-choice-hint
                    data-poll-choice-mode={willPoll ? "poll" : "book"}
                    className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2"
                  >
                    <Users className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
                    <span>
                      <span className="font-medium">
                        {t.calendar.wizard.mehrereTermineWaehlbar}
                      </span>{" "}
                      {t.calendar.wizard.hoechstensN(MAX_POLL_SLOTS)}{" "}
                      <span className="font-medium tabular-nums">
                        {t.calendar.wizard.vonN(pickedSlots.length, MAX_POLL_SLOTS)}
                      </span>{" "}
                      {t.calendar.wizard.gewaehlt}{" "}
                      {pickedSlots.length === 0 ? (
                        t.calendar.wizard.nochKeinerAusgewaehlt
                      ) : willPoll ? (
                        <span className="font-medium">
                          {t.calendar.wizard.nTermineZurAbstimmung(
                            pickedSlots.length
                          )}
                        </span>
                      ) : (
                        <span className="font-medium">
                          {t.calendar.wizard.einzelnerTerminGebucht}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {/* Die Grenze greift nicht still: sie steht da, samt Grund und
                    Ausweg, und die ungewählten Karten sind sichtbar gesperrt.
                    role=alert, weil der Zustand erst durch den letzten Klick
                    des Nutzers entstanden ist. */}
                {slotCapReached && (
                  <div
                    data-poll-cap-note
                    role="alert"
                    className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
                    <span>
                      {t.calendar.wizard.capVorschlaegeKopf(MAX_POLL_SLOTS)}{" "}
                      <span className="font-medium">
                        {t.calendar.wizard.abwaehlen}
                      </span>
                      .
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {foundShown.map((s, i) => {
                    const on = pickedSlots.some((p) => slotKey(p) === slotKey(s));
                    // Bei erreichter Grenze bleiben die GEWÄHLTEN Karten
                    // bedienbar (abwählen muss immer gehen), die übrigen nicht.
                    const blocked = slotCapReached && !on;
                    return (
                      <SlotCard
                        key={`${s.start}-${i}`}
                        slot={s}
                        chosen={on}
                        blocked={blocked}
                        onToggle={toggleSlot}
                        badge={
                          <SlotOptionalMark
                            slot={s}
                            askedOptional={askedOptional}
                            optionalUnknown={optionalUnknown}
                            optional={optional}
                            externals={externals}
                          />
                        }
                      />
                    );
                  })}
                </div>
                {/* W10 — die restlichen Vorschläge aufklappen. KEIN neuer
                    Request: es sind bereits alle geladen (`SLOT_FETCH_MAX`),
                    und die Server-Auswahl ist präfix-stabil, also sind die
                    ersten `visibleSlots` genau die besten `visibleSlots`. */}
                {hiddenCount > 0 && (
                  <div className="pt-1">
                    <Button
                      data-more-slots
                      variant="ghost"
                      size="sm"
                      icon={<Plus className="w-4 h-4" />}
                      onClick={() => setVisibleSlots(SLOT_FETCH_MAX)}
                    >
                      {/* „Noch N …" und nicht „N weitere …": im selben Fenster
                          steht der Hauptknopf »Weiter«, und zwei Bedienelemente,
                          die beide mit demselben Wortstamm anfangen, sind eine
                          Zumutung — der eine geht zum nächsten Schritt, der
                          andere klappt eine Liste auf. */}
                      {t.calendar.wizard.nochNVorschlaege(hiddenCount)}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-1">
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowLeft className="w-4 h-4" />}
                onClick={() => setStep(1)}
              >
                {t.common.zurueck}
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={<ArrowRight className="w-4 h-4" />}
                disabled={pickedSlots.length === 0}
                onClick={() => setStep(3)}
              >
                {t.calendar.wizard.weiter}
              </Button>
            </div>
          </>
        )}

        {/* Schritt 3 — Betreff/Beschreibung + Zusammenfassung + Senden
            (vor #79 Schritt 4) */}
        {step === 3 && !created && !pollCreated && pickedSlots.length > 0 && (
          <>
            <div className="space-y-4">
              <Field label={t.calendar.wizard.betreff}>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t.calendar.wizard.betreffPlatzhalter}
                  autoFocus
                />
              </Field>
              <Field label={t.calendar.wizard.beschreibungFeld} optional>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[80px]"
                  placeholder={t.calendar.wizard.beschreibungPlatzhalter}
                />
              </Field>

              {/* Online-Meeting-Toggle (P2.16 Slice B): AN → Teams-Link.
                  Default AN an jedem Einstieg (siehe useState oben). */}
              <OnlineMeetingToggle
                on={onlineMeeting}
                onToggle={() => setOnlineMeeting((v) => !v)}
              />

              <div className="rounded-lg border border-border bg-surface-2 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-text-3">
                  {t.calendar.wizard.zusammenfassung}
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Users className="w-4 h-4 mt-0.5 text-text-3 shrink-0" />
                  {/* Letzte Station vor dem Senden: WER als optional eingeladen
                      wird, muss hier stehen. Eine reine Namensliste verschwiege
                      genau den Unterschied, den der Nutzer eben eingestellt hat.
                      Deshalb zwei Zeilen statt eines angehängten „· optional":
                      in einer Komma-Liste („…, Eva Galik, Klaus Chef · optional")
                      ist nicht zu sehen, ob der Zusatz für den letzten Namen
                      gilt oder für alle — und beim Umbruch landete er sogar in
                      einer eigenen Zeile unter der ganzen Liste. */}
                  <span data-summary-attendees className="text-text">
                    {t.calendar.wizard.duOrganisator}
                    {required.length > 0
                      ? " + " + required.map((c) => summaryName(t, c)).join(", ")
                      : optional.length === 0
                        ? t.calendar.wizard.keineWeiterenTeilnehmer
                        : ""}
                  </span>
                </div>
                {optional.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    {/* Platzhalter statt zweitem Icon: die Zeile gehört zur
                        Teilnehmer-Zeile darüber und soll mit ihr fluchten. */}
                    <span className="w-4 shrink-0" aria-hidden />
                    <span data-summary-optional className="text-text-2">
                      {t.calendar.wizard.optionalListe(
                        optional.map((c) => summaryName(t, c)).join(", ")
                      )}
                    </span>
                  </div>
                )}
                {/* Letzte Station vor „Senden": Externe noch einmal explizit. */}
                <ExternalAttendeeWarning selected={selected} />
                <UnknownContactWarning
                  checking={checkingExternals}
                  unknown={blockedExternals}
                  unchecked={uncheckedExternals}
                />
                {/* ALLE gewählten Termine, einer je Zeile. Bei genau einem ist
                    das die Zeile von bisher; bei mehreren wäre eine gekürzte
                    Liste („… und 2 weitere") genau an der Stelle unehrlich, an
                    der der Nutzer prüft, was er gleich verschickt. */}
                <div className="flex items-start gap-2 text-sm">
                  <CalendarClock className="w-4 h-4 mt-0.5 text-text-3 shrink-0" />
                  <div data-summary-slots className="space-y-0.5">
                    {pickedSlots.map((s) => (
                      <div
                        key={slotKey(s)}
                        data-summary-slot
                        className="text-text capitalize"
                      >
                        {fmtWeekdayDate(s.start)},{" "}
                        <span className="tabular-nums">
                          {fmtTime(s.start)} – {fmtTime(s.end)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Der Satz, der den bisherigen Text richtigstellt: hier wird
                    nichts gebucht. Er steht IN der Zusammenfassung, direkt
                    unter den Terminen — nicht als Fußnote unter dem Knopf. */}
                {willPoll && (
                  <div
                    data-poll-summary-note
                    className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
                  >
                    <HelpCircle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
                    <span>
                      <span className="font-medium">
                        {t.calendar.wizard.nochNichtsGebucht}
                      </span>{" "}
                      {t.calendar.wizard.nochNichtsGebuchtRest(pickedSlots.length)}
                    </span>
                  </div>
                )}
                {/* Erreichbar über eine Mail, die nur mich in An und einen
                    Externen in Cc hat: dann gibt es niemanden, dessen Zusage
                    die Buchung trägt. Der Server lehnt das ab (422
                    `no_required_participant`) — das gehört VOR den Klick. */}
                {pollNeedsRequired && (
                  <div
                    data-poll-required-missing
                    role="alert"
                    className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      {t.calendar.wizard.brauchtPflichtKopf}{" "}
                      <span className="font-medium">
                        {t.calendar.wizard.pflichtTeilnehmer}
                      </span>{" "}
                      {t.calendar.wizard.brauchtPflichtRest}
                    </span>
                  </div>
                )}
                {/* W11 — rein interne Abstimmung. Genau der neue Fall: der
                    Kunde hat Zeiten genannt, gefragt werden die Kollegen. Was
                    dabei NICHT von allein passiert: der Kunde bekommt keine
                    Einladung. Die Auto-Buchung lädt ein, wer in der Runde steht
                    (app/services/calendar_polls.py::settle_after_vote) — steht
                    er nicht drin, ist der gebuchte Termin ohne ihn.

                    Bewusst ein HINWEIS und keine Sperre: es ist ein legitimer
                    Ablauf (erst intern klären, dann selbst beim Kunden
                    bestätigen). Ihn stillschweigend laufen zu lassen wäre
                    falsch, ihn zu verbieten auch. */}
                {internalOnlyPoll && (
                  <div
                    data-poll-internal-only
                    className="flex items-start gap-2 rounded-md bg-surface-2 border border-warning/40 px-3 py-2 text-xs text-text-2"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
                    <span>
                      {t.calendar.wizard.internRundeKopf}{" "}
                      <span className="font-medium">
                        {t.calendar.wizard.internRundeFett}
                      </span>{" "}
                      {t.calendar.wizard.internRundeRest}
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-2 text-sm">
                  <CalendarPlus className="w-4 h-4 mt-0.5 text-text-3 shrink-0" />
                  <span className={clsx("text-text", !subject.trim() && "text-text-3 italic")}>
                    {subject.trim() || t.calendar.wizard.betreffFehltNoch}
                  </span>
                </div>
              </div>

              {sendError && (
                <div className="flex items-start gap-2 rounded-md bg-danger-soft border border-danger/40 px-3 py-2 text-xs text-danger">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {attendeeErr
                      ? t.calendar.wizard.fehlerNichtEinladbar
                      : externalErr
                        ? t.calendar.wizard.fehlerExtern
                        : serviceErr
                          ? t.calendar.wizard.fehlerDienstSenden
                          : formatErr
                            ? t.calendar.wizard.fehlerFormatSenden(willPoll)
                            : willPoll
                              ? t.calendar.wizard.fehlerAbstimmung
                              : t.calendar.wizard.fehlerEinladung}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                icon={<ArrowLeft className="w-4 h-4" />}
                onClick={() => {
                  setConfirming(false);
                  setStep(2);
                }}
              >
                {t.common.zurueck}
              </Button>
              {confirming ? (
                <div className="flex items-center gap-2">
                  {/* Auch die Rückfrage nennt die WIRKUNG, nicht nur „bist du
                      sicher": sie ist die letzte Stelle vor dem Vollzug. */}
                  <span className="text-xs text-text-2">
                    {willPoll
                      ? t.calendar.wizard.rueckfrageAbstimmung(pickedSlots.length)
                      : t.calendar.wizard.rueckfrageSenden}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirming(false)}
                    disabled={sending}
                  >
                    {t.common.abbrechen}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={
                      willPoll ? (
                        <Users className="w-4 h-4" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )
                    }
                    loading={sending}
                    onClick={() => void (willPoll ? startPoll() : send())}
                  >
                    {willPoll
                      ? t.calendar.wizard.jaZurAbstimmung
                      : t.calendar.wizard.jaSenden}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  icon={
                    willPoll ? (
                      <Users className="w-4 h-4" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )
                  }
                  // Eine vorbelegte Adresse ohne SF-Kontakt läuft im Event-Pfad
                  // garantiert in den 422 — der Klick wird gar nicht erst
                  // angeboten, der Grund steht darüber. Ebenso, solange der
                  // Abgleich noch läuft: sonst umginge ein schneller Klick die
                  // Prüfung, wenn Salesforce nur langsam statt kaputt ist.
                  // Dieselbe Prüfung gilt für die Abstimmung: sie endet in
                  // einer echten Einladung und hängt am selben Maßstab
                  // (app/routers/calendar_polls.py, _validate_attendees).
                  disabled={!subject.trim() || sendBlocked || pollNeedsRequired}
                  title={
                    blockedExternals.length > 0
                      ? t.calendar.wizard.titelNichtEinladbar
                      : checkingExternals.length > 0
                        ? t.calendar.wizard.titelWirdGeprueft
                        : pollNeedsRequired
                          ? t.calendar.wizard.titelBrauchtPflicht
                          : undefined
                  }
                  onClick={() => setConfirming(true)}
                >
                  {/* Die Beschriftung sagt VORHER, was der Klick tut — sie ist
                      die Zusage, an der die ganze Weiche hängt. */}
                  {willPoll
                    ? t.calendar.wizard.zurAbstimmungStellen
                    : t.calendar.wizard.einladungSenden}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Abschluss der Abstimmung. Bewusst NICHT die grüne „Termin
            erstellt"-Kachel: grün + Haken hieße erledigt, und erledigt ist
            hier gar nichts — es ist eine Frage unterwegs. Der Text nennt
            deshalb beides ausdrücklich: was existiert (die Auswahl) und was
            NICHT passiert ist (Buchung, Versand). */}
        {pollCreated && (
          <div
            data-poll-created
            className="rounded-lg border border-accent-500/40 bg-accent-50/40 p-4 space-y-3"
          >
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-accent-100 grid place-items-center text-accent-700 shrink-0">
                <Users className="w-5 h-5" />
              </span>
              <div>
                <div className="text-sm font-medium text-text">
                  {t.calendar.wizard.zurAbstimmungGestellt}
                </div>
                <div className="text-xs text-text-2 mt-0.5">
                  {t.calendar.wizard.termineStehenZurWahl(
                    pollCreated.poll.slots.length
                  )}{" "}
                  <span className="font-medium">
                    {t.calendar.wizard.gebuchtIstNochNichts}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              {/* Aus der SERVER-Antwort, nicht aus der eigenen Auswahl: was
                  hier steht, ist damit das, was wirklich angelegt wurde. */}
              {pollCreated.poll.slots.map((s) => (
                <div
                  key={s.id}
                  data-poll-created-slot
                  className="flex items-center gap-2 text-sm text-text"
                >
                  <CalendarClock className="w-3.5 h-3.5 text-text-3 shrink-0" />
                  {/* Datum und Uhrzeit in EINEM Flex-Kind: als zwei Kinder
                      zöge `gap-2` eine Lücke mitten in die Zeile. */}
                  <span className="capitalize">
                    {fmtWeekdayDate(s.starts_at)},{" "}
                    <span className="tabular-nums">
                      {fmtTime(s.starts_at)} – {fmtTime(s.ends_at)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <div className="text-xs text-text-2">
              {t.calendar.wizard.laeuftBis}{" "}
              <span className="capitalize">
                {fmtWeekdayDate(pollCreated.poll.expires_at)}
              </span>
              {t.calendar.wizard.laeuftBisRest(
                fmtTime(pollCreated.poll.expires_at)
              )}
            </div>
            {/* Versand-Stand — AUS DER ANTWORT, nicht aus einer Annahme.
                „Zur Abstimmung gestellt" liest sich sonst als „die Teilnehmer
                haben es", und ob das stimmt, weiß nur der Server: er verschickt
                je Empfänger eine Mail mit DESSEN Link und meldet jeden
                Fehlschlag einzeln zurück (ein Fehlschlag rollt die Umfrage
                nicht zurück — sie steht, der Empfänger weiß nur nichts davon).
                Meldet er dazu gar nichts, sagt der Wizard dazu auch nichts. */}
            <PollInvitationNote invitations={pollCreated.tokens} />
            {/* Der Weg zum Stand — und der Satz, der sagt, dass es KEINE Mail
                geben wird. Ohne ihn wartet der Organisator auf eine
                Benachrichtigung, die es bewusst nicht gibt (gemeldet am
                05.08.2026). Der Link ist absichtlich nicht der einzige Zugang:
                er hilft nur, solange diese Karte offen ist. Dauerhaft findet
                man die Abstimmung ueber den Einstieg in der Kalender-Leiste
                und ueber /abstimmungen. */}
            <div className="text-xs text-text-2">
              {t.calendar.wizard.keineMailKopf}{" "}
              <Link
                to={`/abstimmungen/${pollCreated.poll.id}`}
                data-poll-created-status-link
                className="font-medium text-accent-600 hover:underline"
              >
                {t.calendar.wizard.abstimmungenLink}
              </Link>
              {t.calendar.wizard.keineMailRest}
            </div>
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t.common.schliessen}
              </Button>
            </div>
          </div>
        )}

        {/* Erfolg */}
        {created && (
          <div className="rounded-lg border border-success/40 bg-success-soft/40 p-4 text-center space-y-3">
            <div className="mx-auto w-11 h-11 rounded-full bg-success-soft grid place-items-center text-success">
              <Check className="w-6 h-6" />
            </div>
            <div>
              <div className="text-sm font-medium text-text">
                {t.calendar.wizard.terminErstellt}
              </div>
              <div className="text-xs text-text-2 mt-0.5">
                {t.calendar.wizard.einladungVersendet}
              </div>
            </div>
            {/* Teams-Join-Link (P2.16 Slice B) — nur bei online_meeting; der
                email-service liefert join_url durch. Primärer Anlaufpunkt für
                das Online-Meeting. */}
            {created.join_url && (
              <a
                href={created.join_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:underline"
              >
                <Video className="w-4 h-4" />
                {t.calendar.wizard.teamsMeetingOeffnen}
              </a>
            )}
            <div className="flex items-center justify-center gap-2">
              {created.web_link && (
                <a
                  href={created.web_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t.calendar.wizard.inOutlookOeffnen}
                </a>
              )}
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t.common.schliessen}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- Seite -----------------------------------------------------------------

const VIEW_TAB_IDS: ViewMode[] = ["tag", "woche", "monat", "agenda"];

function viewTabLabel(t: Katalog, id: ViewMode): string {
  if (id === "tag") return t.calendar.seite.tag;
  if (id === "woche") return t.calendar.seite.woche;
  if (id === "monat") return t.calendar.seite.monat;
  return t.calendar.seite.agenda;
}

/** Klick-Punkt eines Maus-Klicks; bei Tastatur-Auslösung (Enter/Leertaste, dann
 *  `detail === 0`) gibt es keinen Zeiger → `undefined` = Viewport-Mitte. */
function clickPoint(e: {
  detail: number;
  clientX: number;
  clientY: number;
}): { x: number; y: number } | undefined {
  return e.detail === 0 ? undefined : { x: e.clientX, y: e.clientY };
}

/** Vorschlags-Fenster für „Neuer Termin" aus der Toolbar (ohne Raster-Klick):
 *  am heutigen Anker die nächste halbe Stunde, an jedem anderen Tag 09:00.
 *  Beides 30 Minuten lang — dieselbe Standard-Dauer wie ein Raster-Klick. */
function suggestSlot(anchor: Date): { start: Date; end: Date } {
  const now = new Date();
  const min = sameDay(anchor, now)
    ? Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30
    : 9 * 60;
  return {
    start: dateAtMinute(anchor, min),
    end: dateAtMinute(anchor, min + 30),
  };
}

/** Wie viele eigene Terminabstimmungen gerade auf Antworten warten.
 *
 *  Der gemeldete Mangel (05.08.2026) war NICHT, dass es keine Statusseite gab —
 *  es gab keinen WEG dorthin. Die Kennung der frisch angelegten Abstimmung lebte
 *  nur im Wizard-Zustand; wer den Tab schloss, kam nie wieder an den Stand.
 *  Deshalb sitzt der Einstieg hier, in der Werkzeugleiste des Kalenders: dort
 *  wird die Abstimmung angelegt, und dort sucht man sie am naechsten Tag.
 *
 *  Die Zahl ist der eigentliche Grund, warum daraus ein Einstieg wird und kein
 *  Knopf, den man uebersieht: „2" heisst „da laeuft etwas, das auf Antworten
 *  wartet". Sie ist bewusst KEINE Ungelesen-Zaehlung — einen Lese-Zustand gibt
 *  es nicht, und einen zu erfinden hiesse, ihn auch pflegen zu muessen.
 *
 *  E2: der Einstieg ist in den ···-Ueberlauf gezogen. Damit die Zahl nicht mit
 *  ihm hinter einen Klick verschwindet — sie ist das Signal, nicht die Deko —
 *  traegt der ···-Knopf einen Punkt, solange etwas laeuft. Deshalb liegt der
 *  Abruf jetzt als Hook in der Seite und nicht mehr in einer eigenen
 *  Einstiegs-Komponente: Menue-Eintrag und Punkt lesen dieselbe Zahl.
 *
 *  Faellt der Aufruf aus, verschwindet die Zahl und der Weg bleibt. Ein
 *  Fehlerdialog fuer eine Navigations-Zahl waere Laerm; der Weg funktioniert
 *  auch ohne sie. */
function useLaufendeAbstimmungen(): number | null {
  const [laufend, setLaufend] = useState<number | null>(null);

  useEffect(() => {
    let lebt = true;
    void (async () => {
      try {
        const rows = await api.get<{ open_for_votes: boolean }[]>(
          "/calendar/polls"
        );
        if (lebt) setLaufend(rows.filter((r) => r.open_for_votes).length);
      } catch {
        if (lebt) setLaufend(null);
      }
    })();
    return () => {
      lebt = false;
    };
  }, []);

  return laufend;
}

export default function CalendarMsPage() {
  const t = useT();
  const toast = useToast();
  const modal = useModal();
  const [params, setParams] = useSearchParams();

  const [mode, setMode] = useState<ViewMode>("woche");
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [data, setData] = useState<CalViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editEv, setEditEv] = useState<CalEvent | null>(null);
  // Angeklickter Raster-Termin (Detail-Fenster) und aufgezogener freier Slot.
  const [detailEv, setDetailEv] = useState<CalEvent | null>(null);
  const [newSlot, setNewSlot] = useState<{
    start: Date;
    end: Date;
    at?: { x: number; y: number };
  } | null>(null);
  // Geschäftszeiten fürs Abblenden der Randzeiten im Raster. `ready`/`failed`
  // werden hier BEWUSST nicht abgefragt: solange die Einstellung noch lädt oder
  // gar nicht lesbar ist, steht `hours` auf 9-17 — und mit dieser Annahme
  // abzublenden ist richtiger, als nicht abzublenden. Das Band ist eine
  // Orientierung, keine Auskunft (es sperrt nichts, es behauptet keine Zahl im
  // Text), und wäre es an `ready` gekoppelt, verschwände es genau dann, wenn
  // /settings klemmt — also wenn der Nutzer es am wenigsten beheben kann.
  // Wer die Zeiten als TEXT nennt (Wizard, Settings), muss `failed` weiterhin
  // unterscheiden; die Begründung dazu steht in ui/calendarBusinessHours.ts.
  const { hours: businessHours } = useBusinessHours();
  // Laufende Terminabstimmungen — Zahl am Menue-Eintrag, Punkt am ···-Knopf.
  const laufendeAbstimmungen = useLaufendeAbstimmungen();

  /** `silent` = Hintergrund-Aktualisierung: KEIN Skelett, und ein Fehlschlag
   *  wirft den bereits angezeigten Kalender NICHT weg. Ein Aussetzer des
   *  Dienstes darf einen offenen Tag nicht in eine Fehlerseite verwandeln —
   *  der Nutzer hat dann nichts getan, es lief nur die Uhr. Der Fehler bleibt
   *  bis zum nächsten Erfolg still; die nächste ECHTE Aktion (Ansicht/Anker
   *  wechseln) zeigt ihn wie bisher. */
  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const { start, end } = windowFor(mode, anchor);
      try {
        const res = await api.get<CalViewResponse>(
          `/calendar/view?start=${encodeURIComponent(
            start.toISOString()
          )}&end=${encodeURIComponent(end.toISOString())}`
        );
        setData(res);
        setError(null);
      } catch (e) {
        if (!silent) {
          setError(e as ApiError);
          setData(null);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [mode, anchor]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // #58 — der Kalender aktualisiert sich selbst, aber NUR bei sichtbarem Tab.
  // Dasselbe Muster wie der Mail-Tab (frontend/src/pages/Mail.tsx: REFRESH_MS +
  // der sichtbarkeits-gesteuerte Effekt) und bewusst kein zweites: ein Termin,
  // den ein Kollege inzwischen gebucht hat, taucht sonst nie auf.
  //
  // Bei unsichtbarem Tab entsteht KEIN Egress — der Timer wird gestoppt, nicht
  // nur sein Ergebnis verworfen. Beides zusammen ist noetig: `clearInterval`
  // allein liesse einen Tick durch, der zwischen Tick und Ausblenden schon
  // unterwegs war, und die `visibilityState`-Abfrage IM Tick allein liesse den
  // Timer im Hintergrund weiterlaufen (gedrosselt, aber vorhanden).
  // Bei Rueckkehr wird sofort einmal nachgeladen, statt bis zu 2 Minuten eine
  // veraltete Ansicht zu zeigen.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === "visible") void load({ silent: true });
      }, CAL_REFRESH_MS);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void load({ silent: true });
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  // Kollegen einmalig (still) laden — Picker-Basis für den Wizard.
  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<Colleague[]>("/calendar/colleagues");
        setColleagues(res);
      } catch {
        /* Picker degradiert zu leer; Wizard zeigt „keine Kollegen". */
      }
    })();
  }, []);

  // Follow-up: Wizard per URL-Param auto-öffnen. Die Palette-Aktion „Kalender:
  // Termin finden" navigiert nach /calendar?do=find → Wizard öffnen und NUR den
  // eigenen `do`-Param wegräumen (Muster Mail.tsx `?do=`). Der Button-Flow
  // (setWizardOpen via „Termin finden") bleibt unberührt.
  useEffect(() => {
    if (params.get("do") !== "find") return;
    setWizardOpen(true);
    const next = new URLSearchParams(params);
    next.delete("do");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const events = data?.events ?? [];
  const accessDenied = error?.status === 403;
  const isToday = sameDay(startOfDay(anchor), startOfDay(new Date()));
  const isGrid = mode === "tag" || mode === "woche";

  // Das Detail-Fenster liest den LEBENDEN Stand aus `events`, nicht die
  // Klick-Momentaufnahme — sonst würde eine gesetzte Erinnerung (optimistischer
  // Merge in handleReminder) im offenen Fenster wieder auf den alten Wert
  // zurückfallen. Verschwindet der Termin (Löschen), schließt sich das Fenster.
  const detailLive = useMemo(() => {
    if (!detailEv) return null;
    if (!detailEv.graph_id) return detailEv;
    return events.find((e) => e.graph_id === detailEv.graph_id) ?? null;
  }, [detailEv, events]);

  // ── One-Click-Mutationen ───────────────────────────────────────────────
  const handleRespond = async (ev: CalEvent, response: RespondChoice) => {
    if (!ev.graph_id) return;
    try {
      await api.post(`/calendar/events/${encodeURIComponent(ev.graph_id)}/respond`, {
        response,
      });
      toast.ok(respondLabel(t, response), ev.subject || undefined);
    } catch (e) {
      const s = (e as ApiError)?.status;
      toast.err(
        t.calendar.termin.antwortFehlgeschlagen,
        s === 503 ? t.calendar.termin.dienstNichtErreichbarKurz : undefined
      );
    }
  };

  const handleDelete = async (ev: CalEvent) => {
    if (!ev.graph_id) return;
    const okConfirm = await modal.confirm({
      title: t.calendar.termin.loeschenTitel,
      description: t.calendar.termin.loeschenFrage(
        ev.subject || t.calendar.termin.keinBetreff
      ),
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!okConfirm) return;
    const gid = ev.graph_id;
    // optimistisch entfernen
    setData((prev) =>
      prev ? { ...prev, events: prev.events.filter((e) => e.graph_id !== gid) } : prev
    );
    try {
      await api.delete(`/calendar/events/${encodeURIComponent(gid)}`);
      toast.ok(t.calendar.termin.geloescht);
    } catch {
      // Fehler → STILL zurückrollen (Event wieder einfügen; Render sortiert neu).
      setData((prev) => (prev ? { ...prev, events: [...prev.events, ev] } : prev));
    }
  };

  const handleEditSaved = (gid: string, patch: EditEventPatch) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            events: prev.events.map((e) =>
              e.graph_id === gid
                ? {
                    ...e,
                    subject: patch.subject,
                    start: patch.start,
                    end: patch.end,
                    // Reminder nur mergen, wenn der PATCH sie enthielt (Dirty),
                    // damit das Row-Dropdown den neuen Wert zeigt. Sonst den
                    // Ist-Zustand unangetastet lassen (Platzhalter „–" bleibt).
                    ...(patch.is_reminder_on !== undefined
                      ? {
                          is_reminder_on: patch.is_reminder_on,
                          reminder_minutes_before_start: patch.is_reminder_on
                            ? patch.reminder_minutes_before_start ?? null
                            : null,
                        }
                      : {}),
                  }
                : e
            ),
          }
        : prev
    );
  };

  // Reminder setzen (P2.17) — reused den P1.9-Event-PATCH. Optimistisch mergen
  // (Muster handleEditSaved), im Fehlerfall STILL zurückrollen (Muster
  // handleDelete): kein Toast, kein Dialog, genau EIN PATCH pro Wahl.
  const handleReminder = async (ev: CalEvent, minutes: number | null) => {
    const gid = ev.graph_id;
    if (!gid) return;
    const prevOn = ev.is_reminder_on;
    const prevMin = ev.reminder_minutes_before_start;
    const patch =
      minutes === null
        ? { is_reminder_on: false }
        : { is_reminder_on: true, reminder_minutes_before_start: minutes };
    const applyReminder = (
      on: boolean | null | undefined,
      min: number | null | undefined
    ) =>
      setData((prev) =>
        prev
          ? {
              ...prev,
              events: prev.events.map((e) =>
                e.graph_id === gid
                  ? { ...e, is_reminder_on: on, reminder_minutes_before_start: min }
                  : e
              ),
            }
          : prev
      );
    // optimistisch: „Keine" → false/null, sonst true/N.
    applyReminder(minutes !== null, minutes);
    try {
      await api.patch(`/calendar/events/${encodeURIComponent(gid)}`, patch);
    } catch {
      applyReminder(prevOn, prevMin);
    }
  };

  const actions: EventActions = {
    onRespond: handleRespond,
    onEdit: setEditEv,
    onDelete: handleDelete,
    onReminder: handleReminder,
  };

  const pickDay = (d: Date) => {
    setAnchor(startOfDay(d));
    setMode("tag");
  };

  const renderView = () => {
    if (mode === "tag")
      return (
        <DayView
          anchor={anchor}
          events={events}
          loading={loading}
          onOpenEvent={setDetailEv}
          businessHours={businessHours}
          onCreateSlot={(start, end, at) => setNewSlot({ start, end, at })}
        />
      );
    if (mode === "woche")
      return (
        <WeekView
          anchor={anchor}
          events={events}
          loading={loading}
          onOpenEvent={setDetailEv}
          businessHours={businessHours}
          onCreateSlot={(start, end, at) => setNewSlot({ start, end, at })}
          onPickDay={pickDay}
        />
      );
    if (mode === "monat")
      return (
        <MonthView
          anchor={anchor}
          events={events}
          loading={loading}
          onPickDay={pickDay}
        />
      );
    return (
      <AgendaView anchor={anchor} events={events} loading={loading} actions={actions} />
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* E2: `pt-6 space-y-3` → `pt-5 space-y-2`. Die Leiste ist von 84 auf
          48 px geschrumpft; der Kopfblock darf danach weniger Luft nehmen, sonst
          zahlt der Kalender die Ersparnis in Rand zurueck. Die 8 px sind der
          Unterschied zwischen „36 px hoeher" und der Zielmarke „>= 40 px
          hoeher" aus der Freigabe — gemessen, nicht geschaetzt
          (ux-e2-mess-walk.mjs, M3). */}
      <div className="shrink-0 px-6 pt-5 pb-3 bg-canvas space-y-2">
        {/* Titel trägt bewusst KEINE Aktionen mehr — jede Steuerung des
            Kalenders sitzt in der EINEN Leiste darunter (vorher: Aktualisieren/
            Termin finden oben rechts, Heute/Pfeile links, Ansicht rechts = drei
            Gruppen auf zwei Zeilen). */}
        <PageHeader title={t.calendar.seitenTitel} />

        <div
          data-cal-toolbar
          className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2"
        >
          {/* Navigation */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAnchor(startOfDay(new Date()))}
            disabled={isToday}
          >
            {t.calendar.heute}
          </Button>
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              aria-label={t.common.zurueck}
              title={t.common.zurueck}
              onClick={() => setAnchor((a) => shiftAnchor(mode, a, -1))}
              className="grid place-items-center w-8 h-7 text-text-2 hover:bg-surface-3 border-r border-border"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label={t.calendar.seite.vor}
              title={t.calendar.seite.vor}
              onClick={() => setAnchor((a) => shiftAnchor(mode, a, 1))}
              className="grid place-items-center w-8 h-7 text-text-2 hover:bg-surface-3"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <span className="text-sm font-medium text-text capitalize truncate">
            {rangeLabel(mode, anchor)}
          </span>

          {/* Aktionen — rechts in derselben Leiste.
              E2 (19.08.2026): die Leiste trug eine Reihe aus elf gleichrangigen
              Bedienelementen (Heute · ‹ · › · 4 Ansichts-Tabs · Aktualisieren ·
              Neuer Termin · Termin finden · Abstimmungen) und brach bei 1280 px
              auf ZWEI Zeilen um (84 px hoch) — das Raster begann erst bei
              y = 165. Gemessen im Audit, docs/evidence/ux-mail-kalender-audit.
              Es faellt NICHTS weg: die vier Ansichten ziehen in ein „Ansicht"-
              Menue (mit der aktiven Stufe als Haken UND am Wrapper ablesbar),
              die drei zweitrangigen Aktionen in den ···-Ueberlauf. Sichtbar
              bleiben die fuenf Dinge, die man im Kalender staendig braucht:
              Heute · ‹› · Zeitraum · Ansicht · Neuer Termin. */}
          <div className="ml-auto flex items-center gap-2">
            <KopfMenu
              // Traegt die AKTIVE Ansicht als Wert: „welche Ansicht gilt
              // gerade?" bleibt ablesbar, ohne das Menue zu oeffnen.
              wrapperProps={{ "data-cal-ansicht": mode }}
              triggerProps={{
                "data-cal-ansicht-menu": true,
                title: t.calendar.seite.ansichtAria,
                className:
                  "inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text",
              }}
              trigger={
                <>
                  <CalendarRange className="w-4 h-4 shrink-0" />
                  <span>{viewTabLabel(t, mode)}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </>
              }
              panelClass="min-w-[11rem]"
            >
              {(close) => (
                <div role="group" aria-label={t.calendar.seite.ansichtAria}>
                  {VIEW_TAB_IDS.map((id) => {
                    const on = mode === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="menuitemradio"
                        data-cal-view={id}
                        aria-checked={on}
                        onClick={() => {
                          close();
                          setMode(id);
                        }}
                        className={clsx(
                          "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors hover:bg-surface-2",
                          on ? "font-medium text-text" : "text-text-2"
                        )}
                      >
                        <Check
                          className={clsx(
                            "w-3.5 h-3.5 shrink-0",
                            on ? "text-accent-500" : "text-transparent"
                          )}
                        />
                        {viewTabLabel(t, id)}
                      </button>
                    );
                  })}
                </div>
              )}
            </KopfMenu>

            {/* ···-Ueberlauf: Aktualisieren · Termin finden · Abstimmungen.
                Der Punkt am Knopf steht fuer laufende Abstimmungen — die Zahl
                zieht mit um, das Signal bleibt sichtbar (s.
                `useLaufendeAbstimmungen`).
                `min-w-[12rem]` statt der 15rem des Posteingangs: bei 390 px
                bricht die Leiste um, der ···-Knopf steht dann NICHT am rechten
                Rand — und ein rechtsbuendiges 15rem-Panel lief links aus dem
                Bild (gemessen, ux-e2-mess-walk „R"). Die drei Eintraege
                brauchen die Breite nicht. */}
            <KopfMenu
              panelClass="min-w-[12rem]"
              wrapperProps={{ "data-cal-mehr": true }}
              triggerProps={{
                "data-cal-mehr-trigger": true,
                title: t.calendar.seite.weitereBefehle,
                "aria-label": t.calendar.seite.weitereBefehle,
                className:
                  "relative inline-flex items-center justify-center rounded-lg border border-border bg-surface px-2 py-1.5 text-text-2 transition-colors hover:bg-surface-2 hover:text-text",
              }}
              trigger={
                <>
                  <MoreHorizontal className="w-4 h-4" />
                  {laufendeAbstimmungen !== null && laufendeAbstimmungen > 0 && (
                    <span
                      data-cal-mehr-signal
                      aria-hidden
                      className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-accent-500"
                    />
                  )}
                </>
              }
            >
              {(close) => (
                <>
                  <KopfMenuItem
                    data-cal-refresh
                    icon={
                      <RefreshCw
                        className={clsx("w-3.5 h-3.5", loading && "animate-spin")}
                      />
                    }
                    label={t.common.aktualisieren}
                    disabled={loading}
                    onClick={() => {
                      close();
                      void load();
                    }}
                  />
                  <KopfMenuItem
                    data-cal-find
                    icon={<Search className="w-3.5 h-3.5" />}
                    label={t.calendar.seite.terminFinden}
                    onClick={() => {
                      close();
                      setWizardOpen(true);
                    }}
                  />
                  <div className="my-1 border-t border-border" />
                  <KopfMenuItem
                    to="/abstimmungen"
                    data-cal-polls-entry
                    data-cal-polls-open={laufendeAbstimmungen ?? ""}
                    icon={<ListChecks className="w-3.5 h-3.5" />}
                    label={t.calendar.seite.abstimmungen}
                    badge={
                      laufendeAbstimmungen !== null &&
                      laufendeAbstimmungen > 0 && (
                        <span className="inline-grid min-w-[1.25rem] shrink-0 place-items-center rounded-full bg-accent-100 px-1 text-[0.7rem] font-semibold tabular-nums text-accent-700">
                          {laufendeAbstimmungen}
                        </span>
                      )
                    }
                    onClick={close}
                  />
                </>
              )}
            </KopfMenu>

            <Button
              variant="primary"
              size="sm"
              icon={<CalendarPlus className="w-4 h-4" />}
              onClick={(e) =>
                setNewSlot({ ...suggestSlot(anchor), at: clickPoint(e) })
              }
            >
              {t.calendar.seite.neuerTermin}
            </Button>
          </div>
        </div>
      </div>

      {/* Raster-Ansichten füllen die Restfläche (eigener Scroll im Raster, damit
          Wochentags-Kopf + Ganztägig-Band stehen bleiben). Monat/Agenda bleiben
          Dokument-Scroll; Agenda behält eine Lesebreite. */}
      <div
        className={clsx(
          "flex-1 min-h-0 px-6",
          isGrid ? "overflow-hidden pb-6" : "overflow-y-auto pb-8"
        )}
      >
        {error && !accessDenied ? (
          <EmptyState
            icon={<ServerCrash className="w-6 h-6" />}
            title={t.calendar.seite.nichtErreichbarTitel}
            hint={t.calendar.seite.nichtErreichbarHinweis}
            action={
              <Button
                variant="primary"
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => void load()}
              >
                {t.common.erneutVersuchen}
              </Button>
            }
          />
        ) : accessDenied ? (
          <EmptyState
            icon={<CalendarClock className="w-6 h-6" />}
            title={t.calendar.seite.nichtFreigeschaltetTitel}
            hint={t.calendar.seite.nichtFreigeschaltetHinweis}
          />
        ) : isGrid ? (
          <Card padding="none" className="h-full overflow-hidden">
            {renderView()}
          </Card>
        ) : (
          <div className={mode === "agenda" ? "max-w-4xl" : ""}>
            <Card padding="md" className="overflow-x-auto">
              {renderView()}
            </Card>
          </div>
        )}
      </div>

      {detailLive && (
        <EventDetailsModal
          ev={detailLive}
          onClose={() => setDetailEv(null)}
          actions={actions}
        />
      )}

      {newSlot && (
        <NewEventModal
          slot={newSlot}
          colleagues={colleagues}
          onClose={() => setNewSlot(null)}
          onCreated={() => void load()}
        />
      )}

      {editEv && (
        <EditEventModal
          ev={editEv}
          onClose={() => setEditEv(null)}
          onSaved={(patch) => {
            if (editEv.graph_id) handleEditSaved(editEv.graph_id, patch);
          }}
        />
      )}

      {wizardOpen && (
        <FindMeetingWizard
          colleagues={colleagues}
          onClose={() => setWizardOpen(false)}
          onCreated={() => void load()}
        />
      )}
    </div>
  );
}
